import { createContext, useContext, useState, ReactNode } from 'react';

export type Lang = 'nl' | 'fr' | 'en' | 'pl';

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}

// ============ TRANSLATIONS ============
const translations: Record<Lang, Record<string, string>> = {
  nl: {
    'seo.retour.description': 'Hoe u baksteenstrips retourneert en wanneer u uw geld terugkrijgt: termijnen, voorwaarden en de stappen per geval.',
    'seo.retour.keywords': 'retour baksteenstrips, terugbetaling, herroepingsrecht, retourbeleid',
    'seo.retour.title': 'Retour & terugbetaling | Gevelsteenbedrijf.be',
    // NAV
    'nav.home': 'Home',
    'nav.products': 'Producten',
    'nav.calculator': 'Calculator',
    'nav.inspiration': 'Inspiratie & realisaties',
    'nav.architects': 'Architecten',
    'nav.custom': 'Technische info',
    'nav.quote': 'Offerte',
    'nav.login': 'Inloggen',
    'nav.logout': 'Uitloggen',
    'nav.dashboard': 'Dashboard',
    'nav.requestQuote': 'Offerte Aanvragen',

    // FOOTER
    'footer.desc': 'Uw specialist in premium baksteenstrips. Direct van fabrikant naar uw project, met de beste prijs-kwaliteitverhouding.',
    'footer.belgian': 'Belgisch bedrijf',
    'footer.nav': 'Navigatie',
    'footer.professionals': 'Professionals',
    'footer.techSheets': 'Technische Fiches',
    'footer.customStones': 'Technische info',
    'footer.wholesaleLogin': 'Groothandel Login',
    'footer.b2bPricing': 'B2B Prijsaanvraag',
    'footer.contact': 'Contact',
    'footer.rights': '© 2026 Gevelsteenbedrijf.be — Alle rechten voorbehouden',
    'footer.terms': 'Algemene Voorwaarden',
    'pdp.title.suffix': 'gevelsteenstrips kopen',
    'pdp.metaDesc': 'premium baksteenstrips, rechtstreeks van de fabrikant. Prijs per m²:',
    'pdp.exVat': 'excl. BTW',
    'pdp.spec.dimensions': 'Afmetingen',
    'pdp.spec.color': 'Kleur',
    'pdp.spec.texture': 'Textuur',
    'pdp.spec.category': 'Stijl',
    'pdp.spec.sku': 'Artikelnummer',
    'pdp.spec.delivery': 'Levertermijn',
    'pdp.spec.weeks': 'weken',
    'pdp.usp1': 'Identieke kwaliteit als volle gevelsteen — 10 jaar materiaalgarantie',
    'pdp.usp2': 'Rechtstreeks van de fabrikant, zonder tussenpersonen',
    'pdp.usp3': 'Gratis offerte met exacte materiaalberekening',
    'pdp.ctaCalc': 'Bereken uw project met deze steen',
    'pdp.ctaFiche': 'Technische fiche',
    'pdp.more': 'Ontdek onze andere gevelstenen',
    'retour.title': 'Retour & terugbetaling',
    'retour.intro': 'Hieronder leest u hoe retourneren en terugbetalen werkt. Vragen? Contacteer ons gerust — we lossen het graag op.',
    'retour.s1.title': 'Herroepingsrecht — 14 dagen',
    'retour.s1.body': 'Als consument heeft u het wettelijke recht om uw online aankoop binnen 14 kalenderdagen na levering te herroepen, zonder opgave van reden. Meld dit per e-mail; u ontvangt meteen instructies voor de retour.',
    'retour.s2.title': 'Terugsturen',
    'retour.s2.body': 'Stuur de producten binnen 14 dagen na uw melding terug, onbeschadigd, ongebruikt en in de originele verpakking op paletten. De kosten en het risico van de retourzending zijn ten laste van de koper, tenzij de levering foutief of beschadigd was — dan regelen en betalen wij de ophaling.',
    'retour.s3.title': 'Terugbetaling — binnen 14 dagen',
    'retour.s3.body': 'Wij betalen het volledige aankoopbedrag (incl. oorspronkelijke leveringskosten) terug binnen 14 dagen nadat wij de retour hebben ontvangen, of nadat u bewijs van verzending heeft bezorgd — afhankelijk van wat eerst valt. De terugbetaling gebeurt via hetzelfde betaalmiddel als de aankoop.',
    'retour.s4.title': 'Uitzonderingen',
    'retour.s4.body': 'Restpartijen worden verkocht in de staat waarin ze zich bevinden en zijn uitgesloten van retour (dit staat duidelijk vermeld op de Restpartijen-pagina). Op maat bestelde of speciaal voor u geproduceerde stenen kunnen evenmin geretourneerd worden. Aangebroken zakken lijm of voegsel nemen we niet terug.',
    'retour.s5.title': 'Beschadigde of foutieve levering',
    'retour.s5.body': 'Controleer de levering bij ontvangst en meld zichtbare schade binnen 48 uur met foto\'s. Wij vervangen beschadigde of foutief geleverde producten kosteloos, of betalen ze terug — zoals u verkiest.',
    'retour.btw': 'BTW',
    'footer.retour': 'Retour & terugbetaling',
    'calc.fixedLoss': '(vast — automatisch meegerekend)',
    'calc.stoneStrip': 'Onze steensoorten — tik om te wisselen',
    'footer.privacy': 'Privacy',
    'footer.cookies': 'Cookies',

    // HOME
    'home.badge': 'Premium Belgische Kwaliteit',
    'home.heroTitle1': 'Geef uw gevel',
    'home.heroTitle2': 'karakter',
    'home.heroDesc': 'Premium baksteenstrips rechtstreeks van fabrikant. Bespaar tot 40% via ons direct-leveringsmodel — zonder tussenpersonen.',
    'home.discoverCollection': 'Ontdek Collectie',
    'home.calculator': 'Calculator',
    'home.stat.products': 'Producten',
    'home.stat.projects': 'Projecten',
    'home.stat.savings': 'Besparing',
    'home.usp.factory': 'Direct van fabrikant',
    'home.usp.delivery': 'Levering door lokale verdeler',
    'home.usp.calc': 'Gratis berekening',
    'home.usp.custom': 'Custom mogelijk',
    'home.usp.warranty': 'Garantie inbegrepen',
    'home.model': 'Ons Model',
    'home.whyTitle': 'Waarom Gevelsteenbedrijf?',
    'home.whyDesc': 'Wij verbinden internationale fabrikanten direct met u, via een netwerk van lokale verdelers en installateurs — voor de beste prijs én persoonlijke service.',
    'home.model1.title': 'Internationale Fabrikanten',
    'home.model1.desc': 'Directe samenwerking met topfabrikanten uit Polen, Spanje en Italië voor de beste kwaliteit en prijs.',
    'home.model2.title': 'Lokale Verdelers',
    'home.model2.desc': 'Uw bestelling wordt afgeleverd via een erkende verdeler in uw regio, die ook een installateur kan aanbevelen.',
    'home.model3.title': 'Beste Prijsgarantie',
    'home.model3.desc': 'Door de directe lijn bespaart u tot 40% vergeleken met traditionele distributiekanalen.',
    'home.collection': 'Collectie',
    'home.popularStrips': 'Populaire Baksteenstrips',
    'home.viewAll': 'Bekijk alle producten →',
    'home.onlineTool': 'Online Tool',
    'home.calcTitle': 'Bereken uw project in 2 minuten',
    'home.calcDesc': 'Onze geavanceerde calculator berekent exact hoeveel baksteenstrips, lijm, voegsel en hoekstenen u nodig heeft — inclusief snijverlies.',
    'home.openCalc': 'Open Calculator',
    'home.realisations': 'Realisaties',
    'home.inspiration': 'Inspiratie',
    'home.allProjects': 'Alle projecten →',
    'home.private.title': 'Particulieren',
    'home.private.desc': 'Bouw of renoveer uw droomwoning met premium baksteenstrips aan fabrieksprijzen. Gebruik onze calculator en vraag een offerte aan — wij verbinden u met een lokale verdeler.',
    'home.private.cta': 'Bereken uw project',
    'home.pro.title': 'Architecten & Professionals',
    'home.pro.desc': 'Download technische fiches, prestatieverklaringen en BIM-bestanden. Maak gebruik van ons custom programma voor unieke projecten.',
    'home.pro.cta': 'Technische Info',
    'home.ctaTitle': 'Klaar om te starten?',
    'home.ctaDesc': 'Vraag vandaag nog een vrijblijvende offerte aan en ontdek hoeveel u kunt besparen met ons direct-leveringsmodel.',
    'home.requestQuote': 'Offerte Aanvragen',
    'home.viewCollection': 'Bekijk Collectie',

    // PRODUCTS
    'products.collection': 'Collectie',
    'products.title': 'Onze Baksteenstrips',
    'products.desc': 'Ontdek ons assortiment premium baksteenstrips. Filter op kleur en textuur om de perfecte steen voor uw project te vinden.',
    'products.search': 'Zoek baksteenstrips...',
    'products.allColors': 'Alle Kleuren',
    'products.allTextures': 'Alle Texturen',
    'products.clearFilters': 'Filters wissen',
    'products.found': 'gevonden',
    'products.product': 'product',
    'products.products': 'producten',
    'products.tapToPreview': 'Klik op het nummer van de gekozen steen',
    'products.noResults': 'Geen producten gevonden',
    'products.noResultsDesc': 'Pas uw filters aan om producten te vinden.',
    'products.quote': 'Offerte',
    'products.calculate': 'Berekenen',
    'products.3d.title': '3D Gevelpreview',
    'products.3d.rotate': 'Draai en kantel om het huis van alle kanten te bekijken',
    'products.3d.download': 'Download PDF',
    'products.3d.uploadTeaser': 'Upload foto\'s van uw eigen woning',
    'products.3d.uploadCta': 'Binnenkort: upload max. 5 foto\'s en bekijk uw gevel met de gekozen baksteenstrips in 3D.',
    'products.3d.comingSoon': 'Binnenkort',
    'products.3d.roofLabel': 'Dak',
    'products.3d.roofFlat': 'Plat',
    'products.3d.roofTiles': 'Pannen',
    'products.3d.roofSlate': 'Leisteen',
    'products.3d.nightMode': 'Nacht',
    'products.3d.dayMode': 'Dag',
    'products.3d.dragHint': 'slepen',
    'products.3d.roofToggle': 'Dak open',
    'products.3d.roofToggleClose': 'Dak dicht',
    'products.3d.materialsLabel': 'Materiaalberekening',
    'products.3d.materialsWall': 'Totale gevelopp.',
    'products.3d.materialsWindows': 'Openingen',
    'products.3d.materialsNet': 'Netto bakstenen',
    'products.3d.gardenClick': 'Klik op tuinelementen voor info',

    // HERO + CALCULATOR INTRO (v3.2.102 — eerder hardcoded NL)
    'home.heroBadge':       'Premium Belgische kwaliteit',
    'home.heroTitle':       'Geef uw gevel',
    'home.heroTitleAccent': 'karakter',
    'home.heroSubtitle':    'Premium baksteenstrips rechtstreeks van fabrikant. Bespaar tot 40% — zonder tussenpersonen.',
    'home.calcBadge':       'Calculator',
    'home.calcTitle':       'Kies uw gevelsteen — bereken uw prijs',
    'home.calcDesc':        'Kies uw gevelsteen, kies uw oppervlakte en vul het aantal m² in — u krijgt direct een nauwkeurige berekening.',
    'home.savings':         'bespaar',

    // CALCULATOR
    'calc.onlineTool': 'Online Tool',
    'calc.title': 'Materiaal Calculator',
    'calc.desc': 'Bereken exact hoeveel baksteenstrips, lijm, voegsel en hoekstenen u nodig heeft voor uw project.',
    'calc.productChoice': 'Productkeuze',
    'calc.area': 'Oppervlakte',
    'calc.areaLabel': 'Vul het aantal m² in (netto — zonder ramen en deuren)',
    'calc.jointWidth': '↔️ Voegbreedte',
    'calc.noJoint': 'Geen voeg',

    'v6.kiesSteen': 'Kies deze steen — bereken uw prijs',
    'admin.products.discountManage': 'Korting beheren',
    'admin.resellers.contactAdd': 'Toevoegen',
    'admin.resellers.contactRole': 'Rol (bv. Verkoop, Logistiek)',
    'admin.resellers.contactsEmpty': 'Nog geen extra contactpersonen',
    'admin.resellers.contactsHint': 'Andere personen van hetzelfde handelsbedrijf',
    'admin.resellers.contactsTitle': 'Extra contactpersonen',
    'admin.resellers.discount2': 'Korting',
    'admin.resellers.installerAdd': 'Toevoegen',
    'admin.resellers.installerNotes': 'Notities (specialiteit, regio, bereikbaarheid…)',
    'admin.resellers.installerTitle': 'Installateurs van deze handelaar',
    'admin.resellers.installersEmpty': 'Nog geen installateurs',
    'admin.resellers.installersHint': 'Bedrijven die de handelaar kan aanraden voor plaatsing',
    'admin.resellers.website': 'Website',
    'admin.resellers.websiteHint': 'Publieke website van deze handelaar (optioneel)',
    'cookie.functional.desc': 'Onthouden uw voorkeuren zoals taal en gekozen producten.',
    'cookie.functional.title': 'Functioneel',
    'cookie.gpcDetected': 'Uw browser stuurt een Global Privacy Control-signaal. We hebben automatisch enkel de noodzakelijke cookies aangevinkt.',
    'cookie.savePreferences': 'Voorkeuren bewaren',
    'home.tagline': 'BE PREMIUM BELGIAN QUALITY',
    'pdf.downloadHint': 'Klik op "Bewaar als PDF" of druk Ctrl/Cmd+P',
    'pdf.print': 'Bewaar als PDF',
    'v6.toevoegen': '＋ Toevoegen',
    'v6.sluit': 'Sluit',
    'v6.nieuweSteen': '＋ Nieuwe steen',
    'v6.3d': '🏠 3D',
    'v6.allesWissen': '🗑 Alles wissen',
    'v6.allesWissenBevestig': 'Alle gekozen stenen en instellingen wissen en opnieuw beginnen?',
    'v6.allesGewist': '🗑 Offerte-opbouw gewist — kies gerust opnieuw',
    'v6.reedsInOfferte': 'Reeds in deze offerte',
    'v6.staatOpen': '● staat hierboven open — pas gerust aan',
    'v6.instellingenPrijs': 'instellingen & prijs ▸',
    'v6.offerteAanvragen': 'Offerte aanvragen',
    'v6.offerteMetBerekening': 'Offerte aanvragen met deze berekening',
    'v6.totaal': 'Totaal',
    'v6.steen': 'steen',
    'v6.inDezeOfferte': 'in deze offerte',
    'v6.tipHoeken': 'Buitenhoeken van uw gevel — meestal 4.',
    'v6.tipHoogte': 'Hoogte van één hoek in meter — bv. 2,5 m per bouwlaag. Hoeken × hoogte = hoekstukken.',
    'v6.tipSnijverlies': 'Vast percentage — zit automatisch in de prijs.',
    'v6.termijnVraag': 'Wanneer plant u dit project?',
    'v6.t3m': '≤ 3 maanden',
    'v6.t6m': '≤ 6 maanden',
    'v6.t1j': 'Dit jaar',
    'v6.tLater': 'Later',
    'v6.tInfo': 'Ter info',
    'v6.termijnFout': 'Duid aan binnen welke termijn u dit plant — of kies "Ter info".',
    'v6.plaatsingKop': '🔨 Plaatsing',
    'v6.plaatsingVraag': 'Wenst u dat wij een professionele installateur regelen, of plaatst u zelf?',
    'v6.plOptInstall': 'Ik wens dat een installateur dit voor mij plaatst',
    'v6.plOptInstallSub': 'Wij brengen u in contact met een vakman in uw regio',
    'v6.plOptZelf': 'Ik plaats zelf',
    'v6.plOptZelfSub': 'Geen installateur nodig — enkel materiaal',
    'v6.plOptGh': 'Via mijn groothandelaar',
    'v6.plOptGhSub': 'U regelt aankoop en plaatsing via uw eigen groothandel',
    'v6.instVulPostcode': 'Vul hierboven uw postcode + gemeente in — dan tonen we de installateurs uit uw regio (±50 km).',
    'v6.instZoeken': 'Installateurs in uw regio zoeken…',
    'v6.instLijstIntro': 'Installateurs in uw regio — vink aan wie een kopie van uw offerte mag ontvangen (vrijblijvend, u onderhandelt zelf):',
    'v6.instGeen': 'Momenteel geen installateurs in uw regio — contacteer de fabrikant: wij helpen u persoonlijk verder na uw aanvraag.',
    'v6.ghIntro': 'Uw groothandel (optioneel) — dan sturen we hem een kopie van uw offerte:',
    'v6.ghNaam': 'Naam groothandel',
    'v6.ghGemeente': 'Gemeente',
    'v6.ghEmail': 'E-mailadres groothandel (voor de offertekopie)',
    'v6.geldstroom': '💶 Kiest u een installateur of groothandel, dan rekent u rechtstreeks met hen af — zij bestellen de materialen bij ons. Plaatst u zelf, dan bestelt en betaalt u vooraf bij ons.',
    'v6.uwGegevens': 'Uw gegevens',
    'v6.bevestigGegevens': 'Bevestig uw gegevens',
    'v6.sluiten': 'Sluiten',
    'v6.uspFabrikantKop': 'Direct van de fabrikant',
    'v6.uspFabrikant': 'Geen tussenpersonen — premium Belgische baksteenstrips tot 40% voordeliger, met vaste catalogusprijzen.',
    'v6.uspPlaatsingKop': 'Plaatsing geregeld',
    'v6.uspPlaatsing': 'Zelf plaatsen, via uw groothandel, of kies bij uw offerte een installateur uit uw regio (±50 km) — u onderhandelt rechtstreeks.',
    'v6.uspLeveringKop': 'Vlot geleverd',
    'v6.uspLevering': 'Levertermijn ±8 weken, netjes verpakt tot op de werf. Uw offerte blijft 30 dagen geldig — inclusief lijm en voegsel.',
    'v6.inspiratieKop': 'Inspiratie & realisaties',
    'v6.inspiratieSub': 'Bekijk afgewerkte gevels met onze stenen — zo kiest u met vertrouwen.',
    'v6.kortingCta': 'Handelaar, bouwonderneming of installateur?',
    'v6.kortingLink': 'Vraag uw korting aan →',
    'pdf.cfg.stone': 'Gevelsteen',
    'pdf.cfg.stones': 'Stenen',
    'pdf.cfg.corners': 'Hoeken',
    'pdf.cfg.delivery': 'Levertermijn',
    'pdf.cfg.groutColor': 'Voegkleur',
    'pdf.cfg.color': 'Kleur / textuur',
    'calc.corners': 'Hoekstenen',
    'calc.cornerCount': 'Aantal hoeken',
    'calc.cornerHeight': 'Hoogte per hoek (m)',
    'calc.accessories': 'Toebehoren',
    'calc.includeGlue': 'Lijm meerekenen',
    'calc.glueHint': '(~5 kg/m²)',
    'calc.includeGrout': 'Voegsel meerekenen',
    'calc.groutHint': '(afhankelijk van voegbreedte)',
    'calc.groutColor': 'Kleur voegsel',
    'calc.grey': 'grijs',
    'calc.white': 'wit',
    'calc.anthracite': 'antraciet',
    'calc.sand': 'zand',
    'calc.cuttingLoss': 'Snijverlies',
    'calc.cuttingLossHint': 'Standaard 10%. Verhoog bij complexe gevels met veel snijwerk.',
    'calc.yourCalc': 'Uw Berekening',
    'calc.brickStrips': 'Baksteenstrips',
    'calc.pieces': 'stuks',
    'calc.cornerStones': 'Hoekstenen',
    'calc.glue': 'Lijm',
    'calc.bags': 'zakken à 25kg',
    'calc.grout': 'Voegsel',
    'calc.totalExcl': 'Totaal (excl. BTW)',
    'calc.inclVat': 'Incl. 21% BTW',
    'calc.quoteWithCalc': 'Offerte Aanvragen met deze berekening',
    'calc.print': 'Berekening Afdrukken',
    'calc.saveQuote': 'Offerte Opslaan',
    'calc.savedSuccess': 'Offerte opgeslagen! Bekijk in uw dashboard.',
    'calc.disclaimer': '* Richtprijzen. Exacte prijs in uw persoonlijke offerte.',
    'calc.swipeHint': 'Swipe om meer stenen te zien',

    // INSPIRATION
    'insp.realisations': 'Realisaties',
    'insp.title': 'Inspiratie & realisaties',
    'insp.desc': 'Bekijk opgeleverde projecten met onze baksteenstrips. Filter op bouwstijl om inspiratie op te doen voor uw eigen project.',
    'insp.allStyles': 'Alle stijlen',
    'insp.style.modern': 'Modern',
    'insp.style.landelijk': 'Landelijk',
    'insp.style.industrieel': 'Industrieel',
    'insp.style.klassiek': 'Klassiek',
    'insp.architect': 'Architect',
    'insp.noResults': 'Geen projecten gevonden',
    'insp.noResultsDesc': 'Selecteer een andere stijl om projecten te bekijken.',

    // QUOTE
    'quote.title': 'Offerte Aanvragen',
    'quote.desc': 'Vraag een vrijblijvende offerte aan. Wij verbinden u met een lokale verdeler in uw regio voor de beste service en prijs.',
    'quote.iAm': 'Ik ben een...',
    'quote.private': 'Particulier',
    'quote.contractor': 'Aannemer',
    'quote.architect': 'Architect',
    'quote.wholesaler': 'Groothandel',
    'quote.contactInfo': 'Contactgegevens',
    'quote.name': 'Naam',
    'quote.email': 'E-mail',
    'quote.phone': 'Telefoon',
    'quote.postcode': 'Postcode',
    'quote.postcodeHint': 'bv. 2000',
    'quote.company': 'Bedrijfsnaam',
    'quote.projectDetails': 'Projectgegevens',
    'quote.product': 'Product',
    'quote.selectProduct': 'Selecteer product...',
    'quote.estimatedArea': 'Geschatte oppervlakte (m²)',
    'quote.areaHint': 'bv. 80',
    'quote.additionalInfo': 'Bijkomende informatie',
    'quote.additionalInfoHint': 'Beschrijf uw project, specifieke wensen, gewenste leverdatum...',
    'quote.submit': 'Offerte Aanvragen →',
    'quote.submitNote': 'Wij behandelen uw aanvraag binnen 24 uur. Uw gegevens worden vertrouwelijk behandeld.',
    'quote.successTitle': 'Offerte Aangevraagd!',
    'quote.successDesc': 'Bedankt voor uw aanvraag. Wij nemen binnen 24 uur contact met u op met een persoonlijke offerte. Een lokale verdeler in uw regio zal worden toegewezen.',
    'quote.newRequest': 'Nieuwe Aanvraag',

    // ARCHITECT PAGE
    'arch.professionals': 'Professionals',
    'arch.title': 'Architecten & Voorschrijvers',
    'arch.desc': 'Download technische fiches, prestatieverklaringen en BIM-bestanden. Alle documentatie die u nodig heeft voor uw bestek.',
    'arch.generalDocs': 'Algemene Documentatie',
    'arch.perProduct': 'Documentatie per Product',
    'arch.techSpecs': 'Technische Specificaties',
    'arch.doc1': 'Technische Fiche — Volledige Collectie',
    'arch.doc2': 'Prestatieverklaring (DoP) — CE Markering',
    'arch.doc3': 'BIM Objecten — Revit Compatible',
    'arch.doc4': 'Installatiehandleiding',
    'arch.doc5': 'Brandweerstandsrapport — Klasse A1',
    'arch.doc6': 'Vorstbestendigheidstest — Klasse F2',
    'arch.techFiche': 'Technische Fiche',
    'arch.dop': 'Prestatieverklaring',
    'arch.bim': 'BIM Object',
    'arch.spec.dimensions': 'Afmetingen',
    'arch.spec.performance': 'Prestaties',
    'arch.spec.installation': 'Installatie',
    'arch.spec.warranty': 'Garantie',
    'arch.spec.length': 'Lengte',
    'arch.spec.height': 'Hoogte',
    'arch.spec.thickness': 'Dikte',
    'arch.spec.weight': 'Gewicht',
    'arch.spec.fireClass': 'Brandklasse',
    'arch.spec.fireValue': 'A1 (onbrandbaar)',
    'arch.spec.frost': 'Vorstbestendigheid',
    'arch.spec.frostValue': 'Klasse F2',
    'arch.spec.water': 'Waterabsorptie',
    'arch.spec.waterValue': '< 6%',
    'arch.spec.ce': 'CE Markering',
    'arch.spec.ceValue': 'EN 14411',
    'arch.spec.glue': 'Lijm',
    'arch.spec.glueValue': '5 kg/m² (flexibele tegellijm)',
    'arch.spec.grout': 'Voegsel',
    'arch.spec.groutValue': '1.5 kg/m² (bij 10mm voeg)',
    'arch.spec.loss': 'Snijverlies',
    'arch.spec.lossValue': '10% standaard',
    'arch.spec.dryTime': 'Droogtijd',
    'arch.spec.dryValue': '24-48 uur',
    'arch.spec.productWarranty': 'Productgarantie',
    'arch.spec.productWarrantyVal': '10 jaar',
    'arch.spec.colorFast': 'Kleurvastheid',
    'arch.spec.colorFastVal': 'UV-bestendig',
    'arch.spec.lifespan': 'Levensduur',
    'arch.spec.lifespanVal': '> 50 jaar',
    'arch.spec.maintenance': 'Onderhoud',
    'arch.spec.maintenanceVal': 'Onderhoudsvrij',
    'arch.helpTitle': 'Hulp nodig bij uw bestek?',
    'arch.helpDesc': 'Ons technisch team helpt u graag bij het samenstellen van de juiste specificaties voor uw project.',
    'arch.contactUs': 'Contact Opnemen',

    // CUSTOM PAGE
    'custom.exclusive': 'Exclusief',
    'custom.title': 'Custom Gevelstenen',
    'custom.desc': 'Maak uw gevel uniek met op maat gemaakte baksteenstrips. Kies uw eigen kleur, textuur en afwerking — geproduceerd door onze Poolse fabrikant.',
    'custom.color.title': 'Kleur op Maat',
    'custom.color.desc': 'Kies uit een onbeperkt kleurenpalet. Wij matchen elke RAL-kleur of bestaande steen.',
    'custom.texture.title': 'Textuur naar Wens',
    'custom.texture.desc': 'Van ultra-glad tot grof ruw, handvorm of industrieel — elke textuur is mogelijk.',
    'custom.size.title': 'Aangepaste Afmetingen',
    'custom.size.desc': 'Andere formaten, dunnere of dikkere strips — alles is bespreekbaar voor uw project.',
    'custom.howTitle': 'Hoe werkt het?',
    'custom.step1.title': 'Briefing',
    'custom.step1.desc': 'Deel uw wensen: kleur, textuur, formaat en hoeveelheid.',
    'custom.step2.title': 'Staal',
    'custom.step2.desc': 'Wij produceren een staal op maat voor uw goedkeuring.',
    'custom.step3.title': 'Productie',
    'custom.step3.desc': 'Na goedkeuring starten wij de productie. Levertijd: 6-8 weken.',
    'custom.step4.title': 'Levering',
    'custom.step4.desc': 'Uw custom stenen worden geleverd via een lokale verdeler.',
    'custom.interestTitle': 'Interesse in custom stenen?',
    'custom.interestDesc': 'Neem contact op voor een vrijblijvend gesprek over de mogelijkheden. Minimum bestelhoeveelheid: 100 m².',
    'custom.contact': 'Contact Opnemen',

    // LOGIN
    'login.title': 'Inloggen',
    'login.subtitle': 'Toegang tot uw B2B dashboard',
    'login.email': 'E-mailadres',
    'login.emailPlaceholder': 'uw@email.be',
    'login.password': 'Wachtwoord',
    'login.submit': 'Inloggen',
    'login.error': 'Ongeldige inloggegevens. Probeer opnieuw.',
    'login.emailExists': 'Dit e-mailadres is al geregistreerd.',
    'login.invalidInput': 'Vul alle velden correct in (wachtwoord min. 4 tekens).',
    'login.registerTitle': 'Account Aanmaken',
    'login.registerSubtitle': 'Maak een gratis klantaccount aan',
    'login.name': 'Naam',
    'login.namePlaceholder': 'Uw volledige naam',
    'login.passwordHint': 'Min. 4 tekens',
    'login.registerSubmit': 'Registreren',
    'login.hasAccount': 'Al een account? Inloggen',
    'login.noAccount': 'Nog geen account? Registreer gratis',
    'login.demoTitle': 'Demo accounts:',

    // DASHBOARD
    'dash.admin': '👑 Administrator',
    'dash.wholesaler': '🏭 Groothandel',
    'dash.customer': '👤 Klant',
    'dash.overview': '📊 Overzicht',
    'dash.productMgmt': '📦 Productbeheer',
    'dash.customers': '👥 Klanten',
    'dash.orders': '📋 Bestellingen',
    'dash.suppliers': '🚚 Leveranciers',
    'dash.analytics': '📈 Analytics',
    'dash.myPrices': '💰 Mijn Prijzen',
    'dash.myQuotes': '📄 Mijn Offertes',
    'dash.totalQuotes': 'Totale Offertes',
    'dash.activeWholesalers': 'Actieve Groothandels',
    'dash.revenueMonth': 'Omzet deze maand',
    'dash.conversionRate': 'Conversieratio',
    'dash.myOrders': 'Mijn Bestellingen',
    'dash.openQuotes': 'Open Offertes',
    'dash.myDiscount': 'Mijn Korting',
    'dash.totalOrdered': 'Totaal Besteld',
    'dash.status': 'Status',
    'dash.inProgress': 'In behandeling',
    'dash.calculations': 'Berekeningen',
    'dash.favorites': 'Favorieten',
    'dash.recentQuotes': 'Recente Offerteaanvragen',
    'dash.client': 'Klant',
    'dash.type': 'Type',
    'dash.product': 'Product',
    'dash.new': 'Nieuw',
    'dash.quoteSent': 'Offerte verstuurd',
    'dash.newProduct': '+ Nieuw Product',
    'dash.edit': 'Bewerken',
    'dash.delete': 'Verwijderen',
    'dash.supplierMgmt': 'Leveranciersbeheer',
    'dash.active': 'Actief',
    'dash.inTalks': 'In gesprek',
    'dash.planned': 'Gepland',
    'dash.exclusiveContract': 'Exclusief contract',
    'dash.potential': 'Potentieel',
    'dash.future': 'Toekomstig',
    'dash.yourPrices': 'Uw Groothandelsprijzen',
    'dash.comingSoon': 'Binnenkort beschikbaar',
    'dash.comingSoonDesc': 'Deze module wordt momenteel ontwikkeld en is binnenkort beschikbaar.',
    'dash.newQuote': 'Nieuwe Berekening',
    'dash.noQuotes': 'Nog geen opgeslagen offertes',
    'dash.noQuotesDesc': 'Maak een berekening in de calculator en sla deze op als offerte.',
    'dash.goToCalc': 'Ga naar Calculator',
    'dash.downloadPdf': 'Download PDF',
    'dash.corners': 'hoekstenen',
    'dash.glueBags': 'zakken lijm',
    'dash.groutBags': 'zakken voegsel',
    'dash.searchCustomer': 'Klant zoeken...',
    'dash.phone': 'Telefoon',
    'dash.address': 'Adres',
    'dash.editProduct': 'Product bewerken',
    'dash.addProduct': 'Product toevoegen',
    'dash.productName': 'Productnaam',
    'dash.serialNumber': 'Serienummer',
    'dash.supplierCode': 'Leverancierscode',
    'dash.supplier': 'Leverancier',
    'dash.color': 'Kleur',
    'dash.texture': 'Textuur',
    'dash.category': 'Categorie',
    'dash.pricing': 'Prijzen',
    'dash.retailPrice': 'Verkoopprijs/m²',
    'dash.dimensions': 'Afmetingen',
    'dash.weight': 'Gewicht',
    'dash.stock': 'Voorraad',
    'dash.description': 'Beschrijving',
    'dash.descriptionPlaceholder': 'Beschrijf het product...',
    'dash.cancel': 'Annuleren',
    'dash.saveChanges': 'Wijzigingen opslaan',
    'dash.importProducts': 'Importeren',
    'dash.dragDrop': 'Sleep bestand hierheen of klik om te uploaden',
    'dash.supportedFormats': 'Ondersteunde formaten: Excel (.xlsx, .xls, .csv) of PDF',
    'dash.chooseFile': 'Bestand kiezen',
    'dash.templateInfo': 'Template',
    'dash.templateDesc': 'Gebruik kolommen: Naam, Serienummer, Kleur, Textuur, Prijs/m², B2B Tier 1-3, Afmetingen, Gewicht',

    // DATA - Colors
    'data.color.Rood': 'Rood',
    'data.color.Wit': 'Wit',
    'data.color.Bruin': 'Bruin',
    'data.color.Zwart': 'Zwart',
    'data.color.Geel': 'Geel',
    'data.color.Grijs': 'Grijs',
    'data.color.Taupe': 'Taupe',
    'data.color.Creme': 'Cr\u00e8me',
    'data.color.Bont': 'Bont',

    // DATA - Textures
    'data.texture.Ruw': 'Ruw',
    'data.texture.Glad': 'Glad',
    'data.texture.Gesinterd': 'Gesinterd',
    'data.texture.Gewassen': 'Gewassen',

    // DATA - Suppliers
    'data.supplier.polen': 'Fabrikant Polen',
    'data.supplier.spanje': 'Fabrikant Spanje',
    'data.supplier.italie': 'Fabrikant Italië',

    // DATA - Product Descriptions

    // DATA - Inspiration Projects
    'data.insp.i1.title': 'Villa Kalmthout',
    'data.insp.i1.desc': 'Strakke moderne villa met witte en zwarte gevelstrips in een bosrijke omgeving.',
    'data.insp.i2.title': 'Hoeve Restauratie Limburg',
    'data.insp.i2.desc': 'Authentieke renovatie van een Limburgse hoeve met rode en bruine baksteenstrips.',
    'data.insp.i3.title': 'Loft Gent Centrum',
    'data.insp.i3.desc': 'Industrieel loft-project in hartje Gent met grijze en zwarte accenten.',
    'data.insp.i4.title': 'Villa Brugge',
    'data.insp.i4.desc': 'Klassieke villa nabij Brugge met warme bruine en goudgele gevelstrips.',

    // DATA - Dashboard extra
    'data.type.particulier': 'Particulier',
    'data.type.groothandel': 'Groothandel',
    'data.type.architect': 'Architect',
    'data.type.aannemer': 'Aannemer',
    'data.tier': 'Tier',

    // DATA - Login demo
    'login.demoAdmin': 'Admin',
    'login.demoWholesaler': 'Groothandel',
    'login.demoCustomer': 'Klant',

    // DATA - Footer
    'footer.location': 'Bredabaan 123, 2970 Schilde, België',
    'footer.btw': 'BTW BE0123.456.789',

    // DATA - Alt text
    'data.alt.customStones': 'Custom bakstenen',

    // SEO - Page Titles
    'seo.home.title': 'Gevelstenen & Baksteenstrips kopen — Direct van Fabrikant | Gevelsteenbedrijf.be',
    'seo.products.title': 'Baksteenstrips Collectie — Alle Kleuren & Texturen | Gevelsteenbedrijf.be',
    'seo.calculator.title': 'Materiaal Calculator — Bereken uw Project | Gevelsteenbedrijf.be',
    'seo.inspiration.title': 'Inspiratiegalerij — Gerealiseerde Projecten | Gevelsteenbedrijf.be',
    'seo.quote.title': 'Offerte Aanvragen — Vrijblijvend & Snel | Gevelsteenbedrijf.be',
    'seo.architects.title': 'Architecten & Voorschrijvers — Technische Fiches | Gevelsteenbedrijf.be',
    'seo.custom.title': 'Technische info & Custom Gevelstenen | Gevelsteenbedrijf.be',
    'seo.login.title': 'Inloggen — B2B Portaal | Gevelsteenbedrijf.be',
    'seo.dashboard.title': 'Dashboard — Gevelsteenbedrijf.be',

    // SEO - Page Descriptions
    'seo.home.description': 'Gevelstenen en premium baksteenstrips rechtstreeks van fabrikant. Bespaar tot 40% op gevelstrips, baksteenstrips en hoekstenen. Gratis berekening en offerte. Belgisch bedrijf met lokale verdelers.',
    'seo.products.description': 'Ontdek ons assortiment premium baksteenstrips. Filter op kleur en textuur. Rode, witte, bruine, zwarte, gele en grijze baksteenstrips. Direct van fabrikant aan de beste prijs.',
    'seo.calculator.description': 'Bereken gratis hoeveel baksteenstrips, lijm, voegsel en hoekstenen u nodig heeft. Inclusief snijverlies berekening. Ontvang direct een prijsindicatie.',
    'seo.inspiration.description': 'Bekijk gerealiseerde projecten met onze baksteenstrips. Moderne, landelijke, industriële en klassieke gevels. Doe inspiratie op voor uw eigen project.',
    'seo.quote.description': 'Vraag een vrijblijvende offerte aan voor baksteenstrips. Snelle respons binnen 24 uur. Persoonlijke service via een lokale verdeler in uw regio.',
    'seo.architects.description': 'Download technische fiches, prestatieverklaringen (DoP), BIM-bestanden en installatiehandleidingen voor baksteenstrips. Alle documentatie voor uw bestek.',
    'seo.custom.description': 'Laat baksteenstrips op maat produceren. Kies uw eigen kleur, textuur en afmetingen. Minimum bestelhoeveelheid 100 m². Levertijd 6-8 weken.',
    'seo.login.description': 'Log in op het B2B portaal van Gevelsteenbedrijf.be. Toegang tot groothandelsprijzen, bestellingen en offertes.',
    'seo.dashboard.description': 'Beheer uw bestellingen, offertes en klantgegevens via het Gevelsteenbedrijf.be dashboard.',

    // SEO - Keywords
    'seo.home.keywords': 'gevelstenen, gevelstenen kopen, gevelsteen, baksteenstrips, gevelstrips, bakstenen, gevelbekleding, premium baksteenstrips, België, direct van fabrikant, bouwen, verbouwen, renovatie, nieuwbouw, metselwerk, voegen, aanbouw, ruwbouw',
    'seo.products.keywords': 'baksteenstrips collectie, rode baksteenstrips, witte baksteenstrips, bruine gevelstrips, zwarte baksteenstrips, metselen, metselwerk, gevelstrips kopen',
    'seo.calculator.keywords': 'baksteenstrips calculator, materiaal berekenen, hoeveel baksteenstrips, lijm berekening, snijverlies',
    'seo.inspiration.keywords': 'baksteenstrips inspiratie, gevel renovatie, moderne gevel, landelijke gevel, industrieel, klassiek',
    'seo.quote.keywords': 'offerte baksteenstrips, prijsaanvraag gevelstrips, vrijblijvende offerte, baksteenstrips bestellen',
    'seo.architects.keywords': 'technische fiches baksteenstrips, BIM bestanden, prestatieverklaring, CE markering, architecten documentatie',
    'seo.custom.keywords': 'custom baksteenstrips, baksteenstrips op maat, kleur op maat, textuur op maat, maatwerk gevelstrips',
    'seo.login.keywords': 'B2B login, groothandel portaal, inloggen gevelsteenbedrijf',
    'seo.dashboard.keywords': 'dashboard, bestellingen, offertes, klantbeheer',
    'seo.terms.title': 'Algemene Voorwaarden | Gevelsteenbedrijf.be',
    'seo.terms.description': 'Lees onze algemene voorwaarden. Informatie over prijzen, levering, herroepingsrecht, garantie en meer. Gevelsteenbedrijf.be — Belgisch bedrijf.',
    'seo.terms.keywords': 'algemene voorwaarden, verkoopsvoorwaarden, garantie baksteenstrips, herroepingsrecht',
    'seo.cookiePolicy.title': 'Cookiebeleid & Privacy | Gevelsteenbedrijf.be',
    'seo.cookiePolicy.description': 'Ons cookiebeleid en privacyverklaring. Informatie over cookies, gegevensverwerking en uw rechten conform AVG/GDPR. Gevelsteenbedrijf.be.',
    'seo.cookiePolicy.keywords': 'cookiebeleid, privacy, GDPR, AVG, gegevensbescherming, cookies',

    // COOKIE BANNER
    'cookie.title': 'Wij respecteren uw privacy',
    'cookie.description': 'Wij gebruiken cookies om uw ervaring te verbeteren. Essentiële cookies zijn noodzakelijk voor de werking van de site. Analytische en marketingcookies worden enkel geplaatst met uw toestemming, conform de Belgische privacywetgeving en AVG/GDPR.',
    'cookie.acceptAll': 'Alle cookies accepteren',
    'cookie.essentialOnly': 'Enkel essentiële',
    'cookie.showDetails': 'Meer details',
    'cookie.hideDetails': 'Details verbergen',
    'cookie.essential.title': 'Essentiële cookies',
    'cookie.essential.desc': 'Noodzakelijk voor de werking van de website: taalvoorkeur, cookiekeuze, login sessie.',
    'cookie.analytics.title': 'Analytische cookies',
    'cookie.analytics.desc': 'Helpen ons begrijpen hoe bezoekers de website gebruiken. Geanonimiseerde gegevens.',
    'cookie.marketing.title': 'Marketingcookies',
    'cookie.marketing.desc': 'Worden gebruikt om relevante advertenties te tonen op basis van uw interesses.',
    'cookie.legalNote': 'Meer informatie vindt u in ons',
    'cookie.policyLink': 'cookiebeleid',

    // ADMIN DASHBOARD
    'admin.accessDenied': 'Toegang Geweigerd',
    'admin.accessDeniedDesc': 'Log in als eigenaar of superadmin om het dashboard te bekijken.',
    'admin.dashboard': 'Dashboard',
    'admin.demoMode': 'Demo modus — alleen producten zichtbaar',
    'admin.ownerDashboard': 'Owner Dashboard',
    'admin.loggedInAs': 'Ingelogd als',
    'admin.refresh': 'Verversen',
    'admin.loading': 'Data laden...',
    'admin.apiFooter': 'Alle data via beveiligde admin endpoints.',

    // Admin tabs
    'admin.tab.producten': 'Producten',
    'admin.tab.leveranciers': 'Leveranciers',
    'admin.tab.klanten': 'Klanten & Offertes',
    'admin.tab.klantenOffertes': 'Klanten & Offertes',
    'admin.tab.testOffertes': 'Test Offertes',
    'admin.tab.prijzen': 'Kortingen Resellers',
    'admin.tab.berichten': 'Berichten',

    // Admin stat labels
    'admin.stat.producten': 'Producten · Prijzen',
    'admin.stat.leveranciers': 'Leveranciers',
    'admin.stat.resellers': 'Resellers',
    'admin.stat.resellersSub': 'Doorverkopers',
    'admin.stat.klantenOffertes': 'Klanten & Offertes',
    'admin.stat.klanten': 'klanten',
    'admin.stat.offertes': 'offertes',
    'admin.stat.testOffertes': 'Test Offertes',
    'admin.stat.offertesCount': 'Offertes',
    'admin.stat.internTesten': 'Intern testen',
    'admin.stat.prijzen': 'Kortingen Resellers',
    'admin.stat.prijsbeheer': 'Prijsbeheer',
    'admin.stat.prijzenSub': 'Kortingen + pauze per reseller',

    // Admin StatusBadge
    'admin.status.draft': 'Concept',
    'admin.status.pending': 'In behandeling',
    'admin.status.sent': 'Verstuurd',
    'admin.status.accepted': 'Goedgekeurd',
    'admin.status.approved': 'Goedgekeurd',
    'admin.status.rejected': 'Afgewezen',
    'admin.status.expired': 'Verlopen',
    'admin.status.new': 'Nieuw',

    // Admin Mijn Profiel
    'admin.profile': 'Mijn Profiel',
    'admin.profile.edit': 'Bewerken',
    'admin.profile.save': 'Opslaan',
    'admin.profile.saving': 'Opslaan...',
    'admin.profile.cancel': 'Annuleren',
    'admin.profile.success': 'Profiel succesvol bijgewerkt',
    'admin.profile.name': 'Naam',
    'admin.profile.email': 'E-mail',
    'admin.profile.phone': 'Telefoon',
    'admin.profile.company': 'Bedrijfsnaam',
    'admin.profile.companyShort': 'Bedrijf',
    'admin.profile.btw': 'BTW-nummer',
    'admin.profile.btwShort': 'BTW',
    'admin.profile.address': 'Adres',
    'admin.profile.city': 'Stad',
    'admin.profile.postalCode': 'Postcode',

    // Admin Producten tab
    'admin.products.title': 'Producten · Prijzen aanpassen',
    'admin.products.editProduct': 'Product bewerken',
    'admin.products.name': 'Naam',
    'admin.products.category': 'Categorie',
    'admin.products.dimensions': 'Afmetingen',
    'admin.products.serialNumber': 'Serienummer',
    'admin.products.ean': 'EAN-code',
    'admin.products.fixedPrice': 'Vaste Prijs',
    'admin.products.techSpecs': 'Tech Specs',
    'admin.products.save': 'Opslaan',
    'admin.products.saving': 'Opslaan...',
    'admin.products.cancel': 'Annuleren',
    'admin.products.edit': 'Bewerken',
    'admin.products.color': 'Kleur',
    'admin.products.texture': 'Textuur',
    'admin.products.weight': 'Gewicht',
    'admin.products.supplier': 'Leverancier',
    'admin.products.purchase': 'Inkoop',
    'admin.products.exclVat': 'Excl. BTW',
    'admin.products.incl21': 'Incl. 21%',
    'admin.products.incl6': 'Incl. 6%',
    'admin.products.actions': 'Acties',
    'admin.products.showAll': 'Toon alle',
    'admin.products.bestsellers': 'Bestsellers',
    'admin.products.bestsellersOn': 'Klik om bestseller-filter uit te schakelen',
    'admin.products.bestsellersOff': 'Toon enkel bestsellers',
    'admin.products.priceM2Badge': 'Prijzen / m²',
    'admin.products.priceM2Title': 'Alle prijzen in deze tabel zijn per vierkante meter (m²)',
    'admin.products.akpLev': 'AKP Resel.',
    'admin.products.akpLevTooltip': 'Aankoopprijs reseller — wat de reseller aan Filip betaalt (excl. BTW)',
    'admin.products.akpLevHint': 'Aankoopprijs reseller — basis voor de reseller-korting in tab Kortingen',
    'admin.products.akpDiscount': 'Korting Resel→Eindkl.',
    'admin.products.akpDiscountTooltip': 'Korting die reseller doorgeeft aan eindklant (zichtbaar online)',
    'admin.products.netAkp': 'Netto eindklant ex. BTW',
    'admin.products.netAkpTooltip': 'Netto prijs eindklant excl. BTW — wat de klant op de website betaalt vóór BTW',
    'admin.products.purchaseTooltip': 'Netto AKP — wat aan de leverancier betaald wordt (klik op ✎ om AKP Lev. + korting in te vullen)',
    'admin.products.margeTooltip': 'Winst op de verkoopprijs — (verkoop − inkoop) / verkoop × 100',
    'admin.products.exclVatTooltip': 'Verkoopprijs exclusief BTW — wat de klant betaalt vóór BTW',
    'admin.products.dealConsistent': 'Verkoopformule klopt — klik Opslaan om vast te leggen',
    'admin.products.dealInconsistent': 'Netto eindklant wijkt af van AKP Resel × (1 − korting). Vul opnieuw in.',
    'admin.products.purchaseSubtitle': 'AKP {akp} · −{disc}%',
    'admin.products.akpBlockTitle': '📦 Verkoopprijs / m²',
    'admin.products.akpBlockHint': '(optioneel — kan ook later via de Producten-tabel)',

    // PRODUCT DISCOUNT MODAL (v3.2.115 — eerder ontbraken alle keys
    // waardoor Filip raw 'admin.products.discountFor' etc. zag).
    'admin.products.discountFor':         'Korting voor',
    'admin.products.discountHelp':        'Stel een tijdelijke promo of een liquidatie-actie in voor dit product. Wordt zichtbaar op de productkaart op de website.',
    'admin.products.discountShow':        'Standaard korting tonen',
    'admin.products.discountShowHelp':    'Toont een rode korting-pill op de productkaart. Bv. "Vroege boeking" of "Promo".',
    'admin.products.discountPct':         'Korting %',
    'admin.products.discountMax':         'max 50%',
    'admin.products.discountMax50':       'Korting mag maximaal 50% zijn.',
    'admin.products.discountLabel':       'Label',
    'admin.products.discountLabelPh':     'bv. Promo, Vroege boeking',
    'admin.products.discountSave':        'Opslaan',
    'admin.products.discountDateInvalid': 'Einddatum moet na de startdatum vallen.',
    'admin.products.liquidationToggle':   'Liquidatie / einde seizoen',
    'admin.products.liquidationHelp':     'Tijdelijke amber-badge met begin- en einddatum. Wint van de standaard korting tijdens het venster.',
    'admin.products.liquidationPct':      'Liquidatie %',
    'admin.products.liquidationLabel':    'Label',
    'admin.products.liquidationLabelPh':  'bv. Liquidatie, Einde seizoen',
    'admin.products.liquidationStart':    'Startdatum',
    'admin.products.liquidationEnd':      'Einddatum',
    'admin.products.preset1month':        'Volgende maand',
    'admin.products.preset1week':         'Komende week',
    'admin.products.preview':             'Voorbeeld',
    'admin.products.catalogPrice':        'Catalogprijs',
    'admin.products.afterDiscount':       'Na korting',
    'admin.products.resetFilters': 'Reset filters · toon alle producten',
    'admin.products.resetFiltersFull': 'Reset filters · toon alle {count} producten',
    'admin.products.resetFiltersTitle': 'Alle filters uitschakelen om alle producten te zien',
    'admin.products.emptyFilterTitle': 'Geen producten zichtbaar — er staat een filter aan',
    'admin.products.emptyBoth': 'Bestsellers-filter + leverancier {supplier} verbergen alle {count} producten.',
    'admin.products.emptyBestsellers': 'Bestsellers-filter is actief, maar geen enkel product is als bestseller gemarkeerd.',
    'admin.products.emptySupplier': 'Leverancier {supplier} heeft geen producten in deze lijst.',
    'admin.products.optional': 'Optioneel',
    'admin.products.specs': 'Specs',
    'admin.products.filterSupplier': 'Klik om te filteren op deze leverancier',

    // Admin Leveranciers tab
    'admin.suppliers.title': 'Leveranciers',
    'admin.suppliers.editSupplier': 'Leverancier Bewerken',
    'admin.suppliers.newSupplier': 'Nieuwe Leverancier',
    'admin.suppliers.company': 'Bedrijfsnaam',
    'admin.suppliers.nameShort': 'Naam (kort)',
    'admin.suppliers.btw': 'BTW-nummer',
    'admin.suppliers.address': 'Adres',
    'admin.suppliers.city': 'Stad',
    'admin.suppliers.postalCode': 'Postcode',
    'admin.suppliers.contactPerson': 'Contactpersoon',
    'admin.suppliers.email': 'E-mail',
    'admin.suppliers.phone': 'Telefoon',
    'admin.suppliers.website': 'Website',
    'admin.suppliers.save': 'Opslaan',
    'admin.suppliers.saving': 'Opslaan...',
    'admin.suppliers.cancel': 'Annuleren',
    'admin.suppliers.add': 'Toevoegen',
    'admin.suppliers.new': 'Nieuw',
    'admin.suppliers.edit': 'Bewerken',
    'admin.suppliers.delete': 'Verwijderen',
    'admin.suppliers.noSuppliers': 'Geen leveranciers gevonden.',
    'admin.suppliers.nameRequired': 'Naam is verplicht',
    'admin.suppliers.confirmDelete': 'Weet je zeker dat je deze leverancier wilt verwijderen?',
    'admin.suppliers.products': 'Producten',
    'admin.suppliers.name': 'Naam',
    'admin.suppliers.actions': 'Acties',
    'admin.suppliers.prod': 'prod.',

    // Admin Klanten & Offertes tab
    'admin.customers.title': 'Klanten & Offertes',
    'admin.customers.clients': 'klanten',
    'admin.customers.quotes': 'offertes',
    'admin.customers.noCustomers': 'Geen klanten gevonden.',
    'admin.customers.since': 'Sinds',
    'admin.customers.total': 'totaal',
    'admin.customers.off': 'off.',
    'admin.customers.loadingQuotes': 'Offertes laden...',
    'admin.customers.quotesTitle': 'Offertes',
    'admin.customers.excl': 'excl.',
    'admin.customers.noQuotesForCustomer': 'Geen offertes voor deze klant.',
    'admin.customers.auditNote': 'Offertes worden permanent bewaard en kunnen niet verwijderd worden. Alle offertes worden permanent gearchiveerd en kunnen na verzending niet meer verwijderd worden.',

    // Admin Test Offertes tab
    'admin.testQuotes.title': 'Test Offertes (intern)',
    'admin.testQuotes.desc': 'Hier kun je als eigenaar test-offertes bekijken en beheren. Simuleer wat een klant zou zien.',
    'admin.testQuotes.goToQuotePage': 'offertepagina',
    'admin.testQuotes.goToPrefix': 'Ga naar de ',
    'admin.testQuotes.goToSuffix': ' om als klant een test-offerte aan te maken.',
    'admin.testQuotes.autoAppear': 'Offertes die je daar indient verschijnen hier automatisch.',
    'admin.testQuotes.noQuotes': 'Geen offertes gevonden.',
    'admin.testQuotes.items': 'Items',
    'admin.testQuotes.exclVat': 'Excl. BTW',
    'admin.testQuotes.inclVat': 'Incl. BTW',
    'admin.testQuotes.date': 'Datum',
    'admin.testQuotes.action': 'Actie',
    'admin.testQuotes.changeStatus': 'Status wijzigen',
    'admin.testQuotes.client': 'Klant',
    'admin.testQuotes.email': 'E-mail',
    'admin.testQuotes.status': 'Status',
    'admin.testQuotes.delete': 'Verwijderen',
    'admin.testQuotes.deleteTitle': 'Offerte verwijderen',
    'admin.testQuotes.deleteConfirm': 'Weet je zeker dat je deze offerte wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden.',
    'admin.testQuotes.deleteOnlyDraft': 'Alleen concept-offertes kunnen verwijderd worden. Verstuurde offertes blijven gearchiveerd.',
    'admin.testQuotes.deleted': 'Offerte verwijderd.',
    'admin.testQuotes.archiveNote': 'Concept-offertes kunnen verwijderd worden. Verstuurde, geaccepteerde of geweigerde offertes worden permanent gearchiveerd voor audit-doeleinden.',

    // Restpartijen / einde-voorraad pagina
    'restpartijen.tag': 'Einde voorraad · Tijdelijk aanbod',
    'restpartijen.title': 'Restpartijen — kortingen op laatste voorraden',
    'restpartijen.desc': 'Echte premium baksteenstrips, vorige collecties of overstock van onze fabrikanten. Beperkte hoeveelheden, scherpe prijzen — op = op.',
    'restpartijen.loading': 'Restpartijen laden…',
    'restpartijen.empty': 'Op dit moment geen restpartijen beschikbaar',
    'restpartijen.emptyDesc': 'Onze voorraad rouleert regelmatig. Kom later terug of bekijk de volledige catalogus.',
    'restpartijen.backToCatalog': 'Terug naar catalogus',
    'restpartijen.available': 'Beschikbaar',
    'restpartijen.interested': 'Interesse in een restpartij?',
    'restpartijen.interestedDesc': 'Hoeveelheden zijn beperkt. Bel of mail voor reservatie of bezichtiging — eerst komen, eerst maalt.',
    'restpartijen.requestQuote': 'Offerte op deze stenen',
    // Hero block
    'restpartijen.hero.badge': 'Op = Op · Beperkte voorraad',
    'restpartijen.hero.title1': 'Premium baksteenstrips,',
    'restpartijen.hero.title2': 'tot 50% goedkoper',
    'restpartijen.hero.subtitle': 'Restpartijen, einde collecties en surplus van onze Italiaanse, Poolse en Spaanse fabrikanten. Zelfde premium kwaliteit — fractie van de prijs. Eerst komen, eerst maalt.',
    'restpartijen.hero.maxDiscount': 'Max korting',
    'restpartijen.hero.available': 'Beschikbaar',
    'restpartijen.hero.lots': 'Partijen',
    // Benefits
    'restpartijen.benefit1.title': 'Tot 50% korting',
    'restpartijen.benefit1.desc': 'Premium baksteen aan een fractie van de catalogusprijs. Hoe lager de voorraad, hoe scherper de prijs.',
    'restpartijen.benefit2.title': 'Zelfde premium kwaliteit',
    'restpartijen.benefit2.desc': 'Vorstbestendig (F2-klasse), kleurvast, 50+ jaar levensduur. Geen B-keuze of fabrieksfouten.',
    'restpartijen.benefit3.title': 'Snelle levering',
    'restpartijen.benefit3.desc': 'Restpartijen zijn al in ons depot in Schilde — levering binnen 5 werkdagen in heel België.',
    'restpartijen.benefit4.title': 'Uniek voor jouw project',
    'restpartijen.benefit4.desc': 'Vorige collecties die niet meer geproduceerd worden — exclusief voor jouw gevel, niemand anders heeft dit nog.',
    // List
    'restpartijen.list.title': 'Beschikbare restpartijen',
    'restpartijen.list.subtitle': 'Bekijk onze actuele aanbiedingen. Voorraad is beperkt — gereserveerde m² verdwijnen meteen uit deze lijst.',
    'restpartijen.list.opOp': 'Op = Op',
    'restpartijen.list.endOfStock': 'Einde stock',
    'restpartijen.list.savings': 'U bespaart',
    'restpartijen.list.left': 'beschikbaar',
    'restpartijen.list.reserve': 'Reserveer / vraag info',
    // Why
    'restpartijen.why.title': 'Waarom restpartijen kopen?',
    'restpartijen.why.1': 'Identieke kwaliteit als onze reguliere catalogus — geen tweede keus, geen scheurtjes, geen kleurfouten.',
    'restpartijen.why.2': 'Tot 50% goedkoper dan dezelfde steen op normale catalogusprijs.',
    'restpartijen.why.3': 'Onmiddellijk leverbaar — geen 4–6 weken wachten op fabrieksproductie.',
    'restpartijen.why.4': 'Exclusief — vorige collecties die elders niet meer te verkrijgen zijn.',
    'restpartijen.why.5': 'Volledige garantie zoals op nieuwe producten — 10 jaar materiaalgarantie.',
    'restpartijen.note.title': 'Belangrijk om te weten',
    'restpartijen.note.heading': 'Op = op — geen bijbestelling mogelijk',
    'restpartijen.note.body': 'Restpartijen zijn precies dat: laatste voorraad. Een match nabestellen voor uitbreiding is meestal niet meer mogelijk. Tip: bestel altijd 5–10 % extra voor snijverlies en eventuele latere reparaties.',
    'restpartijen.recentSold.title': 'Recent verkocht',
    'restpartijen.recentSold.desc': 'Wat je gemist hebt — deze restpartijen zijn intussen volledig verkocht. Schrijf je in voor de nieuwsbrief om als eerste op de hoogte te zijn van nieuwe aanbiedingen.',
    'restpartijen.recentSold.sticker': 'Verkocht',
    'nav.restpartijen': 'Restpartijen',
    'nav.contact': 'Contact',

    // Contact pagina
    'contact.tag': 'Neem contact op',
    'contact.title': 'Contacteer ons',
    'contact.subtitle': 'Vragen over een project, bestelling of samenwerking? Stuur een bericht of bel rechtstreeks. Wij antwoorden meestal binnen 24 uur.',
    'contact.formTitle': 'Bericht versturen',
    'contact.formDesc': 'Velden met een * zijn verplicht. Bij voorkeur ook uw postcode zodat wij u meteen kunnen koppelen aan onze lokale verdeler.',
    'contact.fName': 'Uw naam',
    'contact.fEmail': 'E-mail',
    'contact.fPhone': 'Telefoon',
    'contact.fPostal': 'Postcode',
    'contact.fPostalHint': 'Helpt ons om u te koppelen aan onze lokale verdeler in uw regio.',
    'contact.fSubject': 'Onderwerp',
    'contact.fMessage': 'Uw bericht',
    'contact.consent': 'Ik ga ermee akkoord dat mijn gegevens worden gebruikt om mijn vraag te beantwoorden, conform de privacyverklaring. Ze worden niet gedeeld met derden buiten onze installateurs in mijn regio.',
    'contact.send': 'Bericht versturen',
    'contact.sending': 'Versturen…',
    'contact.successTitle': 'Bericht verstuurd',
    'contact.successDesc': 'Bedankt — wij nemen zo snel mogelijk contact met u op.',
    'contact.errorTitle': 'Versturen mislukt',
    'contact.errorDesc': 'Er ging iets fout. Probeer opnieuw of bel ons rechtstreeks.',
    'contact.responseTimeTitle': 'Antwoordtijd',
    'contact.responseTimeDesc': 'Berichten worden binnen 24 werkuren beantwoord. Voor dringende projecten belt u best rechtstreeks.',
    'contact.emailRequired': 'E-mailadres is verplicht.',
    'contact.emailFormat': 'Geef een geldig e-mailadres in (bv. naam@voorbeeld.be).',
    'contact.emailDomain': 'Dit e-mailadres lijkt een test- of wegwerpadres. Gebruik een echt e-mailadres zodat we u kunnen bereiken.',
    'seo.contact.title': 'Contact · Gevelsteenbedrijf.be — Premium Baksteenstrips België',
    'seo.contact.description': 'Vragen over baksteenstrips, prijs, levering of installatie? Stuur een bericht of bel rechtstreeks. Schilde, België — bediening in heel Vlaanderen, Brussel en Wallonië.',
    'seo.contact.keywords': 'contact gevelsteenbedrijf, baksteenstrips contact, gevelbouwer Schilde, gevelrenovatie offerte, gevelstrips Antwerpen contact',
    'seo.restpartijen.title': 'Restpartijen Baksteenstrips · Kortingen tot 50% | Gevelsteenbedrijf.be',
    'seo.restpartijen.description': 'Premium baksteenstrips uit overstock en vorige collecties — tot 50% goedkoper. Beperkte hoeveelheden, vorstbestendig, klaar voor levering.',
    'seo.restpartijen.keywords': 'restpartij baksteenstrips, gevelstrips overstock, baksteen einde voorraad, baksteen kortingsactie, goedkope baksteenstrips, outlet gevelbekleding',

    // Admin Resellers tab (owner manages doorverkoopbedrijven)
    'admin.tab.resellers': 'Resellers',
    'admin.resellers.title': 'Doorverkoopbedrijven',
    'admin.resellers.empty': 'Nog geen resellers toegevoegd. Klik "Toevoegen" om te beginnen.',
    'admin.resellers.add': 'Toevoegen',
    'admin.resellers.new': 'Nieuwe reseller',
    'admin.resellers.edit': 'Reseller bewerken',
    'admin.resellers.company': 'Bedrijf',
    'admin.resellers.companyName': 'Bedrijfsnaam',
    'admin.resellers.slug': 'URL-slug',
    'admin.resellers.contactPerson': 'Contactpersoon',
    'admin.resellers.loginEmail': 'Login e-mail',
    'admin.resellers.btwNumber': 'BTW-nummer',
    'admin.resellers.contactEmail': 'Contact e-mail',
    'admin.resellers.contactPhone': 'Telefoon',
    'admin.resellers.address': 'Adres',
    'admin.resellers.postalCode': 'Postcode',
    'admin.resellers.city': 'Gemeente',
    'admin.resellers.discount': 'Korting',
    'admin.resellers.postcodes': 'Werkgebied',
    'admin.resellers.servicePostcodes': 'Werkgebied (komma-gescheiden postcodes)',
    'admin.resellers.postcodesHint': 'Bv. 2000,2018,2020 — eindklanten met deze postcodes worden automatisch toegewezen.',
    'admin.resellers.customers': 'Klanten',
    'admin.resellers.quotes': 'Offertes',
    'admin.resellers.status': 'Status',
    'admin.resellers.active': 'Actief',
    'admin.resellers.inactive': 'Gedeactiveerd',
    'admin.resellers.notes': 'Interne notities',
    'admin.resellers.agreementsTitle': 'Afspraken & voorwaarden met deze reseller',
    'admin.resellers.agreementsHint': 'Vrije tekst — leveringsvoorwaarden, betalingstermijn, kortingsstaffels, exclusiviteitsregelingen, contactpersoon, eindklant-protocol, etc. Alleen jij en de mede-eigenaar zien dit; reseller niet.',
    'admin.resellers.agreementsPlaceholder': 'Bv.\nKortingsstaffel: 30% standaard, 35% vanaf 200 m²\nLevering: franco vanaf €1500\nBetaling: 30 dagen factuur\nEindklant: reseller behoudt klant 2 jaar na laatste levering\nContact: jan@antwerpse-bricks.be (algemeen) / piet@… (technisch)',

    // Prijsbeheer — per-reseller pricing section
    'admin.pricing.resellerSection': 'Prijsafspraken per reseller',
    'admin.pricing.resellerSectionDesc': 'Stel een algemene korting in voor alle producten + optionele overrides per product. Wordt server-side toegepast bij elke offerte van/naar deze reseller.',
    'admin.pricing.selectReseller': 'Reseller kiezen',
    'admin.pricing.defaultDiscount': 'Standaardkorting',
    'admin.pricing.lastSaved': 'Laatst bijgewerkt op',
    // v3.2.61 — strings die voorheen hardcoded NL waren in AdminDashboard.tsx
    'admin.pricing.saveChanges': 'Wijzigingen opslaan',
    'admin.pricing.saveBarDesc': 'standaardkorting, kortingen per product en pauze-status voor',
    'admin.pricing.saveAllAbove': 'Bewaart alle wijzigingen hierboven',
    'admin.pricing.savingDots': 'Bezig met opslaan…',
    'admin.pricing.saveFor': 'Opslaan voor',
    'admin.pricing.thisReseller': 'deze reseller',
    'admin.pricing.tableDesc': 'Eén tabel met alles wat je nodig hebt: bruto-prijs van de leverancier, jouw markup en verkoopprijs uit Prijsbeheer, plus de korting per reseller (en eventueel pauze als je een product niet via deze reseller wil aanbieden).',
    'admin.pricing.colProduct': 'Product',
    'admin.pricing.colInkoop': 'Inkoop',
    'admin.pricing.colInkoopTitle': 'Inkoopprijs van de leverancier',
    'admin.pricing.colMarge': 'Marge',
    'admin.pricing.colMargeTitle': 'Marge = (verkoop − inkoop) / verkoop × 100. Zelfde formule als in Producten · Prijzen tab.',
    'admin.pricing.colSell': 'Verkoop',
    'admin.pricing.colSellTitle': 'Verkoopprijs (excl. BTW). Wordt aangepast in Producten · Prijzen tab.',
    'admin.pricing.colStandard': 'Standaard %',
    'admin.pricing.colStandardTitle': 'Standaardkorting van deze reseller (zelfde voor alle producten)',
    'admin.pricing.colDiscount': 'Korting per product %',
    'admin.pricing.colDiscountTitle': 'Korting specifiek voor dit product — overschrijft de standaardkorting wanneer ingevuld',
    'admin.pricing.colEffective': 'Effectief',
    'admin.pricing.colStatus': 'Status',
    'admin.pricing.statusActive': 'Actief',
    'admin.pricing.statusPause': 'Pauze',
    'admin.pricing.pausedItalic': 'gepauzeerd',
    'admin.pricing.pauseClick': 'Klik om dit product voor deze reseller te pauzeren',
    'admin.pricing.pauseUnpause': 'Klik om weer beschikbaar te maken',
    'admin.pricing.saved': '✓ Afspraken opgeslagen',
    'admin.pricing.saving': 'Opslaan…',
    'admin.pricing.saveAgreement': 'Afspraken opslaan',
    'admin.pricing.overridesTitle': 'Per-product afwijkingen (optioneel)',
    'admin.pricing.overridesDesc': 'Vul enkel in waar de afspraak afwijkt van de standaardkorting. Een vaste prijs heeft voorrang op een %-korting.',
    'admin.pricing.product': 'Product',
    'admin.pricing.catalog': 'Catalogus',
    'admin.pricing.overrideDiscount': 'Afwijkende %',
    'admin.pricing.overridePrice': 'Vaste prijs/m²',
    'admin.pricing.effective': 'Effectief',
    'admin.resellers.logoUrl': 'Logo URL',
    'admin.resellers.logoHint': 'Pad naar logo bestand (bv. /reseller-logos/naam.svg). Upload-UI volgt in v1.1.',
    'admin.resellers.deactivate': 'Deactiveren (data blijft bewaard)',
    'admin.resellers.confirmDeactivate': 'Deze reseller deactiveren? Alle historische data blijft bewaard, maar de reseller kan niet meer inloggen en nieuwe offertes komen niet meer binnen.',

    // Reseller dashboard (doorverkoopbedrijf)
    'reseller.title': 'Reseller Dashboard',
    'reseller.subtitle': 'Beheer je profiel, klanten en offertes',
    'reseller.loading': 'Laden…',
    'reseller.accessDenied': 'Geen toegang',
    'reseller.accessDeniedDesc': 'Deze pagina is enkel toegankelijk voor doorverkopers. Log in met een reseller-account.',
    'reseller.tab.profile': 'Profiel',
    'reseller.tab.catalog': 'Catalogus',
    'reseller.tab.customers': 'Mijn Klanten',
    'reseller.tab.quotes': 'Mijn Offertes',
    'reseller.profile.title': 'Bedrijfsprofiel',
    'reseller.profile.ownerControlled': 'Beheerd door Het Gevelsteenbedrijf',
    'reseller.profile.ownerControlledNote': 'Deze velden worden centraal beheerd. Vraag aanpassingen aan de eigenaar.',
    'reseller.profile.companyName': 'Bedrijfsnaam',
    'reseller.profile.btwNumber': 'BTW-nummer',
    'reseller.profile.discount': 'Standaardkorting',
    'reseller.profile.postcodes': 'Werkgebied (postcodes)',
    'reseller.profile.contactEmail': 'Contact e-mail',
    'reseller.profile.contactPhone': 'Contact telefoon',
    'reseller.profile.address': 'Adres',
    'reseller.profile.city': 'Gemeente',
    'reseller.profile.postalCode': 'Postcode',
    'reseller.catalog.title': 'Producten met jouw prijzen',
    'reseller.catalog.desc': 'Catalogusprijs is wat de eindklant ziet. Jouw prijs is de inkoopprijs voor jou (na korting).',
    'reseller.catalog.empty': 'Geen producten beschikbaar.',
    'reseller.catalog.product': 'Product',
    'reseller.catalog.color': 'Kleur',
    'reseller.catalog.category': 'Categorie',
    'reseller.catalog.catalogPrice': 'Catalogusprijs',
    'reseller.catalog.yourPrice': 'Jouw prijs',
    'reseller.catalog.discount': 'Korting',
    'reseller.catalog.remnant': 'Restpartij',
    'reseller.customers.title': 'Mijn Klanten',
    'reseller.customers.empty': 'Nog geen klanten in jouw werkgebied.',
    'reseller.customers.name': 'Naam',
    'reseller.customers.email': 'E-mail',
    'reseller.customers.type': 'Type',
    'reseller.customers.city': 'Gemeente',
    'reseller.customers.quotes': 'Offertes',
    'reseller.customers.since': 'Sinds',
    'reseller.quotes.title': 'Mijn Offertes',
    'reseller.quotes.empty': 'Nog geen offertes ontvangen.',
    'reseller.quotes.create': 'Nieuwe offerte',
    'reseller.quotes.createTitle': 'Offerte voor klant aanmaken',
    'reseller.quotes.fName': 'Naam klant',
    'reseller.quotes.fEmail': 'E-mail klant',
    'reseller.quotes.fPhone': 'Telefoon',
    'reseller.quotes.fPostal': 'Postcode',
    'reseller.quotes.fIsB2B': 'Bedrijf met BTW-nummer (B2B)',
    'reseller.quotes.fNotes': 'Notities (optioneel)',
    'reseller.quotes.items': 'Producten',
    'reseller.quotes.addItem': 'Product toevoegen',
    'reseller.quotes.selectProduct': 'Product kiezen…',

    // Backlog / Audit / Backup tab
    'admin.tab.backlog': 'Backlog',
    'admin.backlog.exportTitle': 'Exporteren / Backup',
    'admin.backlog.exportDesc': 'Download al je data als CSV. Bewaar deze offline of in de cloud — extra zekerheid bovenop de automatische Cloudflare D1 backups.',
    'admin.backlog.exportQuotes': 'Offertes (CSV)',
    'admin.backlog.exportCustomers': 'Klanten (CSV)',
    'admin.backlog.deletedTitle': 'Verwijderde offertes (herstelbaar)',
    'admin.backlog.deletedDesc': 'Concept-offertes die zijn weggegooid. Niets is permanent verloren — klik "Herstellen" om terug te plaatsen.',
    'admin.backlog.deletedEmpty': 'Geen verwijderde offertes.',
    'admin.backlog.deletedAt': 'Verwijderd op',
    'admin.backlog.restore': 'Herstellen',
    'admin.backlog.restoreConfirm': 'Deze offerte herstellen naar concept-status?',
    'admin.backlog.activityTitle': 'Activiteitenlog',
    'admin.backlog.activityDesc': 'Volledig overzicht van alle admin-acties: logins, offertes, status-wijzigingen, impersonaties. Compliance-bewijs.',
    'admin.backlog.activityEmpty': 'Geen activiteit gelogd.',
    'admin.backlog.when': 'Wanneer',
    'admin.backlog.who': 'Wie',
    'admin.backlog.what': 'Actie',
    'admin.backlog.details': 'Details',
    'admin.backlog.cloudflareTitle': 'Automatische cloud-backups',
    'admin.backlog.cloudflareDesc': 'Bovenop deze CSV-exports maakt Cloudflare D1 ook automatische point-in-time backups — terugzetten naar een vorig tijdstip is mogelijk in geval van calamiteit. Beheer dit via Cloudflare Dashboard → D1 → gevelstenen-db → Backups.',

    // Test Account Switcher (superadmin only)
    'switcher.title': 'Test Account Switcher',
    'switcher.subtitle': 'Schakel naar elk account om te zien wat zij zien',
    'switcher.toggle': 'Test als…',
    'switcher.loading': 'Accounts laden…',
    'switcher.empty': 'Geen test accounts gevonden.',
    'switcher.error': 'Schakelen mislukt',
    'switcher.hint': '⚠️ Alle acties worden gelogd in activity_log. Log uit om terug te keren naar superadmin.',

    // Admin Mede-eigenaar section
    'admin.coOwner': 'Mede-eigenaar',
    'admin.coOwner.sameData': 'Beide eigenaren zien dezelfde data',

    // Admin Prijzen tab
    'admin.pricing.title': 'Prijsbeheer',
    'admin.pricing.desc': 'Brutoprijs = leveranciersprijs. Markup % = uw marge (standaard 25%). Verkoopprijs = bruto × (1 + markup/100). Bij ontbrekende leveranciersprijs wordt teruggerekend vanuit de vaste prijs.',
    'admin.pricing.noProducts': 'Geen producten gevonden.',
    'admin.pricing.grossPrice': 'Brutoprijs',
    'admin.pricing.grossPriceSupplier': 'Brutoprijs (leverancier)',
    'admin.pricing.markup': 'Markup',
    'admin.pricing.markupPct': 'Markup %',
    'admin.pricing.sellPrice': 'Verkoopprijs',
    'admin.pricing.fixedPrice': 'Vaste Prijs',
    'admin.pricing.product': 'Product',
    'admin.pricing.category': 'Categorie',
    'admin.pricing.actions': 'Acties',
    'admin.pricing.save': 'Opslaan',
    'admin.pricing.saving': 'Opslaan...',
    'admin.pricing.cancel': 'Annuleren',
    'admin.pricing.edit': 'Bewerk',

    // Admin PDF labels
    'admin.pdf.quote': 'OFFERTE',
    'admin.pdf.ref': 'Ref',
    'admin.pdf.date': 'Datum',
    'admin.pdf.validUntil': 'Geldig tot',
    'admin.pdf.client': 'Klant',
    'admin.pdf.status': 'Status',
    'admin.pdf.private': 'Particulier (B2C)',
    'admin.pdf.product': 'Product',
    'admin.pdf.articleNumber': 'Artikelnummer',
    'admin.pdf.colorTexture': 'Kleur / Textuur',
    'admin.pdf.surface': 'Oppervlakte',
    'admin.pdf.jointWidth': 'Voegbreedte',
    'admin.pdf.jointColor': 'Voegkleur',
    'admin.pdf.cuttingLoss': 'Snijverlies',
    'admin.pdf.stripCount': 'Aantal strips',
    'admin.pdf.corners': 'Hoekstukken',
    'admin.pdf.glue': 'Lijm',
    'admin.pdf.grout': 'Voegsel',
    'admin.pdf.requestDetails': 'Aanvraaggegevens',
    'admin.pdf.configuration': 'Configuratie',
    'admin.pdf.clientType': 'Type klant',
    'admin.pdf.company': 'Bedrijf',
    'admin.pdf.btwNumber': 'BTW-nummer',
    'admin.pdf.postalCode': 'Postcode',
    'admin.pdf.message': 'Bericht',
    'admin.pdf.priceOverview': 'Prijsoverzicht',
    'admin.pdf.description': 'Omschrijving',
    'admin.pdf.amount': 'Bedrag',
    'admin.pdf.subtotalExclVat': 'Subtotaal excl. BTW',
    'admin.pdf.vat': 'BTW (21%)',
    'admin.pdf.totalInclVat': 'Totaal incl. BTW',
    'admin.pdf.conditions': 'Algemene voorwaarden',
    'admin.pdf.cond1': 'Offerte geldig tot 30 dagen na datum',
    'admin.pdf.cond2': 'Leveringstermijn: 2-4 weken na bevestiging',
    'admin.pdf.cond3': 'Betaling: 50% voorschot, 50% bij levering',
    'admin.pdf.cond4': 'Prijzen exclusief plaatsing tenzij anders vermeld',

    // CUSTOMER DASHBOARD
    'customer.business': 'Zakelijk',
    'customer.private': 'Particulier',
    'customer.btwAccount': 'BTW-plichtig account',

    // Customer tabs
    'customer.tab.myData': 'Mijn Gegevens',
    'customer.tab.myQuotes': 'Mijn Offertes',
    'customer.tab.myPhotos': "Mijn Foto's",

    // Customer Gegevens
    'customer.data.title': 'Mijn Gegevens',
    'customer.data.edit': 'Bewerken',
    'customer.data.cancel': 'Annuleren',
    'customer.data.save': 'Opslaan',
    'customer.data.saving': 'Opslaan...',
    'customer.data.saved': 'Gegevens opgeslagen',
    'customer.data.saveFailed': 'Opslaan mislukt.',
    'customer.data.name': 'Naam',
    'customer.data.email': 'E-mail',
    'customer.data.phone': 'Telefoon',
    'customer.data.address': 'Adres',
    'customer.data.postalCode': 'Postcode',
    'customer.data.city': 'Stad',
    'customer.data.company': 'Bedrijfsnaam',
    'customer.data.btw': 'BTW-nummer',
    'customer.data.notes': 'Opmerkingen',
    'customer.data.companyLabel': 'Bedrijf',

    // Customer Offertes
    'customer.quotes.title': 'Mijn Offertes',
    'customer.quotes.newCalc': 'Nieuwe berekening',
    'customer.quotes.noQuotes': 'Nog geen offertes',
    'customer.quotes.noQuotesDesc': 'Gebruik onze calculator om uw eerste offerte aan te vragen.',
    'customer.quotes.goToCalc': 'Naar calculator',
    'customer.quotes.subtotal': 'Subtotaal',
    'customer.quotes.vat': 'BTW',
    'customer.quotes.totalInclVat': 'Totaal incl. BTW',
    'customer.quotes.priceInclVat': 'Prijs incl. BTW',
    'customer.quotes.pdfGenerating': 'PDF wordt gegenereerd',

    // Customer quote statuses
    'customer.status.draft': 'Concept',
    'customer.status.sent': 'Verstuurd',
    'customer.status.accepted': 'Geaccepteerd',
    'customer.status.rejected': 'Afgewezen',
    'customer.status.expired': 'Verlopen',

    // Customer Foto's
    'customer.photos.title': "Mijn Foto's",
    'customer.photos.count': "foto's",
    'customer.photos.addPhoto': 'Foto toevoegen',
    'customer.photos.noPhotos': "Geen foto's",
    'customer.photos.noPhotosDesc': "Upload foto's van uw woning of project voor persoonlijk advies.",
    'customer.photos.upload': "Foto's uploaden",
    'customer.photos.projectPhoto': 'Project foto',

    // ADMIN SHORTHAND KEYS
    'admin.products': 'Producten',
    'admin.suppliers': 'Leveranciers',
    'admin.customers': 'Klanten',
    'admin.quotes': 'Offertes',
    'admin.pricing': 'Prijsbeheer',
    'admin.name': 'Naam',
    'admin.email': 'E-mail',
    'admin.status': 'Status',
    'admin.items': 'Items',
    'admin.exclVat': 'Excl. BTW',
    'admin.inclVat': 'Incl. BTW',
    'admin.date': 'Datum',
    'admin.action': 'Actie',
    'admin.noQuotes': 'Geen offertes',
    'admin.draft': 'concept',
    'admin.sent': 'verzonden',
    'admin.accepted': 'geaccepteerd',
    'admin.rejected': 'geweigerd',
    'admin.save': 'Opslaan',
    'admin.phone': 'Telefoon',
    'admin.company': 'Bedrijf',
    'admin.role': 'Rol',
    'admin.price': 'Prijs',
    'admin.supplier': 'Leverancier',
    'admin.markup': 'Marge',
    'admin.sellPrice': 'Verkoopprijs',
    'admin.bestseller': 'Bestseller',
    'admin.filter': 'Filter',
    'admin.sort': 'Sorteer',

    // CUSTOMER SHORTHAND KEYS
    'customer.myQuotes': 'Mijn Offertes',
    'customer.myPhotos': 'Mijn Foto\'s',
    'customer.myData': 'Mijn Gegevens',
    'customer.noQuotes': 'Nog geen offertes',
    'customer.uploadPhoto': 'Foto uploaden',
    'customer.tab.myFavorites': 'Favorieten',
    'customer.favorites.title': 'Mijn Favorieten',
    'customer.favorites.desc': 'Producten die u heeft gemarkeerd. Filip en de verdeler in uw regio kunnen deze voorkeuren zien wanneer ze een offerte voor u opstellen.',
    'customer.favorites.loading': 'Favorieten laden…',
    'customer.favorites.empty': 'Nog geen favorieten',
    'customer.favorites.emptyDesc': 'Markeer producten met het hartje op de catalogus om ze hier terug te vinden.',
    'customer.favorites.browse': 'Catalogus bekijken',
    'customer.favorites.remove': 'Uit favorieten halen',
    'customer.photos.rulesTitle': 'Upload-regels',
    'customer.photos.ruleMax': 'Maximum {n} foto\'s per account',
    'customer.photos.ruleSize': 'Maximum {mb} MB per foto',
    'customer.photos.ruleType': 'Toegestaan: JPG, PNG, WebP',
    'customer.photos.ruleResolution': 'Aanbevolen: minstens 1024×768 pixels, foto\'s worden automatisch verkleind voor optimale weergave',
    'customer.photos.maxReached': 'U heeft al het maximum aantal foto\'s. Verwijder eerst een foto om er een nieuwe toe te voegen.',
    'customer.photos.errType': 'Bestandstype niet ondersteund. Gebruik JPG, PNG of WebP.',
    'customer.photos.errSize': 'Bestand te groot — maximum 5 MB',
    'customer.photos.errTrimmed': 'U heeft meer foto\'s gekozen dan toegestaan. Enkel de eerste {n} worden geüpload.',
    'customer.photos.errGeneric': 'Upload mislukt. Probeer opnieuw of contacteer ons.',

    // PDF — Offerte-export (alle keys gebruikt door src/lib/quotePdf.ts)
    'pdf.quote': 'OFFERTE',
    'pdf.ref': 'Ref',
    'pdf.date': 'Datum',
    'pdf.validUntil': 'Geldig tot',
    'pdf.client': 'Klant',
    'pdf.status': 'Status',
    'pdf.product': 'Product',
    'pdf.configuration': 'Configuratie',
    'pdf.requestDetails': 'Aanvraagdetails',
    'pdf.priceOverview': 'Prijsoverzicht',
    'pdf.description': 'Omschrijving',
    'pdf.amount': 'Bedrag',
    'pdf.subtotalExcl': 'Subtotaal excl. BTW',
    'pdf.vat21': 'BTW (21%)',
    'pdf.totalIncl': 'Totaal incl. BTW',
    'pdf.totalPrice': 'Totaalprijs',
    'pdf.vatIncluded': 'BTW (21%) inbegrepen',
    'pdf.conditions': 'Algemene voorwaarden',
    'pdf.cond1': 'Offerte geldig tot 30 dagen na datum',
    'pdf.cond2': 'Leveringstermijn: 2-4 weken na bevestiging',
    'pdf.cond3': 'Betaling: 50% voorschot, 50% bij levering',
    'pdf.cond4': 'Prijzen exclusief plaatsing tenzij anders vermeld',
    'pdf.poweredBy': 'Mogelijk gemaakt door Het Gevelsteenbedrijf',
    'pdf.installerNote': 'Erkend distributeur & installateur in samenwerking met Het Gevelsteenbedrijf',
    'pdf.unitPriceM2': 'EENHEIDSPRIJZEN PER M²',
    'pdf.totalsForQuote': 'Totalen voor deze offerte',
    'pdf.installAddressLabel': 'Werkadres / Plaatsing',
    'pdf.installAddressNote': 'Levering en plaatsing gebeurt op dit adres — niet op het factuuradres.',
    // PDF — Levering & plaatsing-blok + installer-status (v4)
    'pdf.deliveryHeading': 'Levering & plaatsing',
    'pdf.deliveryNote1': 'Adres voor afhaling, levering of plaatsing wordt bevestigd na akkoord op deze offerte.',
    'pdf.deliveryNote2': 'Bij eigen plaatsing levert u alleen materiaal af; bij installatie via ons netwerk wordt een installateur gekoppeld.',
    'pdf.installerSelfShort': 'Klant plaatst zelf',
    'pdf.installerWantedShort': 'Installateur via ons netwerk',
    // PDF — Configuratie/breakdown labels (v4)
    'pdf.cfg.product': 'Product',
    'pdf.cfg.articleNr': 'Artikelnummer',
    'pdf.cfg.colorTexture': 'Kleur / Textuur',
    'pdf.cfg.dimensions': 'Afmetingen',
    'pdf.cfg.area': 'Oppervlakte',
    'pdf.cfg.jointWidth': 'Voegbreedte',
    'pdf.cfg.jointColor': 'Voegkleur',
    'pdf.cfg.cuttingLoss': 'Snijverlies',
    'pdf.cfg.stripCount': 'Aantal strips',
    'pdf.cfg.cornerPieces': 'Hoekstukken',
    'pdf.cfg.glue': 'Lijm',
    'pdf.cfg.grout': 'Voegsel',
    'pdf.cfg.bricks': 'Baksteenstrips',
    'pdf.cfg.cornerStones': 'Hoekstenen',
    // Eenheden voor notes_nl values (v4.0.2)
    'pdf.unit.bags': 'zakken',
    'pdf.unit.pieces': 'st',
  },

  fr: {
    'seo.retour.description': 'Comment retourner des plaquettes de parement et quand vous êtes remboursé : délais, conditions et étapes par cas.',
    'seo.retour.keywords': 'retour plaquettes de parement, remboursement, droit de rétractation, politique de retour',
    'seo.retour.title': 'Retour & remboursement | Gevelsteenbedrijf.be',
    'cookie.functional.desc': 'Mémorisent vos préférences comme la langue et les produits choisis.',
    'cookie.functional.title': 'Fonctionnels',
    'cookie.gpcDetected': 'Votre navigateur envoie un signal Global Privacy Control. Nous n\'avons présélectionné que les cookies nécessaires.',
    'cookie.savePreferences': 'Enregistrer les préférences',
    'home.tagline': 'BE PREMIUM BELGIAN QUALITY',
    // NAV
    'nav.home': 'Accueil',
    'nav.products': 'Produits',
    'nav.calculator': 'Calculateur',
    'nav.inspiration': 'Inspiration & réalisations',
    'nav.architects': 'Architectes',
    'nav.custom': 'Infos techniques',
    'nav.quote': 'Devis',
    'nav.login': 'Connexion',
    'nav.logout': 'Déconnexion',
    'nav.dashboard': 'Tableau de bord',
    'nav.requestQuote': 'Demander un Devis',

    // FOOTER
    'footer.desc': 'Votre spécialiste en plaquettes de parement premium. Directement de l\'usine à votre projet, avec le meilleur rapport qualité-prix.',
    'footer.belgian': 'Entreprise belge',
    'footer.nav': 'Navigation',
    'footer.professionals': 'Professionnels',
    'footer.techSheets': 'Fiches Techniques',
    'footer.customStones': 'Pierres Sur Mesure',
    'footer.wholesaleLogin': 'Login Grossiste',
    'footer.b2bPricing': 'Prix B2B',
    'footer.contact': 'Contact',
    'footer.rights': '© 2026 Gevelsteenbedrijf.be — Tous droits réservés',
    'footer.terms': 'Conditions Générales',
    'pdp.title.suffix': 'acheter plaquettes de parement',
    'pdp.metaDesc': 'plaquettes de brique premium, directement du fabricant. Prix au m² :',
    'pdp.exVat': 'hors TVA',
    'pdp.spec.dimensions': 'Dimensions',
    'pdp.spec.color': 'Couleur',
    'pdp.spec.texture': 'Texture',
    'pdp.spec.category': 'Style',
    'pdp.spec.sku': 'Référence',
    'pdp.spec.delivery': 'Délai de livraison',
    'pdp.spec.weeks': 'semaines',
    'pdp.usp1': 'Qualité identique à la brique pleine — 10 ans de garantie matériau',
    'pdp.usp2': 'Directement du fabricant, sans intermédiaires',
    'pdp.usp3': 'Devis gratuit avec calcul exact des matériaux',
    'pdp.ctaCalc': 'Calculez votre projet avec cette brique',
    'pdp.ctaFiche': 'Fiche technique',
    'pdp.more': 'Découvrez nos autres briques',
    'retour.title': 'Retours & remboursement',
    'retour.intro': 'Voici comment fonctionnent les retours et remboursements. Des questions ? Contactez-nous, nous trouvons volontiers une solution.',
    'retour.s1.title': 'Droit de rétractation — 14 jours',
    'retour.s1.body': 'En tant que consommateur, vous disposez d\'un droit légal de rétractation de 14 jours calendrier après la livraison, sans motif. Signalez-le par e-mail ; vous recevrez aussitôt les instructions de retour.',
    'retour.s2.title': 'Renvoi',
    'retour.s2.body': 'Renvoyez les produits dans les 14 jours suivant votre notification, non endommagés, non utilisés et dans l\'emballage d\'origine sur palettes. Les frais et risques du retour sont à charge de l\'acheteur, sauf en cas de livraison erronée ou endommagée — nous organisons et payons alors l\'enlèvement.',
    'retour.s3.title': 'Remboursement — sous 14 jours',
    'retour.s3.body': 'Nous remboursons le montant total (frais de livraison initiaux inclus) dans les 14 jours après réception du retour, ou après preuve d\'expédition — selon la première échéance. Le remboursement s\'effectue via le même moyen de paiement.',
    'retour.s4.title': 'Exceptions',
    'retour.s4.body': 'Les lots de fin de série sont vendus en l\'état et exclus du retour (clairement indiqué sur la page Fins de série). Les briques sur mesure ou produites spécialement ne peuvent pas être retournées. Les sacs de colle ou de joint entamés ne sont pas repris.',
    'retour.s5.title': 'Livraison endommagée ou erronée',
    'retour.s5.body': 'Contrôlez la livraison à la réception et signalez tout dommage visible sous 48 heures avec photos. Nous remplaçons gratuitement les produits endommagés ou erronés, ou les remboursons — à votre choix.',
    'retour.btw': 'TVA',
    'footer.retour': 'Retours & remboursement',
    'calc.fixedLoss': '(fixe — inclus automatiquement)',
    'calc.stoneStrip': 'Nos types de briques — touchez pour changer',
    'data.insp.i5.title': 'Maison de rangée Anvers-Sud',
    'data.insp.i5.desc': 'Maison de rangée rénovée en tons gris et joints fins — urbain et intemporel.',
    'data.insp.i6.title': 'Maison BEN à Malines',
    'data.insp.i6.desc': 'Construction neuve quasi neutre en énergie avec accents taupe et gris-bleu sur le pignon.',
    'data.insp.i7.title': 'Fermette au Pajottenland',
    'data.insp.i7.desc': 'Fermette chaleureuse aux plaquettes ivoire et terracotta, en harmonie avec le paysage vallonné.',
    'data.insp.i8.title': 'Ferme du Westhoek',
    'data.insp.i8.desc': 'Ferme reconstruite avec plaquettes rustiques rouges et large joint sablé.',
    'data.insp.i9.title': 'Entrepôt Hasselt Kanaalkom',
    'data.insp.i9.desc': 'Entrepôt reconverti au bord de l\'eau, bandes noires et gris nuancé en appareil sauvage.',
    'data.insp.i10.title': 'Hall de bureaux Courtrai',
    'data.insp.i10.desc': 'Hall transformé en bureaux ; des plaquettes sombres donnent au volume une peau industrielle.',
    'data.insp.i11.title': 'Maison de maître Louvain',
    'data.insp.i11.desc': 'Maison de maître au centre protégé, restaurée en appareil nuancé et joint blanc.',
    'data.insp.i12.title': 'Presbytère au Pays de Waas',
    'data.insp.i12.desc': 'Presbytère classique en rouge chaud et jaune sable — la tradition flamande en plaquettes.',
    'footer.privacy': 'Confidentialité',
    'footer.cookies': 'Cookies',

    // HOME
    'home.badge': 'Qualité Belge Premium',
    'home.heroTitle1': 'Donnez à votre façade',
    'home.heroTitle2': 'du caractère',
    'home.heroDesc': 'Plaquettes de parement premium directement de l\'usine. Économisez jusqu\'à 40% grâce à notre modèle de livraison directe — sans intermédiaires.',
    'home.discoverCollection': 'Découvrir la Collection',
    'home.calculator': 'Calculateur',
    'home.stat.products': 'Produits',
    'home.stat.projects': 'Projets',
    'home.stat.savings': 'Économie',
    'home.usp.factory': 'Direct de l\'usine',
    'home.usp.delivery': 'Livraison par distributeur local',
    'home.usp.calc': 'Calcul gratuit',
    'home.usp.custom': 'Sur mesure possible',
    'home.usp.warranty': 'Garantie incluse',
    'home.model': 'Notre Modèle',
    'home.whyTitle': 'Pourquoi Gevelsteenbedrijf ?',
    'home.whyDesc': 'Nous relions directement les fabricants internationaux à vous, via un réseau de distributeurs et installateurs locaux — pour le meilleur prix et un service personnalisé.',
    'home.model1.title': 'Fabricants Internationaux',
    'home.model1.desc': 'Collaboration directe avec les meilleurs fabricants de Pologne, d\'Espagne et d\'Italie pour la meilleure qualité et le meilleur prix.',
    'home.model2.title': 'Distributeurs Locaux',
    'home.model2.desc': 'Votre commande est livrée par un distributeur agréé dans votre région, qui peut également recommander un installateur.',
    'home.model3.title': 'Meilleur Prix Garanti',
    'home.model3.desc': 'Grâce à la ligne directe, vous économisez jusqu\'à 40% par rapport aux canaux de distribution traditionnels.',
    'home.collection': 'Collection',
    'home.popularStrips': 'Plaquettes de Parement Populaires',
    'home.viewAll': 'Voir tous les produits →',
    'home.onlineTool': 'Outil en Ligne',
    'home.calcTitle': 'Calculez votre projet en 2 minutes',
    'home.calcDesc': 'Notre calculateur avancé calcule exactement combien de plaquettes, colle, mortier et pierres d\'angle vous avez besoin — y compris les pertes de coupe.',
    'home.openCalc': 'Ouvrir le Calculateur',
    'home.realisations': 'Réalisations',
    'home.inspiration': 'Inspiration',
    'home.allProjects': 'Tous les projets →',
    'home.private.title': 'Particuliers',
    'home.private.desc': 'Construisez ou rénovez votre maison de rêve avec des plaquettes premium à prix usine. Utilisez notre calculateur et demandez un devis — nous vous connectons avec un distributeur local.',
    'home.private.cta': 'Calculez votre projet',
    'home.pro.title': 'Architectes & Professionnels',
    'home.pro.desc': 'Téléchargez fiches techniques, déclarations de performance et fichiers BIM. Profitez de notre programme sur mesure pour des projets uniques.',
    'home.pro.cta': 'Info Technique',
    'home.ctaTitle': 'Prêt à commencer ?',
    'home.ctaDesc': 'Demandez un devis gratuit aujourd\'hui et découvrez combien vous pouvez économiser avec notre modèle de livraison directe.',
    'home.requestQuote': 'Demander un Devis',
    'home.viewCollection': 'Voir la Collection',

    // PRODUCTS
    'products.collection': 'Collection',
    'products.title': 'Nos Plaquettes de Parement',
    'products.desc': 'Découvrez notre gamme de plaquettes premium. Filtrez par couleur et texture pour trouver la pierre parfaite pour votre projet.',
    'products.search': 'Rechercher plaquettes...',
    'products.allColors': 'Toutes les Couleurs',
    'products.allTextures': 'Toutes les Textures',
    'products.clearFilters': 'Effacer les filtres',
    'products.found': 'trouvé(s)',
    'products.product': 'produit',
    'products.products': 'produits',
    'products.tapToPreview': 'Cliquez sur le numéro de la brique choisie',
    'products.noResults': 'Aucun produit trouvé',
    'products.noResultsDesc': 'Ajustez vos filtres pour trouver des produits.',
    'products.quote': 'Devis',
    'products.calculate': 'Calculer',
    'products.3d.title': 'Aperçu 3D de façade',
    'products.3d.rotate': 'Faites pivoter et inclinez pour voir la maison sous tous les angles',
    'products.3d.download': 'Télécharger PDF',
    'products.3d.uploadTeaser': 'Téléchargez des photos de votre maison',
    'products.3d.uploadCta': 'Bientôt : téléchargez max. 5 photos et visualisez votre façade avec les briquettes choisies en 3D.',
    'products.3d.comingSoon': 'Bientôt',
    'products.3d.roofLabel': 'Toit',
    'products.3d.roofFlat': 'Plat',
    'products.3d.roofTiles': 'Tuiles',
    'products.3d.roofSlate': 'Ardoise',
    'products.3d.nightMode': 'Nuit',
    'products.3d.dayMode': 'Jour',
    'products.3d.dragHint': 'glisser',
    'products.3d.roofToggle': 'Toit ouvert',
    'products.3d.roofToggleClose': 'Toit fermé',
    'products.3d.materialsLabel': 'Calcul des matériaux',
    'products.3d.materialsWall': 'Surface totale façade',
    'products.3d.materialsWindows': 'Ouvertures',
    'products.3d.materialsNet': 'Briques nettes',
    'products.3d.gardenClick': 'Cliquez sur les éléments du jardin pour info',

    // HERO + CALCULATOR INTRO (v3.2.102)
    'home.heroBadge':       'Qualité belge premium',
    'home.heroTitle':       'Donnez du',
    'home.heroTitleAccent': 'caractère à votre façade',
    'home.heroSubtitle':    'Plaquettes de parement premium directement du fabricant. Économisez jusqu\'à 40 % — sans intermédiaire.',
    'home.calcBadge':       'Calculateur',
    'home.calcTitle':       'Choisissez votre brique — calculez votre prix',
    'home.calcDesc':        'Sélectionnez un produit ci-dessous, entrez votre surface et obtenez immédiatement un calcul précis.',
    'home.savings':         'économisez',

    // CALCULATOR
    'calc.onlineTool': 'Outil en Ligne',
    'calc.title': 'Calculateur de Matériaux',
    'calc.desc': 'Calculez exactement combien de plaquettes, colle, mortier et pierres d\'angle vous avez besoin pour votre projet.',
    'calc.productChoice': 'Choix du Produit',
    'calc.area': 'Surface',
    'calc.areaLabel': 'Surface nette (m²) — sans fenêtres et portes',
    'calc.jointWidth': '↔️ Largeur de Joint',
    'calc.noJoint': 'Sans joint',

    'v6.kiesSteen': 'Choisir cette brique — calculez votre prix',
    'admin.products.discountManage': 'Gérer la remise',
    'admin.resellers.contactAdd': 'Ajouter',
    'admin.resellers.contactRole': 'Rôle (p.ex. Vente, Logistique)',
    'admin.resellers.contactsEmpty': 'Pas encore de contacts supplémentaires',
    'admin.resellers.contactsHint': 'Autres personnes de la même entreprise',
    'admin.resellers.contactsTitle': 'Personnes de contact supplémentaires',
    'admin.resellers.discount2': 'Remise',
    'admin.resellers.installerAdd': 'Ajouter',
    'admin.resellers.installerNotes': 'Notes (spécialité, région, disponibilité…)',
    'admin.resellers.installerTitle': 'Installateurs de ce revendeur',
    'admin.resellers.installersEmpty': 'Pas encore d\'installateurs',
    'admin.resellers.installersHint': 'Entreprises que le revendeur peut recommander pour la pose',
    'admin.resellers.website': 'Site web',
    'admin.resellers.websiteHint': 'Site public de ce revendeur (optionnel)',
    'pdf.downloadHint': 'Cliquez sur « Enregistrer en PDF » ou appuyez sur Ctrl/Cmd+P',
    'pdf.print': 'Enregistrer en PDF',
    'admin.pricing.colDiscount': 'Remise par produit %',
    'admin.pricing.colDiscountTitle': 'Remise spécifique à ce produit — remplace la remise standard lorsqu\'elle est renseignée',
    'admin.pricing.colEffective': 'Effectif',
    'admin.pricing.colInkoop': 'Achat',
    'admin.pricing.colInkoopTitle': 'Prix d\'achat du fournisseur',
    'admin.pricing.colMarge': 'Marge',
    'admin.pricing.colMargeTitle': 'Marge = (vente − achat) / vente × 100. Même formule que dans l\'onglet Produits · Prix.',
    'admin.pricing.colProduct': 'Produit',
    'admin.pricing.colSell': 'Vente',
    'admin.pricing.colSellTitle': 'Prix de vente (hors TVA). Se modifie dans l\'onglet Produits · Prix.',
    'admin.pricing.colStandard': 'Standard %',
    'admin.pricing.colStandardTitle': 'Remise standard de ce revendeur (identique pour tous les produits)',
    'admin.pricing.colStatus': 'Statut',
    'admin.pricing.pauseClick': 'Cliquez pour mettre ce produit en pause pour ce revendeur',
    'admin.pricing.pauseUnpause': 'Cliquez pour le rendre à nouveau disponible',
    'admin.pricing.pausedItalic': 'en pause',
    'admin.pricing.saveAllAbove': 'Enregistre toutes les modifications ci-dessus',
    'admin.pricing.saveBarDesc': 'remise standard, remises par produit et statut de pause pour',
    'admin.pricing.saveChanges': 'Enregistrer les modifications',
    'admin.pricing.saveFor': 'Enregistrer pour',
    'admin.pricing.savingDots': 'Enregistrement en cours…',
    'admin.pricing.statusActive': 'Actif',
    'admin.pricing.statusPause': 'Pause',
    'admin.pricing.tableDesc': 'Un seul tableau avec tout ce dont vous avez besoin : le prix brut du fournisseur, votre marge et votre prix de vente issus de la Gestion des prix, ainsi que la remise par revendeur (et éventuellement la pause si vous ne souhaitez pas proposer un produit via ce revendeur).',
    'admin.pricing.thisReseller': 'ce revendeur',
    'calc.desc': 'Calculez exactement combien de plaquettes, colle, mortier et pierres d\'angle vous avez besoin pour votre projet.',
    'calc.onlineTool': 'Outil en Ligne',
    'calc.title': 'Calculateur de Matériaux',
    'home.allProjects': 'Tous les projets →',
    'home.badge': 'Qualité Belge Premium',
    'home.calculator': 'Calculateur',
    'home.collection': 'Collection',
    'home.ctaDesc': 'Demandez un devis gratuit aujourd\'hui et découvrez combien vous pouvez économiser avec notre modèle de livraison directe.',
    'home.ctaTitle': 'Prêt à commencer ?',
    'home.discoverCollection': 'Découvrir la Collection',
    'home.heroTitle2': 'du caractère',
    'home.inspiration': 'Inspiration',
    'home.model': 'Notre Modèle',
    'home.model1.desc': 'Collaboration directe avec les meilleurs fabricants de Pologne, d\'Espagne et d\'Italie pour la meilleure qualité et le meilleur prix.',
    'home.model1.title': 'Fabricants Internationaux',
    'home.model2.desc': 'Votre commande est livrée par un distributeur agréé dans votre région, qui peut également recommander un installateur.',
    'home.model2.title': 'Distributeurs Locaux',
    'home.model3.desc': 'Grâce à la ligne directe, vous économisez jusqu\'à 40% par rapport aux canaux de distribution traditionnels.',
    'home.model3.title': 'Meilleur Prix Garanti',
    'home.onlineTool': 'Outil en Ligne',
    'home.openCalc': 'Ouvrir le Calculateur',
    'home.popularStrips': 'Plaquettes de Parement Populaires',
    'home.private.cta': 'Calculez votre projet',
    'home.private.desc': 'Construisez ou rénovez votre maison de rêve avec des plaquettes premium à prix usine. Utilisez notre calculateur et demandez un devis — nous vous connectons avec un distributeur local.',
    'home.private.title': 'Particuliers',
    'home.pro.cta': 'Info Technique',
    'home.pro.desc': 'Téléchargez fiches techniques, déclarations de performance et fichiers BIM. Profitez de notre programme sur mesure pour des projets uniques.',
    'home.pro.title': 'Architectes & Professionnels',
    'home.realisations': 'Réalisations',
    'home.requestQuote': 'Demander un Devis',
    'home.stat.products': 'Produits',
    'home.stat.projects': 'Projets',
    'home.stat.savings': 'Économie',
    'home.usp.calc': 'Calcul gratuit',
    'home.usp.custom': 'Sur mesure possible',
    'home.usp.delivery': 'Livraison par distributeur local',
    'home.usp.factory': 'Direct de l\'usine',
    'home.usp.warranty': 'Garantie incluse',
    'home.viewAll': 'Voir tous les produits →',
    'home.viewCollection': 'Voir la Collection',
    'home.whyDesc': 'Nous relions directement les fabricants internationaux à vous, via un réseau de distributeurs et installateurs locaux — pour le meilleur prix et un service personnalisé.',
    'home.whyTitle': 'Pourquoi Gevelsteenbedrijf ?',
    'nav.architects': 'Architectes',
    'nav.custom': 'Infos techniques',
    'nav.requestQuote': 'Demander un Devis',
    'products.3d.comingSoon': 'Bientôt',
    'products.3d.dayMode': 'Jour',
    'products.3d.download': 'Télécharger PDF',
    'products.3d.dragHint': 'glisser',
    'products.3d.gardenClick': 'Cliquez sur les éléments du jardin pour info',
    'products.3d.materialsLabel': 'Calcul des matériaux',
    'products.3d.materialsNet': 'Briques nettes',
    'products.3d.materialsWall': 'Surface totale façade',
    'products.3d.materialsWindows': 'Ouvertures',
    'products.3d.nightMode': 'Nuit',
    'products.3d.roofFlat': 'Plat',
    'products.3d.roofLabel': 'Toit',
    'products.3d.roofSlate': 'Ardoise',
    'products.3d.roofTiles': 'Tuiles',
    'products.3d.roofToggle': 'Toit ouvert',
    'products.3d.roofToggleClose': 'Toit fermé',
    'products.3d.rotate': 'Faites pivoter et inclinez pour voir la maison sous tous les angles',
    'products.3d.title': 'Aperçu 3D de façade',
    'products.3d.uploadCta': 'Bientôt : téléchargez max. 5 photos et visualisez votre façade avec les briquettes choisies en 3D.',
    'products.3d.uploadTeaser': 'Téléchargez des photos de votre maison',
    'products.allColors': 'Toutes les Couleurs',
    'products.allTextures': 'Toutes les Textures',
    'products.calculate': 'Calculer',
    'products.clearFilters': 'Effacer les filtres',
    'products.collection': 'Collection',
    'products.desc': 'Découvrez notre gamme de plaquettes premium. Filtrez par couleur et texture pour trouver la pierre parfaite pour votre projet.',
    'products.found': 'trouvé(s)',
    'products.noResults': 'Aucun produit trouvé',
    'products.noResultsDesc': 'Ajustez vos filtres pour trouver des produits.',
    'products.product': 'produit',
    'products.products': 'produits',
    'products.quote': 'Devis',
    'products.search': 'Rechercher plaquettes...',
    'products.tapToPreview': 'Cliquez sur le numéro de la brique choisie',
    'products.title': 'Nos Plaquettes de Parement',
    'data.product.p1.desc': 'Plaquette allongée épurée en taupe chaud. Parfaite pour un bardage moderne au rendu doux et neutre.',
    'data.product.p2.desc': 'Plaquette allongée gris foncé aux subtiles variations de texture. Un look contemporain et affirmé.',
    'data.product.p3.desc': 'Plaquette allongée blanche à la surface légèrement structurée. Intemporelle et élégante.',
    'data.product.p4.desc': 'Plaquette allongée gris clair. Subtile et raffinée pour les projets architecturaux épurés.',
    'data.product.p5.desc': 'Plaquette allongée gris-bleu aux tons froids. Un accent de couleur unique pour les façades modernes.',
    'data.product.p6.desc': 'Plaquette allongée noir profond pour une façade puissante et exclusive. Contraste maximal.',
    'data.product.p7.desc': 'Plaquette champêtre couleur ivoire aux nuances grises et à la surface brute. Authentique et chaleureuse.',
    'data.product.p8.desc': 'Plaquette de brique blanc éclatant au caractère brut. Inspiration scandinave, sobriété intemporelle.',
    'data.product.p9.desc': 'Plaquette allongée terracotta chaude à la surface rustique. Caractère méditerranéen.',
    'data.product.p10.desc': 'Plaquette de brique multicolore aux empreintes authentiques. Caractère recyclé, chaque pièce est unique.',
    'data.product.p11.desc': "Plaquette de brique rose-rouge douce à l'aspect patiné. Maçonnerie belge classique.",
    'data.product.p12.desc': 'Plaquette allongée anthracite-noir foncé. Industrielle et puissante.',
    'data.product.p13.desc': 'Plaquette de brique terracotta chaude aux douces variations de couleur. Charme méditerranéen.',
    'data.product.p14.desc': 'Plaquette allongée blanche à la texture brute. Style scandinave, moderne et lumineux.',
    'data.product.p15.desc': 'Plaquette de brique panachée de Campine aux nuances rouge-brun. Authentiquement flamande.',
    'data.product.p16.desc': 'Plaquette de brique blanche douce au caractère champêtre. Parfaite pour les maisons de style cottage.',
    'data.product.p17.desc': 'Plaquette allongée gris-brun aux nuances de couleur subtiles. Urbaine et affirmée.',
    'data.product.p18.desc': 'Plaquette allongée brun rouille chaleureuse. Couleur riche aux variations naturelles.',
    'data.product.p19.desc': 'Plaquette de brique gris foncé au caractère profond. Forte et intemporelle.',
    'data.product.p20.desc': "Plaquette allongée brun chaud aux nuances grises. L'élégance brugeoise.",
    'data.product.p21.desc': 'Plaquette de brique crème douce à la chaleur provençale. Légère et élégante.',
    'data.product.p22.desc': 'Gris doux aux variations subtiles. Un rendu moderne avec une touche de caractère.',
    'data.product.p23.desc': 'Plaquette de brique rouge classique. Le choix intemporel pour les façades belges traditionnelles.',
    'data.product.p24.desc': 'Plaquette de brique noir profond avec joint blanc. Contraste dramatique, design exclusif.',
    'data.product.p25.desc': 'Plaquette allongée couleur sable aux sous-tons jaunes chauds. Légère et accueillante.',
    'data.product.p26.desc': 'Plaquette de brique terracotta claire. Douces teintes rose-rouge au caractère toscan.',
    'data.product.p27.desc': 'Plaquette de brique panachée aux mélanges rouge-brun et accents foncés. Typiquement flamande.',
    'v6.toevoegen': '＋ Ajouter',
    'v6.sluit': 'Fermer',
    'v6.nieuweSteen': '＋ Nouvelle brique',
    'v6.3d': '🏠 3D',
    'v6.allesWissen': '🗑 Tout effacer',
    'v6.allesWissenBevestig': 'Effacer toutes les briques choisies et les réglages, et recommencer ?',
    'v6.allesGewist': '🗑 Sélection effacée — choisissez à nouveau',
    'v6.reedsInOfferte': 'Déjà dans ce devis',
    'v6.staatOpen': '● ouverte ci-dessus — modifiez librement',
    'v6.instellingenPrijs': 'réglages & prix ▸',
    'v6.offerteAanvragen': 'Demander un devis',
    'v6.offerteMetBerekening': 'Demander un devis avec ce calcul',
    'v6.totaal': 'Total',
    'v6.steen': 'brique',
    'v6.inDezeOfferte': 'dans ce devis',
    'v6.tipHoeken': 'Angles extérieurs de votre façade — généralement 4.',
    'v6.tipHoogte': 'Hauteur d\'un angle en mètres — p.ex. 2,5 m par étage. Angles × hauteur = pièces d\'angle.',
    'v6.tipSnijverlies': 'Pourcentage fixe — inclus automatiquement dans le prix.',
    'v6.termijnVraag': 'Quand prévoyez-vous ce projet ?',
    'v6.t3m': '≤ 3 mois',
    'v6.t6m': '≤ 6 mois',
    'v6.t1j': 'Cette année',
    'v6.tLater': 'Plus tard',
    'v6.tInfo': 'À titre info',
    'v6.termijnFout': 'Indiquez le délai prévu — ou choisissez « À titre info ».',
    'v6.plaatsingKop': '🔨 Pose',
    'v6.plaatsingVraag': 'Souhaitez-vous un installateur professionnel, ou posez-vous vous-même ?',
    'v6.plOptInstall': 'Je souhaite qu\'un installateur pose pour moi',
    'v6.plOptInstallSub': 'Nous vous mettons en contact avec un professionnel de votre région',
    'v6.plOptZelf': 'Je pose moi-même',
    'v6.plOptZelfSub': 'Pas d\'installateur — matériaux uniquement',
    'v6.plOptGh': 'Via mon grossiste',
    'v6.plOptGhSub': 'Achat et pose via votre propre grossiste',
    'v6.instVulPostcode': 'Indiquez ci-dessus votre code postal + commune — nous affichons les installateurs de votre région (±50 km).',
    'v6.instZoeken': 'Recherche des installateurs de votre région…',
    'v6.instLijstIntro': 'Installateurs de votre région — cochez qui peut recevoir une copie de votre devis (sans engagement, vous négociez vous-même) :',
    'v6.instGeen': 'Aucun installateur dans votre région pour le moment — contactez le fabricant : nous vous aidons personnellement après votre demande.',
    'v6.ghIntro': 'Votre grossiste (facultatif) — nous lui envoyons une copie de votre devis :',
    'v6.ghNaam': 'Nom du grossiste',
    'v6.ghGemeente': 'Commune',
    'v6.ghEmail': 'E-mail du grossiste (pour la copie du devis)',
    'v6.geldstroom': '💶 Avec un installateur ou grossiste, vous réglez directement avec eux — ils commandent les matériaux chez nous. Si vous posez vous-même, vous commandez et payez d\'avance chez nous.',
    'v6.uwGegevens': 'Vos coordonnées',
    'v6.bevestigGegevens': 'Confirmez vos coordonnées',
    'v6.sluiten': 'Fermer',
    'v6.uspFabrikantKop': 'Direct du fabricant',
    'v6.uspFabrikant': 'Sans intermédiaires — plaquettes de brique belges premium jusqu\'à 40% moins cher, à prix catalogue fixes.',
    'v6.uspPlaatsingKop': 'Pose organisée',
    'v6.uspPlaatsing': 'Posez vous-même, via votre grossiste, ou choisissez un installateur de votre région (±50 km) — vous négociez directement.',
    'v6.uspLeveringKop': 'Livraison rapide',
    'v6.uspLevering': 'Délai ±8 semaines, emballé soigneusement jusqu\'au chantier. Devis valable 30 jours — colle et joint inclus.',
    'v6.inspiratieKop': 'Inspiration & réalisations',
    'v6.inspiratieSub': 'Découvrez des façades réalisées avec nos briques — choisissez en confiance.',
    'v6.kortingCta': 'Négociant, entreprise de construction ou installateur ?',
    'v6.kortingLink': 'Demandez votre remise →',
    'pdf.cfg.stone': 'Brique',
    'pdf.cfg.stones': 'Briques',
    'pdf.cfg.corners': 'Angles',
    'pdf.cfg.delivery': 'Délai de livraison',
    'pdf.cfg.groutColor': 'Couleur du joint',
    'pdf.cfg.color': 'Couleur / texture',
    'calc.corners': 'Pierres d\'Angle',
    'calc.cornerCount': 'Nombre d\'angles',
    'calc.cornerHeight': 'Hauteur par angle (m)',
    'calc.accessories': 'Accessoires',
    'calc.includeGlue': 'Inclure la colle',
    'calc.glueHint': '(~5 kg/m²)',
    'calc.includeGrout': 'Inclure le mortier',
    'calc.groutHint': '(selon largeur de joint)',
    'calc.groutColor': 'Couleur du mortier',
    'calc.grey': 'gris',
    'calc.white': 'blanc',
    'calc.anthracite': 'anthracite',
    'calc.sand': 'sable',
    'calc.cuttingLoss': 'Perte de Coupe',
    'calc.cuttingLossHint': 'Standard 10%. Augmentez pour les façades complexes avec beaucoup de découpe.',
    'calc.yourCalc': 'Votre Calcul',
    'calc.brickStrips': 'Plaquettes de parement',
    'calc.pieces': 'pièces',
    'calc.cornerStones': 'Pierres d\'angle',
    'calc.glue': 'Colle',
    'calc.bags': 'sacs de 25kg',
    'calc.grout': 'Mortier',
    'calc.totalExcl': 'Total (hors TVA)',
    'calc.inclVat': 'Incl. 21% TVA',
    'calc.quoteWithCalc': 'Demander un devis avec ce calcul',
    'calc.print': 'Imprimer le Calcul',
    'calc.saveQuote': 'Enregistrer le Devis',
    'calc.savedSuccess': 'Devis enregistré ! Consultez votre tableau de bord.',
    'calc.disclaimer': '* Prix indicatifs. Prix exact dans votre devis personnalisé.',
    'calc.swipeHint': 'Glissez pour voir plus de briques',

    // INSPIRATION
    'insp.realisations': 'Réalisations',
    'insp.title': 'Galerie d\'Inspiration',
    'insp.desc': 'Découvrez des projets réalisés avec nos plaquettes. Filtrez par style architectural pour trouver l\'inspiration pour votre projet.',
    'insp.allStyles': 'Tous les styles',
    'insp.style.modern': 'Moderne',
    'insp.style.landelijk': 'Champêtre',
    'insp.style.industrieel': 'Industriel',
    'insp.style.klassiek': 'Classique',
    'insp.architect': 'Architecte',
    'insp.noResults': 'Aucun projet trouvé',
    'insp.noResultsDesc': 'Sélectionnez un autre style pour voir les projets.',

    // QUOTE
    'quote.title': 'Demander un Devis',
    'quote.desc': 'Demandez un devis gratuit. Nous vous connectons avec un distributeur local dans votre région pour le meilleur service et prix.',
    'quote.iAm': 'Je suis un...',
    'quote.private': 'Particulier',
    'quote.contractor': 'Entrepreneur',
    'quote.architect': 'Architecte',
    'quote.wholesaler': 'Grossiste',
    'quote.contactInfo': 'Coordonnées',
    'quote.name': 'Nom',
    'quote.email': 'E-mail',
    'quote.phone': 'Téléphone',
    'quote.postcode': 'Code postal',
    'quote.postcodeHint': 'ex. 1000',
    'quote.company': 'Nom de l\'entreprise',
    'quote.projectDetails': 'Détails du Projet',
    'quote.product': 'Produit',
    'quote.selectProduct': 'Sélectionner un produit...',
    'quote.estimatedArea': 'Surface estimée (m²)',
    'quote.areaHint': 'ex. 80',
    'quote.additionalInfo': 'Informations complémentaires',
    'quote.additionalInfoHint': 'Décrivez votre projet, souhaits spécifiques, date de livraison souhaitée...',
    'quote.submit': 'Demander un Devis →',
    'quote.submitNote': 'Nous traitons votre demande dans les 24 heures. Vos données sont traitées de manière confidentielle.',
    'quote.successTitle': 'Devis Demandé !',
    'quote.successDesc': 'Merci pour votre demande. Nous vous contacterons dans les 24 heures avec un devis personnalisé. Un distributeur local dans votre région vous sera attribué.',
    'quote.newRequest': 'Nouvelle Demande',

    // ARCHITECT PAGE
    'arch.professionals': 'Professionnels',
    'arch.title': 'Architectes & Prescripteurs',
    'arch.desc': 'Téléchargez fiches techniques, déclarations de performance et fichiers BIM. Toute la documentation nécessaire pour votre cahier des charges.',
    'arch.generalDocs': 'Documentation Générale',
    'arch.perProduct': 'Documentation par Produit',
    'arch.techSpecs': 'Spécifications Techniques',
    'arch.doc1': 'Fiche Technique — Collection Complète',
    'arch.doc2': 'Déclaration de Performance (DoP) — Marquage CE',
    'arch.doc3': 'Objets BIM — Compatible Revit',
    'arch.doc4': 'Guide d\'Installation',
    'arch.doc5': 'Rapport de Résistance au Feu — Classe A1',
    'arch.doc6': 'Test de Résistance au Gel — Classe F2',
    'arch.techFiche': 'Fiche Technique',
    'arch.dop': 'Déclaration de Performance',
    'arch.bim': 'Objet BIM',
    'arch.spec.dimensions': 'Dimensions',
    'arch.spec.performance': 'Performances',
    'arch.spec.installation': 'Installation',
    'arch.spec.warranty': 'Garantie',
    'arch.spec.length': 'Longueur',
    'arch.spec.height': 'Hauteur',
    'arch.spec.thickness': 'Épaisseur',
    'arch.spec.weight': 'Poids',
    'arch.spec.fireClass': 'Classe de feu',
    'arch.spec.fireValue': 'A1 (incombustible)',
    'arch.spec.frost': 'Résistance au gel',
    'arch.spec.frostValue': 'Classe F2',
    'arch.spec.water': 'Absorption d\'eau',
    'arch.spec.waterValue': '< 6%',
    'arch.spec.ce': 'Marquage CE',
    'arch.spec.ceValue': 'EN 14411',
    'arch.spec.glue': 'Colle',
    'arch.spec.glueValue': '5 kg/m² (colle flexible)',
    'arch.spec.grout': 'Mortier',
    'arch.spec.groutValue': '1.5 kg/m² (joint de 10mm)',
    'arch.spec.loss': 'Perte de coupe',
    'arch.spec.lossValue': '10% standard',
    'arch.spec.dryTime': 'Temps de séchage',
    'arch.spec.dryValue': '24-48 heures',
    'arch.spec.productWarranty': 'Garantie produit',
    'arch.spec.productWarrantyVal': '10 ans',
    'arch.spec.colorFast': 'Solidité des couleurs',
    'arch.spec.colorFastVal': 'Résistant aux UV',
    'arch.spec.lifespan': 'Durée de vie',
    'arch.spec.lifespanVal': '> 50 ans',
    'arch.spec.maintenance': 'Entretien',
    'arch.spec.maintenanceVal': 'Sans entretien',
    'arch.helpTitle': 'Besoin d\'aide pour votre cahier des charges ?',
    'arch.helpDesc': 'Notre équipe technique vous aide à élaborer les spécifications adaptées à votre projet.',
    'arch.contactUs': 'Nous Contacter',

    // CUSTOM PAGE
    'custom.exclusive': 'Exclusif',
    'custom.title': 'Pierres de Façade Sur Mesure',
    'custom.desc': 'Rendez votre façade unique avec des plaquettes sur mesure. Choisissez votre couleur, texture et finition — produites par notre fabricant polonais.',
    'custom.color.title': 'Couleur Sur Mesure',
    'custom.color.desc': 'Choisissez parmi une palette illimitée. Nous reproduisons chaque couleur RAL ou brique existante.',
    'custom.texture.title': 'Texture au Choix',
    'custom.texture.desc': 'D\'ultra-lisse à très rugueux, fait main ou industriel — chaque texture est possible.',
    'custom.size.title': 'Dimensions Personnalisées',
    'custom.size.desc': 'Autres formats, bandes plus fines ou plus épaisses — tout est négociable pour votre projet.',
    'custom.howTitle': 'Comment ça marche ?',
    'custom.step1.title': 'Briefing',
    'custom.step1.desc': 'Partagez vos souhaits : couleur, texture, format et quantité.',
    'custom.step2.title': 'Échantillon',
    'custom.step2.desc': 'Nous produisons un échantillon sur mesure pour votre approbation.',
    'custom.step3.title': 'Production',
    'custom.step3.desc': 'Après approbation, nous lançons la production. Délai : 6-8 semaines.',
    'custom.step4.title': 'Livraison',
    'custom.step4.desc': 'Vos pierres sur mesure sont livrées par un distributeur local.',
    'custom.interestTitle': 'Intéressé par les pierres sur mesure ?',
    'custom.interestDesc': 'Contactez-nous pour un entretien sans engagement. Quantité minimum : 100 m².',
    'custom.contact': 'Nous Contacter',

    // LOGIN
    'login.title': 'Connexion',
    'login.subtitle': 'Accès à votre tableau de bord B2B',
    'login.email': 'Adresse e-mail',
    'login.emailPlaceholder': 'votre@email.be',
    'login.password': 'Mot de passe',
    'login.submit': 'Se Connecter',
    'login.error': 'Identifiants invalides. Veuillez réessayer.',
    'login.emailExists': 'Cette adresse e-mail est déjà enregistrée.',
    'login.invalidInput': 'Veuillez remplir tous les champs correctement (mot de passe min. 4 caractères).',
    'login.registerTitle': 'Créer un Compte',
    'login.registerSubtitle': 'Créez un compte client gratuit',
    'login.name': 'Nom',
    'login.namePlaceholder': 'Votre nom complet',
    'login.passwordHint': 'Min. 4 caractères',
    'login.registerSubmit': "S'inscrire",
    'login.hasAccount': 'Déjà un compte ? Se connecter',
    'login.noAccount': "Pas encore de compte ? Inscrivez-vous gratuitement",
    'login.demoTitle': 'Comptes démo :',

    // DASHBOARD
    'dash.admin': '👑 Administrateur',
    'dash.wholesaler': '🏭 Grossiste',
    'dash.customer': '👤 Client',
    'dash.overview': '📊 Aperçu',
    'dash.productMgmt': '📦 Gestion Produits',
    'dash.customers': '👥 Clients',
    'dash.orders': '📋 Commandes',
    'dash.suppliers': '🚚 Fournisseurs',
    'dash.analytics': '📈 Analytique',
    'dash.myPrices': '💰 Mes Prix',
    'dash.myQuotes': '📄 Mes Devis',
    'dash.totalQuotes': 'Total Devis',
    'dash.activeWholesalers': 'Grossistes Actifs',
    'dash.revenueMonth': 'Revenu ce mois',
    'dash.conversionRate': 'Taux de conversion',
    'dash.myOrders': 'Mes Commandes',
    'dash.openQuotes': 'Devis Ouverts',
    'dash.myDiscount': 'Ma Remise',
    'dash.totalOrdered': 'Total Commandé',
    'dash.status': 'Statut',
    'dash.inProgress': 'En cours',
    'dash.calculations': 'Calculs',
    'dash.favorites': 'Favoris',
    'dash.recentQuotes': 'Demandes de Devis Récentes',
    'dash.client': 'Client',
    'dash.type': 'Type',
    'dash.product': 'Produit',
    'dash.new': 'Nouveau',
    'dash.quoteSent': 'Devis envoyé',
    'dash.newProduct': '+ Nouveau Produit',
    'dash.edit': 'Modifier',
    'dash.delete': 'Supprimer',
    'dash.supplierMgmt': 'Gestion Fournisseurs',
    'dash.active': 'Actif',
    'dash.inTalks': 'En discussion',
    'dash.planned': 'Planifié',
    'dash.exclusiveContract': 'Contrat exclusif',
    'dash.potential': 'Potentiel',
    'dash.future': 'Futur',
    'dash.yourPrices': 'Vos Prix Grossiste',
    'dash.comingSoon': 'Bientôt disponible',
    'dash.comingSoonDesc': 'Ce module est en cours de développement et sera bientôt disponible.',
    'dash.newQuote': 'Nouveau Calcul',
    'dash.noQuotes': 'Aucun devis enregistré',
    'dash.noQuotesDesc': 'Faites un calcul dans le calculateur et enregistrez-le comme devis.',
    'dash.goToCalc': 'Aller au Calculateur',
    'dash.downloadPdf': 'Télécharger PDF',
    'dash.corners': 'pierres d\'angle',
    'dash.glueBags': 'sacs de colle',
    'dash.groutBags': 'sacs de mortier',
    'dash.searchCustomer': 'Rechercher client...',
    'dash.phone': 'Téléphone',
    'dash.address': 'Adresse',
    'dash.editProduct': 'Modifier le produit',
    'dash.addProduct': 'Ajouter un produit',
    'dash.productName': 'Nom du produit',
    'dash.serialNumber': 'Numéro de série',
    'dash.supplierCode': 'Code fournisseur',
    'dash.supplier': 'Fournisseur',
    'dash.color': 'Couleur',
    'dash.texture': 'Texture',
    'dash.category': 'Catégorie',
    'dash.pricing': 'Prix',
    'dash.retailPrice': 'Prix de vente/m²',
    'dash.dimensions': 'Dimensions',
    'dash.weight': 'Poids',
    'dash.stock': 'Stock',
    'dash.description': 'Description',
    'dash.descriptionPlaceholder': 'Décrivez le produit...',
    'dash.cancel': 'Annuler',
    'dash.saveChanges': 'Enregistrer les modifications',
    'dash.importProducts': 'Importer',
    'dash.dragDrop': 'Glissez un fichier ici ou cliquez pour télécharger',
    'dash.supportedFormats': 'Formats supportés: Excel (.xlsx, .xls, .csv) ou PDF',
    'dash.chooseFile': 'Choisir un fichier',
    'dash.templateInfo': 'Template',
    'dash.templateDesc': 'Utilisez les colonnes: Nom, N° de série, Couleur, Texture, Prix/m², B2B Tier 1-3, Dimensions, Poids',

    // DATA - Colors
    'data.color.Rood': 'Rouge',
    'data.color.Wit': 'Blanc',
    'data.color.Bruin': 'Brun',
    'data.color.Zwart': 'Noir',
    'data.color.Geel': 'Jaune',
    'data.color.Grijs': 'Gris',
    'data.color.Taupe': 'Taupe',
    'data.color.Creme': 'Cr\u00e8me',
    'data.color.Bont': 'Multicolore',

    // DATA - Textures
    'data.texture.Ruw': 'Rugueux',
    'data.texture.Glad': 'Lisse',
    'data.texture.Gesinterd': 'Fritté',
    'data.texture.Gewassen': 'Lavé',

    // DATA - Suppliers
    'data.supplier.polen': 'Fabricant Pologne',
    'data.supplier.spanje': 'Fabricant Espagne',
    'data.supplier.italie': 'Fabricant Italie',

    // DATA - Product Descriptions

    // DATA - Inspiration Projects
    'data.insp.i1.title': 'Villa Kalmthout',
    'data.insp.i1.desc': 'Villa moderne épurée avec plaquettes blanches et noires dans un environnement boisé.',
    'data.insp.i2.title': 'Restauration Ferme Limbourg',
    'data.insp.i2.desc': 'Rénovation authentique d\'une ferme limbourgeoise avec plaquettes rouges et brunes.',
    'data.insp.i3.title': 'Loft Centre de Gand',
    'data.insp.i3.desc': 'Projet loft industriel au cœur de Gand avec des accents gris et noirs.',
    'data.insp.i4.title': 'Villa à Bruges',
    'data.insp.i4.desc': 'Villa classique près de Bruges avec plaquettes brunes et jaune doré.',

    // DATA - Dashboard extra
    'data.type.particulier': 'Particulier',
    'data.type.groothandel': 'Grossiste',
    'data.type.architect': 'Architecte',
    'data.type.aannemer': 'Entrepreneur',
    'data.tier': 'Niveau',

    // DATA - Login demo
    'login.demoAdmin': 'Admin',
    'login.demoWholesaler': 'Grossiste',
    'login.demoCustomer': 'Client',

    // DATA - Footer
    'footer.location': 'Bredabaan 123, 2970 Schilde, Belgique',
    'footer.btw': 'TVA BE0123.456.789',

    // DATA - Alt text
    'data.alt.customStones': 'Pierres sur mesure',

    // SEO - Page Titles
    'seo.home.title': 'Gevelsteenbedrijf.be — Plaquettes de Parement Premium | Direct d\'Usine',
    'seo.products.title': 'Collection de Plaquettes — Toutes Couleurs & Textures | Gevelsteenbedrijf.be',
    'seo.calculator.title': 'Calculateur de Matériaux — Calculez votre Projet | Gevelsteenbedrijf.be',
    'seo.inspiration.title': 'Galerie d\'Inspiration — Projets Réalisés | Gevelsteenbedrijf.be',
    'seo.quote.title': 'Demander un Devis — Gratuit & Rapide | Gevelsteenbedrijf.be',
    'seo.architects.title': 'Architectes & Prescripteurs — Fiches Techniques | Gevelsteenbedrijf.be',
    'seo.custom.title': 'Pierres Sur Mesure — Façade Unique | Gevelsteenbedrijf.be',
    'seo.login.title': 'Connexion — Portail B2B | Gevelsteenbedrijf.be',
    'seo.dashboard.title': 'Tableau de Bord — Gevelsteenbedrijf.be',

    // SEO - Page Descriptions
    'seo.home.description': 'Plaquettes de parement premium directement de l\'usine. Économisez jusqu\'à 40% sur les plaquettes de façade et pierres d\'angle. Calcul et devis gratuits. Entreprise belge avec distributeurs locaux.',
    'seo.products.description': 'Découvrez notre gamme de plaquettes de parement premium. Filtrez par couleur et texture. Plaquettes rouges, blanches, brunes, noires, jaunes et grises. Direct d\'usine au meilleur prix.',
    'seo.calculator.description': 'Calculez gratuitement combien de plaquettes, colle, mortier et pierres d\'angle vous avez besoin. Calcul des pertes de coupe inclus. Recevez une estimation de prix immédiate.',
    'seo.inspiration.description': 'Découvrez des projets réalisés avec nos plaquettes de parement. Façades modernes, champêtres, industrielles et classiques. Trouvez l\'inspiration pour votre projet.',
    'seo.quote.description': 'Demandez un devis gratuit pour plaquettes de parement. Réponse rapide sous 24 heures. Service personnalisé via un distributeur local dans votre région.',
    'seo.architects.description': 'Téléchargez fiches techniques, déclarations de performance (DoP), fichiers BIM et guides d\'installation pour plaquettes de parement. Documentation complète pour votre cahier des charges.',
    'seo.custom.description': 'Faites produire des plaquettes de parement sur mesure. Choisissez couleur, texture et dimensions. Quantité minimum 100 m². Délai de livraison 6-8 semaines.',
    'seo.login.description': 'Connectez-vous au portail B2B de Gevelsteenbedrijf.be. Accès aux prix grossiste, commandes et devis.',
    'seo.dashboard.description': 'Gérez vos commandes, devis et données clients via le tableau de bord Gevelsteenbedrijf.be.',

    // SEO - Keywords
    'seo.home.keywords': 'plaquettes de parement, briques de façade, pierres de façade, revêtement de façade, plaquettes premium, Belgique, direct d\'usine, construction, rénovation, maçonnerie, jointoiement, nouvelle construction',
    'seo.products.keywords': 'collection plaquettes, plaquettes rouges, plaquettes blanches, plaquettes brunes, plaquettes noires, maçonnerie, acheter plaquettes',
    'seo.calculator.keywords': 'calculateur plaquettes, calcul matériaux, combien de plaquettes, calcul colle, perte de coupe',
    'seo.inspiration.keywords': 'inspiration plaquettes, rénovation façade, façade moderne, façade champêtre, industriel, classique',
    'seo.quote.keywords': 'devis plaquettes, demande de prix, devis gratuit, commander plaquettes',
    'seo.architects.keywords': 'fiches techniques plaquettes, fichiers BIM, déclaration de performance, marquage CE, documentation architectes',
    'seo.custom.keywords': 'plaquettes sur mesure, couleur personnalisée, texture sur mesure, fabrication sur mesure',
    'seo.login.keywords': 'login B2B, portail grossiste, connexion gevelsteenbedrijf',
    'seo.dashboard.keywords': 'tableau de bord, commandes, devis, gestion clients',
    'seo.terms.title': 'Conditions Générales | Gevelsteenbedrijf.be',
    'seo.terms.description': 'Lisez nos conditions générales de vente. Informations sur les prix, la livraison, le droit de rétractation, la garantie et plus. Gevelsteenbedrijf.be — Entreprise belge.',
    'seo.terms.keywords': 'conditions générales, conditions de vente, garantie plaquettes, droit de rétractation',
    'seo.cookiePolicy.title': 'Politique de Cookies & Confidentialité | Gevelsteenbedrijf.be',
    'seo.cookiePolicy.description': 'Notre politique de cookies et déclaration de confidentialité. Informations sur les cookies, le traitement des données et vos droits conformément au RGPD. Gevelsteenbedrijf.be.',
    'seo.cookiePolicy.keywords': 'politique cookies, confidentialité, RGPD, protection données, cookies',

    // COOKIE BANNER
    'cookie.title': 'Nous respectons votre vie privée',
    'cookie.description': 'Nous utilisons des cookies pour améliorer votre expérience. Les cookies essentiels sont nécessaires au fonctionnement du site. Les cookies analytiques et marketing ne sont placés qu\'avec votre consentement, conformément à la législation belge et au RGPD.',
    'cookie.acceptAll': 'Accepter tous les cookies',
    'cookie.essentialOnly': 'Essentiels uniquement',
    'cookie.showDetails': 'Plus de détails',
    'cookie.hideDetails': 'Masquer les détails',
    'cookie.essential.title': 'Cookies essentiels',
    'cookie.essential.desc': 'Nécessaires au fonctionnement du site : préférence linguistique, choix cookies, session login.',
    'cookie.analytics.title': 'Cookies analytiques',
    'cookie.analytics.desc': 'Nous aident à comprendre comment les visiteurs utilisent le site. Données anonymisées.',
    'cookie.marketing.title': 'Cookies marketing',
    'cookie.marketing.desc': 'Utilisés pour afficher des publicités pertinentes basées sur vos intérêts.',
    'cookie.legalNote': 'Plus d\'informations dans notre',
    'cookie.policyLink': 'politique de cookies',

    // ADMIN DASHBOARD
    'admin.accessDenied': 'Accès Refusé',
    'admin.accessDeniedDesc': 'Connectez-vous en tant que propriétaire ou superadmin pour accéder au tableau de bord.',
    'admin.dashboard': 'Tableau de bord',
    'admin.demoMode': 'Mode démo — seuls les produits sont visibles',
    'admin.ownerDashboard': 'Tableau de bord Propriétaire',
    'admin.loggedInAs': 'Connecté en tant que',
    'admin.refresh': 'Actualiser',
    'admin.loading': 'Chargement des données...',
    'admin.apiFooter': 'Toutes les données via des endpoints admin sécurisés.',

    // Admin tabs
    'admin.tab.producten': 'Produits',
    'admin.tab.leveranciers': 'Fournisseurs',
    'admin.tab.klanten': 'Clients & Devis',
    'admin.tab.klantenOffertes': 'Clients & Devis',
    'admin.tab.testOffertes': 'Devis Test',
    'admin.tab.prijzen': 'Remises Revendeurs',
    'admin.tab.berichten': 'Messages',

    // Admin stat labels
    'admin.stat.producten': 'Produits · Prix',
    'admin.stat.leveranciers': 'Fournisseurs',
    'admin.stat.resellers': 'Revendeurs',
    'admin.stat.resellersSub': 'Distributeurs',
    'admin.stat.prijzenSub': 'Prix par revendeur',
    'admin.stat.klantenOffertes': 'Clients & Devis',
    'admin.stat.klanten': 'Clients',
    'admin.stat.offertes': 'devis',
    'admin.stat.testOffertes': 'Devis Test',
    'admin.stat.offertesCount': 'Devis',
    'admin.stat.internTesten': 'Tests internes',
    'admin.stat.prijzen': 'Remises Revendeurs',
    'admin.stat.prijsbeheer': 'Gestion des prix',

    // Admin StatusBadge
    'admin.status.draft': 'Brouillon',
    'admin.status.pending': 'En cours',
    'admin.status.sent': 'Envoyé',
    'admin.status.accepted': 'Approuvé',
    'admin.status.approved': 'Approuvé',
    'admin.status.rejected': 'Refusé',
    'admin.status.expired': 'Expiré',
    'admin.status.new': 'Nouveau',

    // Admin Mijn Profiel
    'admin.profile': 'Mon Profil',
    'admin.profile.edit': 'Modifier',
    'admin.profile.save': 'Enregistrer',
    'admin.profile.saving': 'Enregistrement...',
    'admin.profile.cancel': 'Annuler',
    'admin.profile.success': 'Profil mis à jour avec succès',
    'admin.profile.name': 'Nom',
    'admin.profile.email': 'E-mail',
    'admin.profile.phone': 'Téléphone',
    'admin.profile.company': 'Nom de l\'entreprise',
    'admin.profile.companyShort': 'Entreprise',
    'admin.profile.btw': 'Numéro de TVA',
    'admin.profile.btwShort': 'TVA',
    'admin.profile.address': 'Adresse',
    'admin.profile.city': 'Ville',
    'admin.profile.postalCode': 'Code postal',

    // Admin Producten tab
    'admin.products.title': 'Produits · Modifier prix',
    'admin.products.editProduct': 'Modifier le produit',
    'admin.products.name': 'Nom',
    'admin.products.category': 'Catégorie',
    'admin.products.dimensions': 'Dimensions',
    'admin.products.serialNumber': 'Numéro de série',
    'admin.products.ean': 'Code EAN',
    'admin.products.fixedPrice': 'Prix fixe',
    'admin.products.techSpecs': 'Spéc. techniques',
    'admin.products.save': 'Enregistrer',
    'admin.products.saving': 'Enregistrement...',
    'admin.products.cancel': 'Annuler',
    'admin.products.edit': 'Modifier',
    'admin.products.color': 'Couleur',
    'admin.products.texture': 'Texture',
    'admin.products.weight': 'Poids',
    'admin.products.supplier': 'Fournisseur',
    'admin.products.purchase': 'Achat',
    'admin.products.exclVat': 'Hors TVA',
    'admin.products.incl21': 'Incl. 21%',
    'admin.products.incl6': 'Incl. 6%',
    'admin.products.actions': 'Actions',
    'admin.products.showAll': 'Tout afficher',
    'admin.products.bestsellers': 'Meilleures ventes',
    'admin.products.bestsellersOn': 'Cliquer pour désactiver le filtre des best-sellers',
    'admin.products.bestsellersOff': 'N\'afficher que les best-sellers',
    'admin.products.priceM2Badge': 'Prix / m²',
    'admin.products.priceM2Title': 'Tous les prix de ce tableau sont par mètre carré (m²)',
    'admin.products.akpLev': 'PA Reven.',
    'admin.products.akpLevTooltip': 'Prix d\'achat revendeur — ce que le revendeur paie à Filip (HT)',
    'admin.products.akpLevHint': 'Prix d\'achat revendeur — base pour la remise revendeur dans l\'onglet Remises',
    'admin.products.akpDiscount': 'Remise Reven.→Client',
    'admin.products.akpDiscountTooltip': 'Remise que le revendeur transmet au client final (visible en ligne)',
    'admin.products.netAkp': 'Net client final HT',
    'admin.products.netAkpTooltip': 'Prix net client final hors TVA — ce que le client paie sur le site avant TVA',
    'admin.products.purchaseTooltip': 'PA Net — ce qui est payé au fournisseur (cliquer sur ✎ pour saisir PA Four. + remise)',
    'admin.products.margeTooltip': 'Marge sur le prix de vente — (vente − achat) / vente × 100',
    'admin.products.exclVatTooltip': 'Prix de vente hors TVA — ce que le client paie avant TVA',
    'admin.products.dealConsistent': 'Formule de vente correcte — cliquez Enregistrer pour valider',
    'admin.products.dealInconsistent': 'Net client final diffère de PA Reven × (1 − remise). Remplir à nouveau.',
    'admin.products.purchaseSubtitle': 'PA {akp} · −{disc}%',
    'admin.products.akpBlockTitle': '📦 Prix de vente / m²',

    // PRODUCT DISCOUNT MODAL (FR)
    'admin.products.discountFor':         'Remise pour',
    'admin.products.discountHelp':        'Configurez une promo temporaire ou une action de liquidation pour ce produit. Visible sur la fiche produit du site.',
    'admin.products.discountShow':        'Afficher remise standard',
    'admin.products.discountShowHelp':    'Affiche une pastille rouge sur la fiche produit. Ex. "Réservation anticipée" ou "Promo".',
    'admin.products.discountPct':         'Remise %',
    'admin.products.discountMax':         'max 50%',
    'admin.products.discountMax50':       'La remise ne peut dépasser 50%.',
    'admin.products.discountLabel':       'Étiquette',
    'admin.products.discountLabelPh':     'p.ex. Promo, Réservation anticipée',
    'admin.products.discountSave':        'Enregistrer',
    'admin.products.discountDateInvalid': 'La date de fin doit être postérieure à la date de début.',
    'admin.products.liquidationToggle':   'Liquidation / fin de saison',
    'admin.products.liquidationHelp':     'Pastille ambre temporaire avec date de début et de fin. Remplace la remise standard pendant la fenêtre.',
    'admin.products.liquidationPct':      'Liquidation %',
    'admin.products.liquidationLabel':    'Étiquette',
    'admin.products.liquidationLabelPh':  'p.ex. Liquidation, Fin de saison',
    'admin.products.liquidationStart':    'Date de début',
    'admin.products.liquidationEnd':      'Date de fin',
    'admin.products.preset1month':        'Mois prochain',
    'admin.products.preset1week':         'Semaine prochaine',
    'admin.products.preview':             'Aperçu',
    'admin.products.catalogPrice':        'Prix catalogue',
    'admin.products.afterDiscount':       'Après remise',
    'admin.products.akpBlockHint': '(optionnel — peut aussi se faire via la table Produits)',
    'admin.products.resetFilters': 'Réinit. filtres · tout afficher',
    'admin.products.resetFiltersFull': 'Réinit. filtres · afficher les {count} produits',
    'admin.products.resetFiltersTitle': 'Désactiver tous les filtres pour voir tous les produits',
    'admin.products.emptyFilterTitle': 'Aucun produit visible — un filtre est actif',
    'admin.products.emptyBoth': 'Filtre best-sellers + fournisseur {supplier} masquent les {count} produits.',
    'admin.products.emptyBestsellers': 'Le filtre best-sellers est actif, mais aucun produit n\'est marqué.',
    'admin.products.emptySupplier': 'Le fournisseur {supplier} n\'a aucun produit dans cette liste.',
    'admin.products.optional': 'Optionnel',
    'admin.products.specs': 'Spéc.',
    'admin.products.filterSupplier': 'Cliquez pour filtrer par ce fournisseur',

    // Admin Leveranciers tab
    'admin.suppliers.title': 'Fournisseurs',
    'admin.suppliers.editSupplier': 'Modifier le fournisseur',
    'admin.suppliers.newSupplier': 'Nouveau Fournisseur',
    'admin.suppliers.company': 'Nom de l\'entreprise',
    'admin.suppliers.nameShort': 'Nom (court)',
    'admin.suppliers.btw': 'Numéro de TVA',
    'admin.suppliers.address': 'Adresse',
    'admin.suppliers.city': 'Ville',
    'admin.suppliers.postalCode': 'Code postal',
    'admin.suppliers.contactPerson': 'Personne de contact',
    'admin.suppliers.email': 'E-mail',
    'admin.suppliers.phone': 'Téléphone',
    'admin.suppliers.website': 'Site web',
    'admin.suppliers.save': 'Enregistrer',
    'admin.suppliers.saving': 'Enregistrement...',
    'admin.suppliers.cancel': 'Annuler',
    'admin.suppliers.add': 'Ajouter',
    'admin.suppliers.new': 'Nouveau',
    'admin.suppliers.edit': 'Modifier',
    'admin.suppliers.delete': 'Supprimer',
    'admin.suppliers.noSuppliers': 'Aucun fournisseur trouvé.',
    'admin.suppliers.nameRequired': 'Le nom est obligatoire',
    'admin.suppliers.confirmDelete': 'Êtes-vous sûr de vouloir supprimer ce fournisseur ?',
    'admin.suppliers.products': 'Produits',
    'admin.suppliers.name': 'Nom',
    'admin.suppliers.actions': 'Actions',
    'admin.suppliers.prod': 'prod.',

    // Admin Klanten & Offertes tab
    'admin.customers.title': 'Clients & Devis',
    'admin.customers.clients': 'clients',
    'admin.customers.quotes': 'devis',
    'admin.customers.noCustomers': 'Aucun client trouvé.',
    'admin.customers.since': 'Depuis',
    'admin.customers.total': 'total',
    'admin.customers.off': 'dev.',
    'admin.customers.loadingQuotes': 'Chargement des devis...',
    'admin.customers.quotesTitle': 'Devis',
    'admin.customers.excl': 'excl.',
    'admin.customers.noQuotesForCustomer': 'Aucun devis pour ce client.',
    'admin.customers.auditNote': 'Les devis sont conservés de manière permanente et ne peuvent pas être supprimés. Tous les devis sont archivés de manière permanente et ne peuvent plus être supprimés après envoi.',

    // Admin Test Offertes tab
    'admin.testQuotes.title': 'Devis Test (interne)',
    'admin.testQuotes.desc': 'Ici vous pouvez consulter et gérer les devis test en tant que propriétaire. Simulez ce qu\'un client verrait.',
    'admin.testQuotes.goToQuotePage': 'page de devis',
    'admin.testQuotes.goToPrefix': 'Allez à la ',
    'admin.testQuotes.goToSuffix': ' pour créer un devis test en tant que client.',
    'admin.testQuotes.autoAppear': 'Les devis soumis là-bas apparaissent automatiquement ici.',
    'admin.testQuotes.noQuotes': 'Aucun devis trouvé.',
    'admin.testQuotes.items': 'Articles',
    'admin.testQuotes.exclVat': 'Hors TVA',
    'admin.testQuotes.inclVat': 'Incl. TVA',
    'admin.testQuotes.date': 'Date',
    'admin.testQuotes.action': 'Action',
    'admin.testQuotes.changeStatus': 'Modifier le statut',
    'admin.testQuotes.client': 'Client',
    'admin.testQuotes.email': 'E-mail',
    'admin.testQuotes.status': 'Statut',
    'admin.testQuotes.delete': 'Supprimer',
    'admin.testQuotes.deleteTitle': 'Supprimer le devis',
    'admin.testQuotes.deleteConfirm': 'Êtes-vous sûr de vouloir supprimer ce devis ? Cette action est irréversible.',
    'admin.testQuotes.deleteOnlyDraft': 'Seuls les devis en statut "draft" peuvent être supprimés. Les devis envoyés restent archivés.',
    'admin.testQuotes.deleted': 'Devis supprimé.',
    'admin.testQuotes.archiveNote': 'Les devis en brouillon peuvent être supprimés. Les devis envoyés, acceptés ou refusés sont archivés de manière permanente à des fins d\'audit.',

    // Restpartijen / fin de stock (FR)
    'restpartijen.tag': 'Fin de stock · Offre temporaire',
    'restpartijen.title': 'Fins de séries — réductions sur dernières quantités',
    'restpartijen.desc': 'Plaquettes de parement premium, anciennes collections ou surstock de nos fabricants. Quantités limitées, prix attractifs.',
    'restpartijen.loading': 'Chargement…',
    'restpartijen.empty': 'Aucune fin de série pour le moment',
    'restpartijen.emptyDesc': 'Notre stock change régulièrement. Revenez plus tard ou consultez le catalogue complet.',
    'restpartijen.backToCatalog': 'Retour au catalogue',
    'restpartijen.available': 'Disponible',
    'restpartijen.interested': 'Intéressé par une fin de série ?',
    'restpartijen.interestedDesc': 'Les quantités sont limitées. Contactez-nous par téléphone ou e-mail pour réservation.',
    'restpartijen.requestQuote': 'Demander un devis',
    'restpartijen.hero.badge': 'Stock épuisé = épuisé · Quantités limitées',
    'restpartijen.hero.title1': 'Plaquettes de parement premium,',
    'restpartijen.hero.title2': 'jusqu\'à 50% moins cher',
    'restpartijen.hero.subtitle': 'Fins de série, anciennes collections et surstock de nos fabricants italiens, polonais et espagnols. Même qualité premium — fraction du prix. Premier arrivé, premier servi.',
    'restpartijen.hero.maxDiscount': 'Remise max',
    'restpartijen.hero.available': 'Disponibles',
    'restpartijen.hero.lots': 'Lots',
    'restpartijen.benefit1.title': 'Jusqu\'à 50% de remise',
    'restpartijen.benefit1.desc': 'Briques premium à une fraction du prix catalogue. Plus le stock est bas, plus le prix est attractif.',
    'restpartijen.benefit2.title': 'Même qualité premium',
    'restpartijen.benefit2.desc': 'Résistant au gel (classe F2), couleur stable, durée de vie 50+ ans. Pas de second choix.',
    'restpartijen.benefit3.title': 'Livraison rapide',
    'restpartijen.benefit3.desc': 'Les fins de série sont déjà dans notre dépôt à Schilde — livraison en 5 jours ouvrés en Belgique.',
    'restpartijen.benefit4.title': 'Unique pour votre projet',
    'restpartijen.benefit4.desc': 'Anciennes collections plus produites — exclusif pour votre façade.',
    'restpartijen.list.title': 'Lots disponibles',
    'restpartijen.list.subtitle': 'Voici nos offres actuelles. Le stock est limité — les m² réservés disparaissent immédiatement de cette liste.',
    'restpartijen.list.opOp': 'Stock épuisé',
    'restpartijen.list.endOfStock': 'Fin de stock',
    'restpartijen.list.savings': 'Économies',
    'restpartijen.list.left': 'restant',
    'restpartijen.list.reserve': 'Réserver / infos',
    'restpartijen.why.title': 'Pourquoi acheter en fin de stock ?',
    'restpartijen.why.1': 'Qualité identique à notre catalogue régulier — pas de second choix, pas de défauts.',
    'restpartijen.why.2': 'Jusqu\'à 50% moins cher que la même brique au prix catalogue normal.',
    'restpartijen.why.3': 'Disponibilité immédiate — pas d\'attente de 4 à 6 semaines pour la production.',
    'restpartijen.why.4': 'Exclusif — anciennes collections plus disponibles ailleurs.',
    'restpartijen.why.5': 'Garantie complète comme sur produits neufs — 10 ans sur le matériau.',
    'restpartijen.note.title': 'À savoir',
    'restpartijen.note.heading': 'Stock épuisé = épuisé — pas de réassort',
    'restpartijen.note.body': 'Les fins de série sont précisément cela : derniers stocks. Un réapprovisionnement pour extension n\'est généralement plus possible. Conseil : commandez toujours 5 à 10 % de plus pour pertes de coupe et réparations futures.',
    'restpartijen.recentSold.title': 'Récemment vendus',
    'restpartijen.recentSold.desc': 'Ce que vous avez manqué — ces lots sont entièrement vendus. Inscrivez-vous à la newsletter pour être informé des prochaines offres.',
    'restpartijen.recentSold.sticker': 'Vendu',
    'nav.restpartijen': 'Fins de séries',
    'nav.contact': 'Contact',

    // Contact (FR)
    'contact.tag': 'Prenez contact',
    'contact.title': 'Contactez-nous',
    'contact.subtitle': 'Des questions sur un projet, une commande ou une collaboration ? Envoyez un message ou appelez directement. Nous répondons généralement dans les 24 heures.',
    'contact.formTitle': 'Envoyer un message',
    'contact.formDesc': 'Les champs avec * sont obligatoires. Indiquez aussi votre code postal pour être mis en relation avec notre revendeur local.',
    'contact.fName': 'Votre nom',
    'contact.fEmail': 'E-mail',
    'contact.fPhone': 'Téléphone',
    'contact.fPostal': 'Code postal',
    'contact.fPostalHint': 'Permet de vous orienter vers notre revendeur local dans votre région.',
    'contact.fSubject': 'Sujet',
    'contact.fMessage': 'Votre message',
    'contact.consent': 'J\'accepte que mes données soient utilisées pour répondre à ma demande, conformément à la politique de confidentialité.',
    'contact.send': 'Envoyer le message',
    'contact.sending': 'Envoi…',
    'contact.successTitle': 'Message envoyé',
    'contact.successDesc': 'Merci — nous vous recontacterons rapidement.',
    'contact.errorTitle': 'Échec de l\'envoi',
    'contact.errorDesc': 'Une erreur est survenue. Veuillez réessayer ou nous appeler directement.',
    'contact.responseTimeTitle': 'Temps de réponse',
    'contact.responseTimeDesc': 'Les messages reçoivent une réponse dans les 24 heures ouvrées.',
    'contact.emailRequired': 'L\'adresse e-mail est requise.',
    'contact.emailFormat': 'Veuillez entrer une adresse e-mail valide (ex. nom@exemple.be).',
    'contact.emailDomain': 'Cette adresse e-mail semble être un test ou jetable. Utilisez une vraie adresse pour que nous puissions vous joindre.',
    'seo.contact.title': 'Contact · Gevelsteenbedrijf.be — Plaquettes de Parement Belgique',
    'seo.contact.description': 'Questions sur les plaquettes de parement, prix, livraison ou installation ? Envoyez un message ou appelez. Schilde, Belgique — service en Flandre, Bruxelles et Wallonie.',
    'seo.contact.keywords': 'contact plaquettes parement, contact revêtement façade, parement Belgique contact, devis plaquettes parement',
    'seo.restpartijen.title': 'Fins de Stock Plaquettes de Parement | Gevelsteenbedrijf.be',
    'seo.restpartijen.description': 'Plaquettes de parement premium en fin de stock — jusqu\'à 50% moins cher. Quantités limitées, résistantes au gel, prêtes à livrer.',
    'seo.restpartijen.keywords': 'fin stock plaquettes, plaquettes parement promotion, briques fin série, plaquettes parement pas cher, déstockage façade',

    // Admin Resellers tab (FR)
    'admin.tab.resellers': 'Revendeurs',
    'admin.resellers.title': 'Revendeurs',
    'admin.resellers.empty': 'Aucun revendeur. Cliquez "Ajouter" pour commencer.',
    'admin.resellers.add': 'Ajouter',
    'admin.resellers.new': 'Nouveau revendeur',
    'admin.resellers.edit': 'Modifier le revendeur',
    'admin.resellers.company': 'Entreprise',
    'admin.resellers.companyName': 'Nom de l\'entreprise',
    'admin.resellers.slug': 'URL-slug',
    'admin.resellers.contactPerson': 'Personne de contact',
    'admin.resellers.loginEmail': 'E-mail de connexion',
    'admin.resellers.btwNumber': 'Numéro de TVA',
    'admin.resellers.contactEmail': 'E-mail de contact',
    'admin.resellers.contactPhone': 'Téléphone',
    'admin.resellers.address': 'Adresse',
    'admin.resellers.postalCode': 'Code postal',
    'admin.resellers.city': 'Ville',
    'admin.resellers.discount': 'Remise',
    'admin.resellers.postcodes': 'Zone',
    'admin.resellers.servicePostcodes': 'Zone d\'activité (codes postaux séparés par virgule)',
    'admin.resellers.postcodesHint': 'Ex. 2000,2018,2020 — les clients avec ces codes postaux sont automatiquement assignés.',
    'admin.resellers.customers': 'Clients',
    'admin.resellers.quotes': 'Devis',
    'admin.resellers.status': 'Statut',
    'admin.resellers.active': 'Actif',
    'admin.resellers.inactive': 'Désactivé',
    'admin.resellers.notes': 'Notes internes',
    'admin.resellers.agreementsTitle': 'Accords & conditions avec ce revendeur',
    'admin.resellers.agreementsHint': 'Texte libre — conditions de livraison, délai de paiement, paliers de remise, exclusivités, contact, protocole client final. Seul vous et le co-propriétaire voient ceci.',
    'admin.resellers.agreementsPlaceholder': 'Ex.\nRemise : 30% standard, 35% à partir de 200 m²\nLivraison : franco à partir de 1500€\nPaiement : 30 jours\nClient : revendeur conserve le client 2 ans après dernière livraison',

    // Prijsbeheer (FR)
    'admin.pricing.resellerSection': 'Tarification par revendeur',
    'admin.pricing.resellerSectionDesc': 'Définissez une remise générale pour tous les produits + dérogations optionnelles par produit.',
    'admin.pricing.selectReseller': 'Choisir un revendeur',
    'admin.pricing.defaultDiscount': 'Remise par défaut',
    'admin.pricing.lastSaved': 'Dernière mise à jour',
    'admin.pricing.saved': '✓ Accords enregistrés',
    'admin.pricing.saving': 'Enregistrement…',
    'admin.pricing.saveAgreement': 'Enregistrer les accords',
    'admin.pricing.overridesTitle': 'Dérogations par produit (optionnel)',
    'admin.pricing.overridesDesc': 'Ne remplissez que là où l\'accord diffère de la remise par défaut.',
    'admin.pricing.product': 'Produit',
    'admin.pricing.catalog': 'Catalogue',
    'admin.pricing.overrideDiscount': '% différente',
    'admin.pricing.overridePrice': 'Prix fixe/m²',
    'admin.pricing.effective': 'Effectif',
    'admin.resellers.logoUrl': 'URL du logo',
    'admin.resellers.logoHint': 'Chemin vers le fichier logo (ex. /reseller-logos/nom.svg). UI d\'upload en v1.1.',
    'admin.resellers.deactivate': 'Désactiver (les données sont conservées)',
    'admin.resellers.confirmDeactivate': 'Désactiver ce revendeur ? Toutes les données historiques sont conservées, mais le revendeur ne peut plus se connecter et ne reçoit plus de nouveaux devis.',

    // Reseller dashboard (revendeur)
    'reseller.title': 'Tableau de bord Revendeur',
    'reseller.subtitle': 'Gérez votre profil, vos clients et vos devis',
    'reseller.loading': 'Chargement…',
    'reseller.accessDenied': 'Accès refusé',
    'reseller.accessDeniedDesc': 'Cette page est réservée aux revendeurs. Connectez-vous avec un compte revendeur.',
    'reseller.tab.profile': 'Profil',
    'reseller.tab.catalog': 'Catalogue',
    'reseller.tab.customers': 'Mes Clients',
    'reseller.tab.quotes': 'Mes Devis',
    'reseller.profile.title': 'Profil de l\'entreprise',
    'reseller.profile.ownerControlled': 'Géré par Het Gevelsteenbedrijf',
    'reseller.profile.ownerControlledNote': 'Ces champs sont gérés centralement. Demandez les modifications au propriétaire.',
    'reseller.profile.companyName': 'Nom de l\'entreprise',
    'reseller.profile.btwNumber': 'Numéro de TVA',
    'reseller.profile.discount': 'Remise par défaut',
    'reseller.profile.postcodes': 'Zone d\'activité (codes postaux)',
    'reseller.profile.contactEmail': 'E-mail de contact',
    'reseller.profile.contactPhone': 'Téléphone de contact',
    'reseller.profile.address': 'Adresse',
    'reseller.profile.city': 'Ville',
    'reseller.profile.postalCode': 'Code postal',
    'reseller.catalog.title': 'Produits avec vos prix',
    'reseller.catalog.desc': 'Le prix catalogue est ce que voit le client final. Votre prix est votre prix d\'achat (après remise).',
    'reseller.catalog.empty': 'Aucun produit disponible.',
    'reseller.catalog.product': 'Produit',
    'reseller.catalog.color': 'Couleur',
    'reseller.catalog.category': 'Catégorie',
    'reseller.catalog.catalogPrice': 'Prix catalogue',
    'reseller.catalog.yourPrice': 'Votre prix',
    'reseller.catalog.discount': 'Remise',
    'reseller.catalog.remnant': 'Fin de stock',
    'reseller.customers.title': 'Mes Clients',
    'reseller.customers.empty': 'Aucun client dans votre zone d\'activité.',
    'reseller.customers.name': 'Nom',
    'reseller.customers.email': 'E-mail',
    'reseller.customers.type': 'Type',
    'reseller.customers.city': 'Ville',
    'reseller.customers.quotes': 'Devis',
    'reseller.customers.since': 'Depuis',
    'reseller.quotes.title': 'Mes Devis',
    'reseller.quotes.empty': 'Aucun devis reçu.',
    'reseller.quotes.create': 'Nouveau devis',
    'reseller.quotes.createTitle': 'Créer un devis pour un client',
    'reseller.quotes.fName': 'Nom du client',
    'reseller.quotes.fEmail': 'E-mail du client',
    'reseller.quotes.fPhone': 'Téléphone',
    'reseller.quotes.fPostal': 'Code postal',
    'reseller.quotes.fIsB2B': 'Entreprise avec n° de TVA (B2B)',
    'reseller.quotes.fNotes': 'Notes (optionnel)',
    'reseller.quotes.items': 'Produits',
    'reseller.quotes.addItem': 'Ajouter un produit',
    'reseller.quotes.selectProduct': 'Choisir un produit…',

    // Backlog / Audit (FR)
    'admin.tab.backlog': 'Backlog',
    'admin.backlog.exportTitle': 'Exporter / Sauvegarde',
    'admin.backlog.exportDesc': 'Téléchargez toutes les données en CSV pour conservation hors ligne — en plus des sauvegardes automatiques Cloudflare D1.',
    'admin.backlog.exportQuotes': 'Devis (CSV)',
    'admin.backlog.exportCustomers': 'Clients (CSV)',
    'admin.backlog.deletedTitle': 'Devis supprimés (récupérables)',
    'admin.backlog.deletedDesc': 'Devis brouillon supprimés. Rien n\'est perdu — cliquez "Restaurer" pour les remettre en place.',
    'admin.backlog.deletedEmpty': 'Aucun devis supprimé.',
    'admin.backlog.deletedAt': 'Supprimé le',
    'admin.backlog.restore': 'Restaurer',
    'admin.backlog.restoreConfirm': 'Restaurer ce devis en statut brouillon ?',
    'admin.backlog.activityTitle': 'Journal d\'activité',
    'admin.backlog.activityDesc': 'Aperçu complet de toutes les actions admin : connexions, devis, changements de statut, impersonations.',
    'admin.backlog.activityEmpty': 'Aucune activité enregistrée.',
    'admin.backlog.when': 'Quand',
    'admin.backlog.who': 'Qui',
    'admin.backlog.what': 'Action',
    'admin.backlog.details': 'Détails',
    'admin.backlog.cloudflareTitle': 'Sauvegardes cloud automatiques',
    'admin.backlog.cloudflareDesc': 'En plus des exports CSV, Cloudflare D1 effectue des sauvegardes automatiques point-in-time. Gestion via Cloudflare Dashboard → D1 → gevelstenen-db → Backups.',

    // Test Account Switcher (FR)
    'switcher.title': 'Sélecteur de compte test',
    'switcher.subtitle': 'Basculez vers n\'importe quel compte pour voir leur vue',
    'switcher.toggle': 'Tester comme…',
    'switcher.loading': 'Chargement des comptes…',
    'switcher.empty': 'Aucun compte test trouvé.',
    'switcher.error': 'Échec du basculement',
    'switcher.hint': '⚠️ Toutes les actions sont enregistrées. Déconnectez-vous pour revenir au superadmin.',

    // Admin Mede-eigenaar section
    'admin.coOwner': 'Co-propriétaire',
    'admin.coOwner.sameData': 'Les deux propriétaires voient les mêmes données',

    // Admin Prijzen tab
    'admin.pricing.title': 'Gestion des Prix',
    'admin.pricing.desc': 'Prix brut = prix fournisseur. Markup % = votre marge (standard 25%). Prix de vente = brut × (1 + markup/100). En l\'absence de prix fournisseur, le calcul est inversé à partir du prix fixe.',
    'admin.pricing.noProducts': 'Aucun produit trouvé.',
    'admin.pricing.grossPrice': 'Prix brut',
    'admin.pricing.grossPriceSupplier': 'Prix brut (fournisseur)',
    'admin.pricing.markup': 'Markup',
    'admin.pricing.markupPct': 'Markup %',
    'admin.pricing.sellPrice': 'Prix de vente',
    'admin.pricing.fixedPrice': 'Prix Fixe',
    'admin.pricing.product': 'Produit',
    'admin.pricing.category': 'Catégorie',
    'admin.pricing.actions': 'Actions',
    'admin.pricing.save': 'Enregistrer',
    'admin.pricing.saving': 'Enregistrement...',
    'admin.pricing.cancel': 'Annuler',
    'admin.pricing.edit': 'Modifier',

    // Admin PDF labels
    'admin.pdf.quote': 'DEVIS',
    'admin.pdf.ref': 'Réf',
    'admin.pdf.date': 'Date',
    'admin.pdf.validUntil': 'Valable jusqu\'au',
    'admin.pdf.client': 'Client',
    'admin.pdf.status': 'Statut',
    'admin.pdf.private': 'Particulier (B2C)',
    'admin.pdf.product': 'Produit',
    'admin.pdf.articleNumber': 'Numéro d\'article',
    'admin.pdf.colorTexture': 'Couleur / Texture',
    'admin.pdf.surface': 'Surface',
    'admin.pdf.jointWidth': 'Largeur de joint',
    'admin.pdf.jointColor': 'Couleur de joint',
    'admin.pdf.cuttingLoss': 'Perte de coupe',
    'admin.pdf.stripCount': 'Nombre de bandes',
    'admin.pdf.corners': 'Pierres d\'angle',
    'admin.pdf.glue': 'Colle',
    'admin.pdf.grout': 'Mortier',
    'admin.pdf.requestDetails': 'Détails de la demande',
    'admin.pdf.configuration': 'Configuration',
    'admin.pdf.clientType': 'Type de client',
    'admin.pdf.company': 'Entreprise',
    'admin.pdf.btwNumber': 'Numéro de TVA',
    'admin.pdf.postalCode': 'Code postal',
    'admin.pdf.message': 'Message',
    'admin.pdf.priceOverview': 'Aperçu des prix',
    'admin.pdf.description': 'Description',
    'admin.pdf.amount': 'Montant',
    'admin.pdf.subtotalExclVat': 'Sous-total hors TVA',
    'admin.pdf.vat': 'TVA (21%)',
    'admin.pdf.totalInclVat': 'Total incl. TVA',
    'admin.pdf.conditions': 'Conditions générales',
    'admin.pdf.cond1': 'Devis valable 30 jours après la date',
    'admin.pdf.cond2': 'Délai de livraison : 2-4 semaines après confirmation',
    'admin.pdf.cond3': 'Paiement : 50% d\'acompte, 50% à la livraison',
    'admin.pdf.cond4': 'Prix hors pose sauf mention contraire',

    // CUSTOMER DASHBOARD
    'customer.business': 'Professionnel',
    'customer.private': 'Particulier',
    'customer.btwAccount': 'Compte assujetti à la TVA',

    // Customer tabs
    'customer.tab.myData': 'Mes Données',
    'customer.tab.myQuotes': 'Mes Devis',
    'customer.tab.myPhotos': 'Mes Photos',

    // Customer Gegevens
    'customer.data.title': 'Mes Données',
    'customer.data.edit': 'Modifier',
    'customer.data.cancel': 'Annuler',
    'customer.data.save': 'Enregistrer',
    'customer.data.saving': 'Enregistrement...',
    'customer.data.saved': 'Données enregistrées',
    'customer.data.saveFailed': 'Échec de l\'enregistrement.',
    'customer.data.name': 'Nom',
    'customer.data.email': 'E-mail',
    'customer.data.phone': 'Téléphone',
    'customer.data.address': 'Adresse',
    'customer.data.postalCode': 'Code postal',
    'customer.data.city': 'Ville',
    'customer.data.company': 'Nom de l\'entreprise',
    'customer.data.btw': 'Numéro de TVA',
    'customer.data.notes': 'Remarques',
    'customer.data.companyLabel': 'Entreprise',

    // Customer Offertes
    'customer.quotes.title': 'Mes Devis',
    'customer.quotes.newCalc': 'Nouveau calcul',
    'customer.quotes.noQuotes': 'Aucun devis',
    'customer.quotes.noQuotesDesc': 'Utilisez notre calculateur pour demander votre premier devis.',
    'customer.quotes.goToCalc': 'Vers le calculateur',
    'customer.quotes.subtotal': 'Sous-total',
    'customer.quotes.vat': 'TVA',
    'customer.quotes.totalInclVat': 'Total incl. TVA',
    'customer.quotes.priceInclVat': 'Prix incl. TVA',
    'customer.quotes.pdfGenerating': 'PDF en cours de génération',

    // Customer quote statuses
    'customer.status.draft': 'Brouillon',
    'customer.status.sent': 'Envoyé',
    'customer.status.accepted': 'Accepté',
    'customer.status.rejected': 'Refusé',
    'customer.status.expired': 'Expiré',

    // Customer Foto's
    'customer.photos.title': 'Mes Photos',
    'customer.photos.count': 'photos',
    'customer.photos.addPhoto': 'Ajouter une photo',
    'customer.photos.noPhotos': 'Aucune photo',
    'customer.photos.noPhotosDesc': 'Téléchargez des photos de votre maison ou projet pour un conseil personnalisé.',
    'customer.photos.upload': 'Télécharger des photos',
    'customer.photos.projectPhoto': 'Photo du projet',

    // ADMIN SHORTHAND KEYS
    'admin.products': 'Produits',
    'admin.suppliers': 'Fournisseurs',
    'admin.customers': 'Clients',
    'admin.quotes': 'Devis',
    'admin.pricing': 'Gestion prix',
    'admin.name': 'Nom',
    'admin.email': 'E-mail',
    'admin.status': 'Statut',
    'admin.items': 'Articles',
    'admin.exclVat': 'Excl. TVA',
    'admin.inclVat': 'Incl. TVA',
    'admin.date': 'Date',
    'admin.action': 'Action',
    'admin.noQuotes': 'Aucun devis',
    'admin.draft': 'brouillon',
    'admin.sent': 'envoyé',
    'admin.accepted': 'accepté',
    'admin.rejected': 'refusé',
    'admin.save': 'Enregistrer',
    'admin.phone': 'Téléphone',
    'admin.company': 'Entreprise',
    'admin.role': 'Rôle',
    'admin.price': 'Prix',
    'admin.supplier': 'Fournisseur',
    'admin.markup': 'Marge',
    'admin.sellPrice': 'Prix de vente',
    'admin.bestseller': 'Best-seller',
    'admin.filter': 'Filtrer',
    'admin.sort': 'Trier',

    // CUSTOMER SHORTHAND KEYS
    'customer.myQuotes': 'Mes Devis',
    'customer.myPhotos': 'Mes Photos',
    'customer.myData': 'Mes Données',
    'customer.noQuotes': 'Pas encore de devis',
    'customer.uploadPhoto': 'Télécharger photo',
    'customer.tab.myFavorites': 'Favoris',
    'customer.favorites.title': 'Mes Favoris',
    'customer.favorites.desc': 'Produits que vous avez sélectionnés. Filip et le revendeur de votre région peuvent voir ces préférences lors de la préparation d\'un devis.',
    'customer.favorites.loading': 'Chargement…',
    'customer.favorites.empty': 'Aucun favori',
    'customer.favorites.emptyDesc': 'Cliquez sur le cœur dans le catalogue pour ajouter des produits.',
    'customer.favorites.browse': 'Voir le catalogue',
    'customer.favorites.remove': 'Retirer des favoris',
    'customer.photos.rulesTitle': 'Règles d\'upload',
    'customer.photos.ruleMax': 'Maximum {n} photos par compte',
    'customer.photos.ruleSize': 'Maximum {mb} MB par photo',
    'customer.photos.ruleType': 'Formats autorisés : JPG, PNG, WebP',
    'customer.photos.ruleResolution': 'Recommandé : au moins 1024×768 pixels',
    'customer.photos.maxReached': 'Vous avez atteint le maximum. Supprimez une photo pour en ajouter une nouvelle.',
    'customer.photos.errType': 'Type de fichier non supporté. Utilisez JPG, PNG ou WebP.',
    'customer.photos.errSize': 'Fichier trop grand — maximum 5 MB',
    'customer.photos.errTrimmed': 'Vous avez sélectionné plus de photos que permis. Seules les {n} premières sont uploadées.',
    'customer.photos.errGeneric': 'Échec de l\'upload. Réessayez ou contactez-nous.',

    // PDF — Export devis (toutes les clés utilisées par src/lib/quotePdf.ts)
    'pdf.quote': 'DEVIS',
    'pdf.ref': 'Réf',
    'pdf.date': 'Date',
    'pdf.validUntil': 'Valable jusqu\'au',
    'pdf.client': 'Client',
    'pdf.status': 'Statut',
    'pdf.product': 'Produit',
    'pdf.configuration': 'Configuration',
    'pdf.requestDetails': 'Détails de la demande',
    'pdf.priceOverview': 'Aperçu des prix',
    'pdf.description': 'Description',
    'pdf.amount': 'Montant',
    'pdf.subtotalExcl': 'Sous-total HT',
    'pdf.vat21': 'TVA (21%)',
    'pdf.totalIncl': 'Total TTC',
    'pdf.totalPrice': 'Prix total',
    'pdf.vatIncluded': 'TVA (21%) incluse',
    'pdf.conditions': 'Conditions générales',
    'pdf.cond1': 'Devis valable 30 jours à partir de la date',
    'pdf.cond2': 'Délai de livraison : 2-4 semaines après confirmation',
    'pdf.cond3': 'Paiement : 50% d\'acompte, 50% à la livraison',
    'pdf.cond4': 'Prix hors pose sauf mention contraire',
    'pdf.poweredBy': 'Propulsé par Het Gevelsteenbedrijf',
    'pdf.installerNote': 'Distributeur & installateur agréé en coopération avec Het Gevelsteenbedrijf',
    'pdf.unitPriceM2': 'PRIX UNITAIRES PAR M²',
    'pdf.totalsForQuote': 'Totaux pour ce devis',
    'pdf.installAddressLabel': 'Adresse de pose / Installation',
    'pdf.installAddressNote': 'Livraison et pose effectuées à cette adresse — pas à l\'adresse de facturation.',
    // PDF — Livraison & pose + installer-status (v4)
    'pdf.deliveryHeading': 'Livraison & pose',
    'pdf.deliveryNote1': 'L\'adresse pour l\'enlèvement, la livraison ou la pose est confirmée après acceptation de ce devis.',
    'pdf.deliveryNote2': 'En pose propre, nous livrons uniquement le matériel ; pour une installation via notre réseau un poseur sera attribué.',
    'pdf.installerSelfShort': 'Client pose lui-même',
    'pdf.installerWantedShort': 'Poseur via notre réseau',
    // PDF — Libellés configuration/répartition prix (v4)
    'pdf.cfg.product': 'Produit',
    'pdf.cfg.articleNr': 'N° d\'article',
    'pdf.cfg.colorTexture': 'Couleur / Texture',
    'pdf.cfg.dimensions': 'Dimensions',
    'pdf.cfg.area': 'Surface',
    'pdf.cfg.jointWidth': 'Largeur de joint',
    'pdf.cfg.jointColor': 'Couleur du joint',
    'pdf.cfg.cuttingLoss': 'Perte de coupe',
    'pdf.cfg.stripCount': 'Nombre de strips',
    'pdf.cfg.cornerPieces': 'Pièces d\'angle',
    'pdf.cfg.glue': 'Colle',
    'pdf.cfg.grout': 'Joint',
    'pdf.cfg.bricks': 'Briques',
    'pdf.cfg.cornerStones': 'Pierres d\'angle',
    'pdf.unit.bags': 'sacs',
    'pdf.unit.pieces': 'pcs',
  },

  // English — partial set covering the most user-visible UI. Anything not
  // defined here falls back to Dutch via t() (see LanguageProvider). We
  // prioritized: navigation, hero, footer, cookies, calculator, quote form,
  // PDF labels, and reseller dashboard.
  en: {
    'seo.retour.description': 'How to return brick slips and when you get your money back: deadlines, conditions and the steps for each case.',
    'seo.retour.keywords': 'return brick slips, refund, right of withdrawal, returns policy',
    'seo.retour.title': 'Returns & refunds | Gevelsteenbedrijf.be',
    'calc.desc': 'Calculate exactly how many brick slips, adhesive, jointing mortar and corner pieces you need.',
    'calc.onlineTool': 'Online Tool',
    'calc.title': 'Material Calculator',
    'home.allProjects': 'All projects →',
    'home.badge': 'Premium Belgian Quality',
    'home.calculator': 'Calculator',
    'home.collection': 'Collection',
    'home.ctaDesc': 'Request a free quotation today and discover how much you can save.',
    'home.ctaTitle': 'Ready to get started?',
    'home.discoverCollection': 'Discover the Collection',
    'home.heroTitle2': 'character',
    'home.inspiration': 'Inspiration',
    'home.model': 'Our Model',
    'home.model1.desc': 'Direct partnerships with top manufacturers from Poland, Spain and Italy for the best quality at factory prices.',
    'home.model1.title': 'International Manufacturers',
    'home.model2.desc': 'Your order is delivered through a recognised distributor in your region, with personal service and support.',
    'home.model2.title': 'Local Distributors',
    'home.model3.desc': 'Thanks to the direct line you save up to 40% compared to traditional distribution channels.',
    'home.model3.title': 'Best Price Guarantee',
    'home.onlineTool': 'Online Tool',
    'home.openCalc': 'Open Calculator',
    'home.popularStrips': 'Popular Brick Slips',
    'home.private.cta': 'Calculate your project',
    'home.private.desc': 'Build or renovate your dream home with premium brick slips at factory prices.',
    'home.private.title': 'Private Customers',
    'home.pro.cta': 'Technical Info',
    'home.pro.desc': 'Download technical sheets, declarations of performance and BIM files. Custom solutions possible.',
    'home.pro.title': 'Architects & Professionals',
    'home.realisations': 'Completed Projects',
    'home.requestQuote': 'Request a Quotation',
    'home.stat.products': 'Products',
    'home.stat.projects': 'Projects',
    'home.stat.savings': 'Savings',
    'home.usp.calc': 'Free calculation',
    'home.usp.custom': 'Custom possible',
    'home.usp.delivery': 'Delivered by a local distributor',
    'home.usp.factory': 'Straight from the manufacturer',
    'home.usp.warranty': 'Warranty included',
    'home.viewAll': 'View all products →',
    'home.viewCollection': 'View the Collection',
    'home.whyDesc': 'We connect international manufacturers directly with you, through a network of local distributors.',
    'home.whyTitle': 'Why Gevelsteenbedrijf?',
    'nav.architects': 'Architects',
    'nav.custom': 'Technical info',
    'nav.requestQuote': 'Request a Quotation',
    'products.3d.dayMode': 'Day',
    'products.3d.download': 'Download PDF',
    'products.3d.dragHint': 'drag',
    'products.3d.gardenClick': 'Click garden elements for info',
    'products.3d.materialsLabel': 'Material calculation',
    'products.3d.materialsNet': 'Net bricks',
    'products.3d.materialsWall': 'Total facade area',
    'products.3d.materialsWindows': 'Openings',
    'products.3d.nightMode': 'Night',
    'products.3d.rotate': 'Rotate and tilt to view the house from every side',
    'products.3d.title': '3D Facade Preview',
    'products.allColors': 'All Colours',
    'products.allTextures': 'All Textures',
    'products.calculate': 'Calculate',
    'products.clearFilters': 'Clear filters',
    'products.collection': 'Collection',
    'products.desc': 'Discover our range of premium brick slips. Filter by colour and texture.',
    'products.found': 'found',
    'products.noResults': 'No products found',
    'products.noResultsDesc': 'Adjust your filters to find products.',
    'products.product': 'product',
    'products.products': 'products',
    'products.quote': 'Quotation',
    'products.search': 'Search brick slips...',
    'products.tapToPreview': 'Click the number of the chosen brick',
    'products.title': 'Our Brick Slips',
    // NAV
    'nav.home': 'Home',
    'nav.products': 'Products',
    'nav.calculator': 'Calculator',
    'nav.inspiration': 'Inspiration & projects',
    'nav.quote': 'Quote',
    'nav.dashboard': 'Dashboard',
    'nav.login': 'Sign In',
    'nav.logout': 'Sign Out',
    'nav.restpartijen': 'End-of-stock',
    'nav.contact': 'Contact',

    // HERO + CALCULATOR INTRO (v3.2.102)
    'home.heroBadge':       'Premium Belgian quality',
    'home.heroTitle':       'Give your facade',
    'home.heroTitleAccent': 'character',
    'home.heroSubtitle':    'Premium brick strips direct from the manufacturer. Save up to 40% — no middlemen.',
    'home.calcBadge':       'Calculator',
    'home.calcTitle':       'Choose your brick — calculate your price',
    'home.calcDesc':        'Select a product below, enter your surface area, and get an accurate calculation instantly.',
    'home.savings':         'save',

    // Contact (EN)
    'contact.tag': 'Get in touch',
    'contact.title': 'Contact us',
    'contact.subtitle': 'Questions about a project, an order, or a partnership? Send a message or call directly. We typically reply within 24 hours.',
    'contact.formTitle': 'Send a message',
    'contact.formDesc': 'Fields with * are required. Add your postcode so we can route you to our local installer.',
    'contact.fName': 'Your name',
    'contact.fEmail': 'Email',
    'contact.fPhone': 'Phone',
    'contact.fPostal': 'Postcode',
    'contact.fPostalHint': 'Helps us connect you with our local installer in your region.',
    'contact.fSubject': 'Subject',
    'contact.fMessage': 'Your message',
    'contact.consent': 'I agree that my data will be used to answer my enquiry, in line with the privacy policy.',
    'contact.send': 'Send message',
    'contact.sending': 'Sending…',
    'contact.successTitle': 'Message sent',
    'contact.successDesc': 'Thank you — we will get back to you shortly.',
    'contact.errorTitle': 'Submission failed',
    'contact.errorDesc': 'Something went wrong. Please try again or call directly.',
    'contact.responseTimeTitle': 'Response time',
    'contact.responseTimeDesc': 'Messages are answered within 24 working hours.',
    'contact.emailRequired': 'Email address is required.',
    'contact.emailFormat': 'Please enter a valid email (e.g. name@example.com).',
    'contact.emailDomain': 'This email looks like a test or disposable address. Please use a real email so we can reach you.',
    'seo.contact.title': 'Contact · Gevelsteenbedrijf.be — Premium Brick Strips Belgium',
    'seo.contact.description': 'Questions about brick strips, pricing, delivery, or installation? Send a message or call directly. Schilde, Belgium — service across Flanders, Brussels, and Wallonia.',
    'seo.contact.keywords': 'contact gevelsteenbedrijf, brick strips contact, facade cladding belgium contact, brick strips quote',

    // Test Account Switcher (EN)
    'switcher.title': 'Test Account Switcher',
    'switcher.subtitle': 'Switch to any account to see their view',
    'switcher.toggle': 'Test as…',
    'switcher.loading': 'Loading accounts…',
    'switcher.empty': 'No test accounts found.',
    'switcher.error': 'Switch failed',
    'switcher.hint': '⚠️ All actions are logged. Sign out to return to superadmin.',

    // HOME
    'home.heroTitle1': 'Give your facade ',
    'home.heroTitleAccent': 'character',
    'home.heroDesc': 'Premium brick strips direct from the manufacturer. Save up to 40% — no middlemen.',
    'home.tagline': 'BE PREMIUM BELGIAN QUALITY',

    // FOOTER
    'footer.desc': 'Premium brick strips direct from the manufacturer. Specialised in facade cladding, custom tones and architectural projects.',
    'footer.belgian': 'Belgian quality',
    'footer.nav': 'Navigation',
    'footer.professionals': 'Professionals',
    'footer.contact': 'Contact',
    'footer.techSheets': 'Technical sheets',
    'footer.customStones': 'Custom stones',
    'footer.b2bPricing': 'B2B pricing',
    'footer.wholesaleLogin': 'Wholesale login',
    'footer.location': 'Bredabaan 123, 2970 Schilde, Belgium',
    'footer.btw': 'VAT BE0123.456.789',
    'footer.rights': '© 2026 Gevelsteenbedrijf.be — All rights reserved.',
    'footer.terms': 'Terms',
    'pdp.title.suffix': 'buy brick slips',
    'pdp.metaDesc': 'premium brick slips, straight from the manufacturer. Price per m²:',
    'pdp.exVat': 'excl. VAT',
    'pdp.spec.dimensions': 'Dimensions',
    'pdp.spec.color': 'Colour',
    'pdp.spec.texture': 'Texture',
    'pdp.spec.category': 'Style',
    'pdp.spec.sku': 'SKU',
    'pdp.spec.delivery': 'Delivery time',
    'pdp.spec.weeks': 'weeks',
    'pdp.usp1': 'Same quality as full-size facing brick — 10-year material warranty',
    'pdp.usp2': 'Straight from the manufacturer, no middlemen',
    'pdp.usp3': 'Free quote with exact material calculation',
    'pdp.ctaCalc': 'Calculate your project with this brick',
    'pdp.ctaFiche': 'Technical sheet',
    'pdp.more': 'Discover our other bricks',
    'retour.title': 'Returns & refunds',
    'retour.intro': 'Here is how returns and refunds work. Questions? Contact us — we are happy to help.',
    'retour.s1.title': 'Right of withdrawal — 14 days',
    'retour.s1.body': 'As a consumer you have the legal right to withdraw from your online purchase within 14 calendar days of delivery, without giving a reason. Notify us by e-mail and you will receive return instructions right away.',
    'retour.s2.title': 'Sending items back',
    'retour.s2.body': 'Return the products within 14 days of your notification, undamaged, unused and in the original packaging on pallets. Return costs and risk are borne by the buyer, unless the delivery was wrong or damaged — in that case we arrange and pay for collection.',
    'retour.s3.title': 'Refund — within 14 days',
    'retour.s3.body': 'We refund the full purchase amount (including original delivery costs) within 14 days of receiving the return, or of receiving proof of shipment — whichever comes first. The refund uses the same payment method as the purchase.',
    'retour.s4.title': 'Exceptions',
    'retour.s4.body': 'End-of-stock lots are sold as-is and excluded from returns (clearly stated on the End-of-stock page). Made-to-order or specially produced bricks cannot be returned either. Opened bags of adhesive or grout are not taken back.',
    'retour.s5.title': 'Damaged or wrong delivery',
    'retour.s5.body': 'Check the delivery on receipt and report visible damage within 48 hours with photos. We replace damaged or wrongly delivered products free of charge, or refund them — whichever you prefer.',
    'retour.btw': 'VAT',
    'footer.retour': 'Returns & refunds',
    'calc.fixedLoss': '(fixed — included automatically)',
    'calc.stoneStrip': 'Our brick types — tap to switch',
    'data.insp.i5.title': 'Townhouse Antwerp-South',
    'data.insp.i5.desc': 'Renovated terraced house in grey tones with crisp joints — urban and timeless.',
    'data.insp.i6.title': 'Near-zero-energy home Mechelen',
    'data.insp.i6.desc': 'Near-energy-neutral new build with taupe and blue-grey accents on the gable.',
    'data.insp.i7.title': 'Fermette in Pajottenland',
    'data.insp.i7.desc': 'Warm fermette with ivory and terracotta brick slips, blending into the rolling landscape.',
    'data.insp.i8.title': 'Farmhouse in the Westhoek',
    'data.insp.i8.desc': 'Rebuilt farmhouse with rustic red brick slips and a wide sand-coloured joint.',
    'data.insp.i9.title': 'Warehouse Hasselt Kanaalkom',
    'data.insp.i9.desc': 'Repurposed waterfront warehouse with black and mixed-grey strips in wild bond.',
    'data.insp.i10.title': 'Office shed Kortrijk',
    'data.insp.i10.desc': 'Shed converted into offices; dark brick slips give the volume an industrial skin.',
    'data.insp.i11.title': 'Townhouse Leuven centre',
    'data.insp.i11.desc': 'Master townhouse in the protected city centre, restored with nuanced brickwork and white joints.',
    'data.insp.i12.title': 'Presbytery house Waasland',
    'data.insp.i12.desc': 'Classic presbytery-style house in warm red and sandy yellow — Flemish brick tradition in slips.',
    'data.insp.i1.title': 'Villa Kalmthout',
    'data.insp.i1.desc': 'Sleek modern villa with white and black brick slips in a wooded setting.',
    'data.insp.i2.title': 'Farmhouse restoration Limburg',
    'data.insp.i2.desc': 'Authentic renovation of a Limburg farmhouse with red and mixed brick slips.',
    'data.insp.i3.title': 'Loft Ghent centre',
    'data.insp.i3.desc': 'Industrial loft project in the heart of Ghent with grey and black accents.',
    'data.insp.i4.title': 'Villa Bruges',
    'data.insp.i4.desc': 'Classic villa near Bruges with warm brown and sandy brick slips.',
    'footer.privacy': 'Privacy',
    'footer.cookies': 'Cookies',

    // COOKIE BANNER
    'cookie.title': 'We respect your privacy',
    'cookie.description': 'We use cookies to enhance your experience, analyse traffic and personalise content. Your choice is stored for 12 months and can be changed at any time.',
    'cookie.essential.title': 'Strictly necessary',
    'cookie.essential.desc': 'Required for the site to function (session, language preference, shopping cart). Cannot be disabled.',
    'cookie.functional.title': 'Functional',
    'cookie.functional.desc': 'Remember your preferences such as language and selected products.',
    'cookie.analytics.title': 'Analytics',
    'cookie.analytics.desc': 'Help us understand how visitors use the site (anonymous traffic statistics).',
    'cookie.marketing.title': 'Marketing',
    'cookie.marketing.desc': 'Personalised ads on third-party sites and remarketing.',
    'cookie.acceptAll': 'Accept all',
    'cookie.essentialOnly': 'Only necessary',
    'cookie.savePreferences': 'Save preferences',
    'cookie.showDetails': 'Customise',
    'cookie.hideDetails': 'Hide options',
    'cookie.legalNote': 'For full details see our',
    'cookie.policyLink': 'cookie policy',
    'cookie.gpcDetected': 'Your browser sends a Global Privacy Control signal. We have automatically pre-selected only the necessary cookies.',

    // CALCULATOR
    'calc.productChoice': 'Product choice',
    'calc.area': 'Area',
    'calc.areaLabel': 'Net area (m²) — windows and doors excluded',
    'calc.jointWidth': 'Joint width',
    'calc.noJoint': 'No joint',

    'v6.kiesSteen': 'Choose this brick — calculate your price',
    'admin.products.discountManage': 'Manage discount',
    'admin.resellers.contactAdd': 'Add',
    'admin.resellers.contactRole': 'Role (e.g. Sales, Logistics)',
    'admin.resellers.contactsEmpty': 'No additional contacts yet',
    'admin.resellers.contactsHint': 'Other people from the same reseller company',
    'admin.resellers.contactsTitle': 'Additional contact people',
    'admin.resellers.discount2': 'Discount',
    'admin.resellers.installerAdd': 'Add',
    'admin.resellers.installerNotes': 'Notes (speciality, region, availability…)',
    'admin.resellers.installerTitle': 'This reseller\'s installers',
    'admin.resellers.installersEmpty': 'No installers yet',
    'admin.resellers.installersHint': 'Companies the reseller can recommend for installation',
    'admin.resellers.website': 'Website',
    'admin.resellers.websiteHint': 'Public website of this reseller (optional)',
    'cookie.functional.desc': 'Remember your preferences such as language and selected products.',
    'cookie.functional.title': 'Functional',
    'cookie.gpcDetected': 'Your browser sends a Global Privacy Control signal. We have automatically pre-selected only the necessary cookies.',
    'cookie.savePreferences': 'Save preferences',
    'pdf.downloadHint': 'Click "Save as PDF" or press Ctrl/Cmd+P',
    'pdf.print': 'Save as PDF',
    'admin.accepted': 'accepted',
    'admin.accessDenied': 'Access Denied',
    'admin.accessDeniedDesc': 'Sign in as owner or superadmin to view the dashboard.',
    'admin.action': 'Action',
    'admin.apiFooter': 'All data via secure admin endpoints.',
    'admin.backlog.activityDesc': 'Full overview of all admin actions: logins, quotations, status changes, impersonations. Compliance evidence.',
    'admin.backlog.activityEmpty': 'No activity logged.',
    'admin.backlog.activityTitle': 'Activity log',
    'admin.backlog.cloudflareDesc': 'On top of these CSV exports, Cloudflare D1 also creates automatic point-in-time backups — restoring to an earlier moment is possible in case of an emergency. Manage this via Cloudflare Dashboard → D1 → gevelstenen-db → Backups.',
    'admin.backlog.cloudflareTitle': 'Automatic cloud backups',
    'admin.backlog.deletedAt': 'Deleted on',
    'admin.backlog.deletedDesc': 'Draft quotations that have been discarded. Nothing is permanently lost — click "Restore" to put them back.',
    'admin.backlog.deletedEmpty': 'No deleted quotations.',
    'admin.backlog.deletedTitle': 'Deleted quotations (recoverable)',
    'admin.backlog.details': 'Details',
    'admin.backlog.exportCustomers': 'Customers (CSV)',
    'admin.backlog.exportDesc': 'Download all your data as CSV. Keep it offline or in the cloud — extra security on top of the automatic Cloudflare D1 backups.',
    'admin.backlog.exportQuotes': 'Quotations (CSV)',
    'admin.backlog.exportTitle': 'Export / Backup',
    'admin.backlog.restore': 'Restore',
    'admin.backlog.restoreConfirm': 'Restore this quotation to draft status?',
    'admin.backlog.what': 'Action',
    'admin.backlog.when': 'When',
    'admin.backlog.who': 'Who',
    'admin.bestseller': 'Bestseller',
    'admin.coOwner': 'Co-owner',
    'admin.coOwner.sameData': 'Both owners see the same data',
    'admin.company': 'Company',
    'admin.customers': 'Customers',
    'admin.customers.auditNote': 'Quotations are kept permanently and cannot be deleted. All quotations are permanently archived and can no longer be deleted once sent.',
    'admin.customers.clients': 'customers',
    'admin.customers.excl': 'excl.',
    'admin.customers.loadingQuotes': 'Loading quotations...',
    'admin.customers.noCustomers': 'No customers found.',
    'admin.customers.noQuotesForCustomer': 'No quotations for this customer.',
    'admin.customers.off': 'quot.',
    'admin.customers.quotes': 'quotations',
    'admin.customers.quotesTitle': 'Quotations',
    'admin.customers.since': 'Since',
    'admin.customers.title': 'Customers & Quotations',
    'admin.customers.total': 'total',
    'admin.date': 'Date',
    'admin.demoMode': 'Demo mode — products only visible',
    'admin.draft': 'draft',
    'admin.email': 'Email',
    'admin.exclVat': 'Excl. VAT',
    'admin.filter': 'Filter',
    'admin.inclVat': 'Incl. VAT',
    'admin.items': 'Items',
    'admin.loading': 'Loading data...',
    'admin.markup': 'Margin',
    'admin.name': 'Name',
    'admin.noQuotes': 'No quotations',
    'admin.pdf.amount': 'Amount',
    'admin.pdf.articleNumber': 'Article number',
    'admin.pdf.btwNumber': 'VAT number',
    'admin.pdf.client': 'Customer',
    'admin.pdf.clientType': 'Customer type',
    'admin.pdf.colorTexture': 'Colour / Texture',
    'admin.pdf.company': 'Company',
    'admin.pdf.cond1': 'Quotation valid for 30 days from date',
    'admin.pdf.cond2': 'Delivery time: 2-4 weeks after confirmation',
    'admin.pdf.cond3': 'Payment: 50% deposit, 50% on delivery',
    'admin.pdf.cond4': 'Prices exclude installation unless stated otherwise',
    'admin.pdf.conditions': 'Terms and conditions',
    'admin.pdf.configuration': 'Configuration',
    'admin.pdf.corners': 'Corner pieces',
    'admin.pdf.cuttingLoss': 'Cutting waste',
    'admin.pdf.date': 'Date',
    'admin.pdf.description': 'Description',
    'admin.pdf.glue': 'Adhesive',
    'admin.pdf.grout': 'Jointing mortar',
    'admin.pdf.jointColor': 'Joint colour',
    'admin.pdf.jointWidth': 'Joint width',
    'admin.pdf.message': 'Message',
    'admin.pdf.postalCode': 'Postcode',
    'admin.pdf.priceOverview': 'Price overview',
    'admin.pdf.private': 'Private customer (B2C)',
    'admin.pdf.product': 'Product',
    'admin.pdf.quote': 'QUOTATION',
    'admin.pdf.ref': 'Ref',
    'admin.pdf.requestDetails': 'Request details',
    'admin.pdf.status': 'Status',
    'admin.pdf.stripCount': 'Number of slips',
    'admin.pdf.subtotalExclVat': 'Subtotal excl. VAT',
    'admin.pdf.surface': 'Surface area',
    'admin.pdf.totalInclVat': 'Total incl. VAT',
    'admin.pdf.validUntil': 'Valid until',
    'admin.pdf.vat': 'VAT (21%)',
    'admin.phone': 'Phone',
    'admin.price': 'Price',
    'admin.pricing': 'Price management',
    'admin.pricing.actions': 'Actions',
    'admin.pricing.cancel': 'Cancel',
    'admin.pricing.catalog': 'Catalogue',
    'admin.pricing.category': 'Category',
    'admin.pricing.colDiscount': 'Discount per product %',
    'admin.pricing.colDiscountTitle': 'Discount specific to this product — overrides the default discount when filled in',
    'admin.pricing.colEffective': 'Effective',
    'admin.pricing.colInkoop': 'Purchase price',
    'admin.pricing.colInkoopTitle': 'Purchase price from the supplier',
    'admin.pricing.colMarge': 'Margin',
    'admin.pricing.colMargeTitle': 'Margin = (selling price − purchase price) / selling price × 100. Same formula as in the Products · Prices tab.',
    'admin.pricing.colProduct': 'Product',
    'admin.pricing.colSell': 'Selling price',
    'admin.pricing.colSellTitle': 'Selling price (excl. VAT). Adjusted in the Products · Prices tab.',
    'admin.pricing.colStandard': 'Standard %',
    'admin.pricing.colStandardTitle': 'Standard discount for this reseller (same for all products)',
    'admin.pricing.colStatus': 'Status',
    'admin.pricing.defaultDiscount': 'Default discount',
    'admin.pricing.desc': 'Gross price = supplier price. Markup % = your margin (25% by default). Selling price = gross × (1 + markup/100). If the supplier price is missing, it is calculated back from the fixed price.',
    'admin.pricing.edit': 'Edit',
    'admin.pricing.effective': 'Effective',
    'admin.pricing.fixedPrice': 'Fixed Price',
    'admin.pricing.grossPrice': 'Gross price',
    'admin.pricing.grossPriceSupplier': 'Gross price (supplier)',
    'admin.pricing.lastSaved': 'Last updated on',
    'admin.pricing.markup': 'Markup',
    'admin.pricing.markupPct': 'Markup %',
    'admin.pricing.noProducts': 'No products found.',
    'admin.pricing.overrideDiscount': 'Custom %',
    'admin.pricing.overridePrice': 'Fixed price/m²',
    'admin.pricing.overridesDesc': 'Only fill in where the agreement differs from the default discount. A fixed price takes precedence over a % discount.',
    'admin.pricing.overridesTitle': 'Per-product exceptions (optional)',
    'admin.pricing.pauseClick': 'Click to pause this product for this reseller',
    'admin.pricing.pauseUnpause': 'Click to make available again',
    'admin.pricing.pausedItalic': 'paused',
    'admin.pricing.product': 'Product',
    'admin.pricing.resellerSection': 'Price agreements per reseller',
    'admin.pricing.resellerSectionDesc': 'Set a general discount for all products + optional per-product overrides. Applied server-side to every quotation from/to this reseller.',
    'admin.pricing.save': 'Save',
    'admin.pricing.saveAgreement': 'Save agreements',
    'admin.pricing.saveAllAbove': 'Saves all changes above',
    'admin.pricing.saveBarDesc': 'default discount, per-product discounts and pause status for',
    'admin.pricing.saveChanges': 'Save changes',
    'admin.pricing.saveFor': 'Save for',
    'admin.pricing.saved': '✓ Agreements saved',
    'admin.pricing.saving': 'Saving...',
    'admin.pricing.savingDots': 'Saving…',
    'admin.pricing.selectReseller': 'Select reseller',
    'admin.pricing.sellPrice': 'Selling price',
    'admin.pricing.statusActive': 'Active',
    'admin.pricing.statusPause': 'Paused',
    'admin.pricing.tableDesc': 'One table with everything you need: gross price from the supplier, your markup and selling price from Price management, plus the discount per reseller (and optionally a pause if you do not want to offer a product through this reseller).',
    'admin.pricing.thisReseller': 'this reseller',
    'admin.pricing.title': 'Price management',
    'admin.products': 'Products',
    'admin.products.actions': 'Actions',
    'admin.products.akpBlockHint': '(optional — can also be done later via the Products table)',
    'admin.products.akpBlockTitle': '📦 Selling price / m²',
    'admin.products.akpDiscount': 'Discount Reseller→End cust.',
    'admin.products.akpDiscountTooltip': 'Discount the reseller passes on to the end customer (visible online)',
    'admin.products.akpLev': 'Purch. price Resel.',
    'admin.products.akpLevHint': 'Reseller purchase price — basis for the reseller discount in the Discounts tab',
    'admin.products.akpLevTooltip': 'Reseller purchase price — what the reseller pays Filip (excl. VAT)',
    'admin.products.bestsellers': 'Bestsellers',
    'admin.products.bestsellersOff': 'Show bestsellers only',
    'admin.products.bestsellersOn': 'Click to switch off the bestseller filter',
    'admin.products.cancel': 'Cancel',
    'admin.products.category': 'Category',
    'admin.products.color': 'Colour',
    'admin.products.dealConsistent': 'Selling formula checks out — click Save to lock it in',
    'admin.products.dealInconsistent': 'Net end customer price differs from Purch. price Resel. × (1 − discount). Please fill in again.',
    'admin.products.dimensions': 'Dimensions',
    'admin.products.ean': 'EAN code',
    'admin.products.edit': 'Edit',
    'admin.products.editProduct': 'Edit product',
    'admin.products.emptyBestsellers': 'The bestsellers filter is active, but no product is marked as a bestseller.',
    'admin.products.emptyBoth': 'Bestsellers filter + supplier {supplier} hide all {count} products.',
    'admin.products.emptyFilterTitle': 'No products visible — a filter is switched on',
    'admin.products.emptySupplier': 'Supplier {supplier} has no products in this list.',
    'admin.products.exclVat': 'Excl. VAT',
    'admin.products.exclVatTooltip': 'Selling price excluding VAT — what the customer pays before VAT',
    'admin.products.filterSupplier': 'Click to filter by this supplier',
    'admin.products.fixedPrice': 'Fixed Price',
    'admin.products.incl21': 'Incl. 21%',
    'admin.products.incl6': 'Incl. 6%',
    'admin.products.margeTooltip': 'Profit on the selling price — (selling price − purchase price) / selling price × 100',
    'admin.products.name': 'Name',
    'admin.products.netAkp': 'Net end customer excl. VAT',
    'admin.products.netAkpTooltip': 'Net end customer price excl. VAT — what the customer pays on the website before VAT',
    'admin.products.optional': 'Optional',
    'admin.products.priceM2Badge': 'Prices / m²',
    'admin.products.priceM2Title': 'All prices in this table are per square metre (m²)',
    'admin.products.purchase': 'Purchase price',
    'admin.products.purchaseSubtitle': 'Purch. price {akp} · −{disc}%',
    'admin.products.purchaseTooltip': 'Net purchase price — what is paid to the supplier (click ✎ to fill in supplier purchase price + discount)',
    'admin.products.resetFilters': 'Reset filters · show all products',
    'admin.products.resetFiltersFull': 'Reset filters · show all {count} products',
    'admin.products.resetFiltersTitle': 'Switch off all filters to see all products',
    'admin.products.save': 'Save',
    'admin.products.saving': 'Saving...',
    'admin.products.serialNumber': 'Serial number',
    'admin.products.showAll': 'Show all',
    'admin.products.specs': 'Specs',
    'admin.products.supplier': 'Supplier',
    'admin.products.techSpecs': 'Tech Specs',
    'admin.products.texture': 'Texture',
    'admin.products.title': 'Products · Adjust prices',
    'admin.products.weight': 'Weight',
    'admin.quotes': 'Quotations',
    'admin.rejected': 'rejected',
    'admin.resellers.active': 'Active',
    'admin.resellers.add': 'Add',
    'admin.resellers.address': 'Address',
    'admin.resellers.agreementsHint': 'Free text — delivery terms, payment terms, discount tiers, exclusivity arrangements, contact person, end-customer protocol, etc. Only you and the co-owner can see this; the reseller cannot.',
    'admin.resellers.agreementsPlaceholder': 'E.g.\\nDiscount tier: 30% standard, 35% from 200 m²\\nDelivery: carriage paid from €1500\\nPayment: 30 days from invoice\\nEnd customer: reseller keeps the customer for 2 years after the last delivery\\nContact: jan@antwerpse-bricks.be (general) / piet@… (technical)',
    'admin.resellers.agreementsTitle': 'Agreements & terms with this reseller',
    'admin.resellers.btwNumber': 'VAT number',
    'admin.resellers.city': 'Municipality',
    'admin.resellers.company': 'Company',
    'admin.resellers.companyName': 'Company name',
    'admin.resellers.confirmDeactivate': 'Deactivate this reseller? All historical data is kept, but the reseller can no longer log in and new quotations will no longer come in.',
    'admin.resellers.contactEmail': 'Contact email',
    'admin.resellers.contactPerson': 'Contact person',
    'admin.resellers.contactPhone': 'Phone',
    'admin.resellers.customers': 'Customers',
    'admin.resellers.deactivate': 'Deactivate (data is kept)',
    'admin.resellers.discount': 'Discount',
    'admin.resellers.edit': 'Edit reseller',
    'admin.resellers.empty': 'No resellers added yet. Click "Add" to get started.',
    'admin.resellers.inactive': 'Deactivated',
    'admin.resellers.loginEmail': 'Login email',
    'admin.resellers.logoHint': 'Path to the logo file (e.g. /reseller-logos/name.svg). Upload UI to follow in v1.1.',
    'admin.resellers.logoUrl': 'Logo URL',
    'admin.resellers.new': 'New reseller',
    'admin.resellers.notes': 'Internal notes',
    'admin.resellers.postalCode': 'Postcode',
    'admin.resellers.postcodes': 'Service area',
    'admin.resellers.postcodesHint': 'E.g. 2000,2018,2020 — end customers with these postcodes are assigned automatically.',
    'admin.resellers.quotes': 'Quotations',
    'admin.resellers.servicePostcodes': 'Service area (comma-separated postcodes)',
    'admin.resellers.slug': 'URL slug',
    'admin.resellers.status': 'Status',
    'admin.resellers.title': 'Reseller companies',
    'admin.role': 'Role',
    'admin.save': 'Save',
    'admin.sellPrice': 'Selling price',
    'admin.sent': 'sent',
    'admin.sort': 'Sort',
    'admin.stat.internTesten': 'Internal testing',
    'admin.stat.offertesCount': 'Quotations',
    'admin.stat.prijsbeheer': 'Price management',
    'admin.stat.testOffertes': 'Test Quotations',
    'admin.status': 'Status',
    'admin.supplier': 'Supplier',
    'admin.suppliers': 'Suppliers',
    'admin.suppliers.actions': 'Actions',
    'admin.suppliers.add': 'Add',
    'admin.suppliers.address': 'Address',
    'admin.suppliers.btw': 'VAT number',
    'admin.suppliers.cancel': 'Cancel',
    'admin.suppliers.city': 'City',
    'admin.suppliers.company': 'Company name',
    'admin.suppliers.confirmDelete': 'Are you sure you want to delete this supplier?',
    'admin.suppliers.contactPerson': 'Contact person',
    'admin.suppliers.delete': 'Delete',
    'admin.suppliers.edit': 'Edit',
    'admin.suppliers.editSupplier': 'Edit Supplier',
    'admin.suppliers.email': 'Email',
    'admin.suppliers.name': 'Name',
    'admin.suppliers.nameRequired': 'Name is required',
    'admin.suppliers.nameShort': 'Name (short)',
    'admin.suppliers.new': 'New',
    'admin.suppliers.newSupplier': 'New Supplier',
    'admin.suppliers.noSuppliers': 'No suppliers found.',
    'admin.suppliers.phone': 'Phone',
    'admin.suppliers.postalCode': 'Postcode',
    'admin.suppliers.prod': 'prod.',
    'admin.suppliers.products': 'Products',
    'admin.suppliers.save': 'Save',
    'admin.suppliers.saving': 'Saving...',
    'admin.suppliers.title': 'Suppliers',
    'admin.suppliers.website': 'Website',
    'admin.tab.backlog': 'Activity log',
    'admin.tab.berichten': 'Messages',
    'admin.tab.klanten': 'Customers & Quotations',
    'admin.tab.klantenOffertes': 'Customers & Quotations',
    'admin.tab.leveranciers': 'Suppliers',
    'admin.tab.prijzen': 'Reseller Discounts',
    'admin.tab.producten': 'Products',
    'admin.tab.resellers': 'Resellers',
    'admin.tab.testOffertes': 'Test Quotations',
    'admin.testQuotes.action': 'Action',
    'admin.testQuotes.archiveNote': 'Draft quotations can be deleted. Sent, accepted or rejected quotations are archived permanently for audit purposes.',
    'admin.testQuotes.autoAppear': 'Quotations you submit there appear here automatically.',
    'admin.testQuotes.changeStatus': 'Change status',
    'admin.testQuotes.client': 'Customer',
    'admin.testQuotes.date': 'Date',
    'admin.testQuotes.delete': 'Delete',
    'admin.testQuotes.deleteConfirm': 'Are you sure you want to delete this quotation? This action cannot be undone.',
    'admin.testQuotes.deleteOnlyDraft': 'Only draft quotations can be deleted. Sent quotations remain archived.',
    'admin.testQuotes.deleteTitle': 'Delete quotation',
    'admin.testQuotes.deleted': 'Quotation deleted.',
    'admin.testQuotes.desc': 'Here you can view and manage test quotations as the owner. Simulate what a customer would see.',
    'admin.testQuotes.email': 'Email',
    'admin.testQuotes.exclVat': 'Excl. VAT',
    'admin.testQuotes.goToPrefix': 'Go to the ',
    'admin.testQuotes.goToQuotePage': 'quotation page',
    'admin.testQuotes.goToSuffix': ' to create a test quotation as a customer.',
    'admin.testQuotes.inclVat': 'Incl. VAT',
    'admin.testQuotes.items': 'Items',
    'admin.testQuotes.noQuotes': 'No quotations found.',
    'admin.testQuotes.status': 'Status',
    'admin.testQuotes.title': 'Test Quotations (internal)',
    'calc.accessories': 'Accessories',
    'calc.anthracite': 'anthracite',
    'calc.cuttingLossHint': 'Standard 10%. Increase for complex facades with a lot of cutting.',
    'calc.glueHint': '(~5 kg/m²)',
    'calc.grey': 'grey',
    'calc.groutColor': 'Jointing mortar colour',
    'calc.groutHint': '(depending on joint width)',
    'calc.pieces': 'pieces',
    'calc.sand': 'sand',
    'calc.swipeHint': 'Swipe to see more bricks',
    'calc.white': 'white',
    'cookie.acceptAll': 'Accept all cookies',
    'cookie.analytics.desc': 'Help us understand how visitors use the website. Anonymised data.',
    'cookie.analytics.title': 'Analytics cookies',
    'cookie.description': 'We use cookies to improve your experience. Essential cookies are necessary for the site to work. Analytics and marketing cookies are only placed with your consent, in line with Belgian privacy law and the GDPR.',
    'cookie.essential.desc': 'Necessary for the website to work: language preference, cookie choice, login session.',
    'cookie.essential.title': 'Essential cookies',
    'cookie.essentialOnly': 'Essential only',
    'cookie.hideDetails': 'Hide details',
    'cookie.legalNote': 'You can find more information in our',
    'cookie.marketing.desc': 'Used to show relevant adverts based on your interests.',
    'cookie.marketing.title': 'Marketing cookies',
    'cookie.policyLink': 'cookie policy',
    'cookie.showDetails': 'More details',
    'cookie.title': 'We respect your privacy',
    'customer.btwAccount': 'VAT-registered account',
    'customer.business': 'Business',
    'customer.data.address': 'Address',
    'customer.data.btw': 'VAT number',
    'customer.data.cancel': 'Cancel',
    'customer.data.city': 'City',
    'customer.data.company': 'Company name',
    'customer.data.companyLabel': 'Company',
    'customer.data.edit': 'Edit',
    'customer.data.email': 'Email',
    'customer.data.name': 'Name',
    'customer.data.notes': 'Comments',
    'customer.data.phone': 'Phone',
    'customer.data.postalCode': 'Postcode',
    'customer.data.save': 'Save',
    'customer.data.saveFailed': 'Saving failed.',
    'customer.data.saved': 'Details saved',
    'customer.data.saving': 'Saving...',
    'customer.data.title': 'My Details',
    'customer.favorites.browse': 'View catalogue',
    'customer.favorites.desc': 'Products you have marked. Filip and the distributor in your region can see these preferences when they draw up a quotation for you.',
    'customer.favorites.empty': 'No favourites yet',
    'customer.favorites.emptyDesc': 'Mark products with the heart icon in the catalogue to find them here.',
    'customer.favorites.loading': 'Loading favourites…',
    'customer.favorites.remove': 'Remove from favourites',
    'customer.favorites.title': 'My Favourites',
    'customer.myData': 'My Details',
    'customer.myPhotos': 'My Photos',
    'customer.myQuotes': 'My Quotations',
    'customer.noQuotes': 'No quotations yet',
    'customer.photos.addPhoto': 'Add photo',
    'customer.photos.count': 'photos',
    'customer.photos.errGeneric': 'Upload failed. Please try again or contact us.',
    'customer.photos.errSize': 'File too large — maximum 5 MB',
    'customer.photos.errTrimmed': 'You selected more photos than allowed. Only the first {n} will be uploaded.',
    'customer.photos.errType': 'File type not supported. Please use JPG, PNG or WebP.',
    'customer.photos.maxReached': 'You have already reached the maximum number of photos. Delete a photo first to add a new one.',
    'customer.photos.noPhotos': 'No photos',
    'customer.photos.noPhotosDesc': 'Upload photos of your home or project for personal advice.',
    'customer.photos.projectPhoto': 'Project photo',
    'customer.photos.ruleMax': 'Maximum {n} photos per account',
    'customer.photos.ruleResolution': 'Recommended: at least 1024×768 pixels; photos are resized automatically for optimal display',
    'customer.photos.ruleSize': 'Maximum {mb} MB per photo',
    'customer.photos.ruleType': 'Allowed: JPG, PNG, WebP',
    'customer.photos.rulesTitle': 'Upload rules',
    'customer.photos.title': 'My Photos',
    'customer.photos.upload': 'Upload photos',
    'customer.private': 'Private individual',
    'customer.quotes.goToCalc': 'Go to calculator',
    'customer.quotes.newCalc': 'New calculation',
    'customer.quotes.noQuotes': 'No quotations yet',
    'customer.quotes.noQuotesDesc': 'Use our calculator to request your first quotation.',
    'customer.quotes.pdfGenerating': 'PDF is being generated',
    'customer.quotes.priceInclVat': 'Price incl. VAT',
    'customer.quotes.subtotal': 'Subtotal',
    'customer.quotes.title': 'My Quotations',
    'customer.quotes.totalInclVat': 'Total incl. VAT',
    'customer.quotes.vat': 'VAT',
    'customer.status.accepted': 'Accepted',
    'customer.status.draft': 'Draft',
    'customer.status.expired': 'Expired',
    'customer.status.rejected': 'Rejected',
    'customer.status.sent': 'Sent',
    'customer.tab.myData': 'My Details',
    'customer.tab.myFavorites': 'Favourites',
    'customer.tab.myPhotos': 'My Photos',
    'customer.tab.myQuotes': 'My Quotations',
    'customer.uploadPhoto': 'Upload photo',
    'dash.active': 'Active',
    'dash.activeWholesalers': 'Active Wholesalers',
    'dash.addProduct': 'Add product',
    'dash.address': 'Address',
    'dash.admin': '👑 Administrator',
    'dash.analytics': '📈 Analytics',
    'dash.calculations': 'Calculations',
    'dash.cancel': 'Cancel',
    'dash.category': 'Category',
    'dash.chooseFile': 'Choose file',
    'dash.client': 'Client',
    'dash.color': 'Colour',
    'dash.comingSoon': 'Coming soon',
    'dash.comingSoonDesc': 'This module is currently under development and will be available soon.',
    'dash.conversionRate': 'Conversion rate',
    'dash.corners': 'corner pieces',
    'dash.customer': '👤 Customer',
    'dash.customers': '👥 Customers',
    'dash.delete': 'Delete',
    'dash.description': 'Description',
    'dash.descriptionPlaceholder': 'Describe the product...',
    'dash.dimensions': 'Dimensions',
    'dash.downloadPdf': 'Download PDF',
    'dash.dragDrop': 'Drag a file here or click to upload',
    'dash.edit': 'Edit',
    'dash.editProduct': 'Edit product',
    'dash.exclusiveContract': 'Exclusive contract',
    'dash.favorites': 'Favourites',
    'dash.future': 'Future',
    'dash.glueBags': 'bags of adhesive',
    'dash.goToCalc': 'Go to Calculator',
    'dash.groutBags': 'bags of jointing mortar',
    'dash.importProducts': 'Import',
    'dash.inProgress': 'In progress',
    'dash.inTalks': 'In talks',
    'dash.myDiscount': 'My Discount',
    'dash.myOrders': 'My Orders',
    'dash.myPrices': '💰 My Prices',
    'dash.myQuotes': '📄 My Quotations',
    'dash.new': 'New',
    'dash.newProduct': '+ New Product',
    'dash.newQuote': 'New Calculation',
    'dash.noQuotes': 'No saved quotations yet',
    'dash.noQuotesDesc': 'Make a calculation in the calculator and save it as a quotation.',
    'dash.openQuotes': 'Open Quotations',
    'dash.orders': '📋 Orders',
    'dash.overview': '📊 Overview',
    'dash.phone': 'Phone',
    'dash.planned': 'Planned',
    'dash.potential': 'Potential',
    'dash.pricing': 'Prices',
    'dash.product': 'Product',
    'dash.productMgmt': '📦 Product Management',
    'dash.productName': 'Product name',
    'dash.quoteSent': 'Quotation sent',
    'dash.recentQuotes': 'Recent Quotation Requests',
    'dash.retailPrice': 'Selling price/m²',
    'dash.revenueMonth': 'Revenue this month',
    'dash.saveChanges': 'Save changes',
    'dash.searchCustomer': 'Search customer...',
    'dash.serialNumber': 'Serial number',
    'dash.status': 'Status',
    'dash.stock': 'Stock',
    'dash.supplier': 'Supplier',
    'dash.supplierCode': 'Supplier code',
    'dash.supplierMgmt': 'Supplier Management',
    'dash.suppliers': '🚚 Suppliers',
    'dash.supportedFormats': 'Supported formats: Excel (.xlsx, .xls, .csv) or PDF',
    'dash.templateDesc': 'Use columns: Name, Serial number, Colour, Texture, Price/m², B2B Tier 1-3, Dimensions, Weight',
    'dash.templateInfo': 'Template',
    'dash.texture': 'Texture',
    'dash.totalOrdered': 'Total Ordered',
    'dash.totalQuotes': 'Total Quotations',
    'dash.type': 'Type',
    'dash.weight': 'Weight',
    'dash.wholesaler': '🏭 Wholesaler',
    'dash.yourPrices': 'Your Wholesale Prices',
    'data.alt.customStones': 'Custom bricks',
    'data.color.Bont': 'Multicoloured',
    'data.color.Bruin': 'Brown',
    'data.color.Creme': 'Cream',
    'data.color.Geel': 'Yellow',
    'data.color.Grijs': 'Grey',
    'data.color.Rood': 'Red',
    'data.color.Taupe': 'Taupe',
    'data.color.Wit': 'White',
    'data.color.Zwart': 'Black',
    'data.supplier.italie': 'Manufacturer Italy',
    'data.supplier.polen': 'Manufacturer Poland',
    'data.supplier.spanje': 'Manufacturer Spain',
    'data.texture.Gesinterd': 'Sintered',
    'data.texture.Gewassen': 'Washed',
    'data.texture.Glad': 'Smooth',
    'data.texture.Ruw': 'Rough',
    'data.tier': 'Tier',
    'data.type.aannemer': 'Contractor',
    'data.type.architect': 'Architect',
    'data.type.groothandel': 'Wholesaler',
    'data.type.particulier': 'Private customer',
    'insp.allStyles': 'All styles',
    'insp.architect': 'Architect',
    'insp.desc': 'Browse completed projects using our brick slips. Filter by architectural style to find inspiration for your own project.',
    'insp.noResults': 'No projects found',
    'insp.noResultsDesc': 'Select a different style to view projects.',
    'insp.realisations': 'Projects',
    'insp.style.industrieel': 'Industrial',
    'insp.style.klassiek': 'Classic',
    'insp.style.landelijk': 'Rural',
    'insp.style.modern': 'Modern',
    'insp.title': 'Inspiration & projects',
    'login.demoAdmin': 'Admin',
    'login.demoCustomer': 'Customer',
    'login.demoTitle': 'Demo accounts:',
    'login.demoWholesaler': 'Wholesaler',
    'login.email': 'Email address',
    'login.emailExists': 'This email address is already registered.',
    'login.emailPlaceholder': 'your@email.be',
    'login.error': 'Invalid login details. Please try again.',
    'login.hasAccount': 'Already have an account? Log in',
    'login.invalidInput': 'Please fill in all fields correctly (password min. 4 characters).',
    'login.name': 'Name',
    'login.namePlaceholder': 'Your full name',
    'login.noAccount': 'No account yet? Register for free',
    'login.password': 'Password',
    'login.passwordHint': 'Min. 4 characters',
    'login.registerSubmit': 'Register',
    'login.registerSubtitle': 'Create a free customer account',
    'login.registerTitle': 'Create Account',
    'login.submit': 'Log in',
    'login.subtitle': 'Access to your B2B dashboard',
    'login.title': 'Log in',
    'products.3d.comingSoon': 'Coming soon',
    'products.3d.roofFlat': 'Flat',
    'products.3d.roofLabel': 'Roof',
    'products.3d.roofSlate': 'Slate',
    'products.3d.roofTiles': 'Tiles',
    'products.3d.roofToggle': 'Roof open',
    'products.3d.roofToggleClose': 'Roof closed',
    'products.3d.uploadCta': 'Coming soon: upload up to 5 photos and view your facade with the chosen brick slips in 3D.',
    'products.3d.uploadTeaser': 'Upload photos of your house',
    'reseller.quotes.addItem': 'Add product',
    'reseller.quotes.create': 'New quotation',
    'reseller.quotes.createTitle': 'Create quotation for customer',
    'reseller.quotes.fEmail': 'Customer email',
    'reseller.quotes.fIsB2B': 'Company with VAT number (B2B)',
    'reseller.quotes.fName': 'Customer name',
    'reseller.quotes.fNotes': 'Notes (optional)',
    'reseller.quotes.fPhone': 'Phone',
    'reseller.quotes.fPostal': 'Postcode',
    'reseller.quotes.items': 'Products',
    'reseller.quotes.selectProduct': 'Choose product…',
    'seo.architects.description': 'Download technical data sheets, Declarations of Performance (DoP), BIM files and installation guides for brick slips. All the documentation you need for your specification.',
    'seo.architects.keywords': 'brick slip technical data sheets, BIM files, declaration of performance, CE marking, architect documentation',
    'seo.architects.title': 'Architects & Specifiers — Technical Data Sheets | Gevelsteenbedrijf.be',
    'seo.calculator.description': 'Work out for free how many brick slips, adhesive, jointing mortar and corner pieces you need. Cutting waste included. Get an instant price indication.',
    'seo.calculator.keywords': 'brick slip calculator, calculate materials, how many brick slips, adhesive calculation, cutting waste',
    'seo.calculator.title': 'Material Calculator — Work Out Your Project | Gevelsteenbedrijf.be',
    'seo.contact.description': 'Questions about brick slips, pricing, delivery or installation? Send a message or call us directly. Schilde, Belgium — serving all of Flanders, Brussels and Wallonia.',
    'seo.contact.keywords': 'contact gevelsteenbedrijf, brick slips contact, facade specialist Schilde, facade renovation quotation, brick slips Antwerp contact',
    'seo.contact.title': 'Contact · Gevelsteenbedrijf.be — Premium Brick Slips Belgium',
    'seo.cookiePolicy.description': 'Our cookie policy and privacy statement. Information on cookies, data processing and your rights under the GDPR. Gevelsteenbedrijf.be.',
    'seo.cookiePolicy.keywords': 'cookie policy, privacy, GDPR, data protection, cookies',
    'seo.cookiePolicy.title': 'Cookie Policy & Privacy | Gevelsteenbedrijf.be',
    'seo.custom.description': 'Have brick slips made to order. Choose your own colour, texture and dimensions. Minimum order quantity 100 m². Lead time 6-8 weeks.',
    'seo.custom.keywords': 'custom brick slips, made-to-order brick slips, bespoke colour, bespoke texture, bespoke facade slips',
    'seo.custom.title': 'Technical Info & Custom Brick Slips | Gevelsteenbedrijf.be',
    'seo.dashboard.description': 'Manage your orders, quotations and customer details through the Gevelsteenbedrijf.be dashboard.',
    'seo.dashboard.keywords': 'dashboard, orders, quotations, customer management',
    'seo.dashboard.title': 'Dashboard — Gevelsteenbedrijf.be',
    'seo.login.description': 'Log in to the Gevelsteenbedrijf.be B2B portal. Access wholesale prices, orders and quotations.',
    'seo.login.keywords': 'B2B login, wholesale portal, gevelsteenbedrijf log in',
    'seo.login.title': 'Log In — B2B Portal | Gevelsteenbedrijf.be',
    'seo.quote.description': 'Request a no-obligation quotation for brick slips. Reply within 24 hours. Personal service from a local distributor in your area.',
    'seo.quote.keywords': 'brick slip quotation, facade slip price request, no-obligation quotation, order brick slips',
    'seo.quote.title': 'Request a Quotation — Free & Fast | Gevelsteenbedrijf.be',
    'seo.restpartijen.description': 'Premium brick slips from overstock and past collections — up to 50% cheaper. Limited quantities, frost-resistant, ready for delivery.',
    'seo.restpartijen.keywords': 'clearance brick slips, facade slips overstock, end-of-line bricks, brick discount offer, cheap brick slips, facade cladding outlet',
    'seo.restpartijen.title': 'Clearance Brick Slips · Up to 50% Off | Gevelsteenbedrijf.be',
    'seo.terms.description': 'Read our terms and conditions. Information on prices, delivery, right of withdrawal, warranty and more. Gevelsteenbedrijf.be — a Belgian company.',
    'seo.terms.keywords': 'terms and conditions, conditions of sale, brick slip warranty, right of withdrawal',
    'seo.terms.title': 'Terms & Conditions | Gevelsteenbedrijf.be',
    'switcher.empty': 'No test accounts found.',
    'switcher.error': 'Switching failed',
    'switcher.hint': '⚠️ All actions are recorded in the activity log. Log out to return to superadmin.',
    'switcher.loading': 'Loading accounts…',
    'switcher.subtitle': 'Switch to any account to see what they see',
    'switcher.title': 'Test Account Switcher',
    'switcher.toggle': 'Test as…',
    'data.product.p1.desc': 'Sleek elongated strip in warm taupe. Perfect for modern facade cladding with a soft, neutral look.',
    'data.product.p2.desc': 'Dark grey elongated strip with subtle texture variation. Delivers a bold, contemporary look.',
    'data.product.p3.desc': 'White elongated strip with a lightly structured surface. Timeless and elegant.',
    'data.product.p4.desc': 'Light grey elongated strip. Subtle and refined for sleek architectural projects.',
    'data.product.p5.desc': 'Blue-grey elongated strip with a cool undertone. A unique colour accent for modern facades.',
    'data.product.p6.desc': 'Deep black elongated strip for a powerful, exclusive facade. Maximum contrast.',
    'data.product.p7.desc': 'Rustic ivory-coloured strip with grey nuances and a rough surface. Authentic and warm.',
    'data.product.p8.desc': 'Bright white brick slip with a raw character. Scandinavian-inspired, timelessly sleek.',
    'data.product.p9.desc': 'Warm terracotta elongated strip with a rustic surface. Mediterranean character.',
    'data.product.p10.desc': 'Multicoloured brick slip with authentic stamps. Reclaimed character — every piece is unique.',
    'data.product.p11.desc': 'Soft pink-red brick slip with a weathered look. Classic Belgian brickwork.',
    'data.product.p12.desc': 'Dark anthracite-black elongated strip. Industrial and powerful.',
    'data.product.p13.desc': 'Warm terracotta brick slip with soft colour variation. Mediterranean charm.',
    'data.product.p14.desc': 'White elongated strip with a rough texture. Scandinavian style, modern and bright.',
    'data.product.p15.desc': 'Mixed Campine brick slip with red-brown shades. Authentically Flemish.',
    'data.product.p16.desc': 'Soft white brick slip with a rural character. Perfect for cottage-style homes.',
    'data.product.p17.desc': 'Grey-brown elongated strip with nuanced colour shades. Urban and bold.',
    'data.product.p18.desc': 'Warm rust-brown elongated strip. Rich colour with natural variation.',
    'data.product.p19.desc': 'Dark grey brick slip with deep character. Strong and timeless.',
    'data.product.p20.desc': 'Warm brown elongated strip with grey nuances. Bruges elegance.',
    'data.product.p21.desc': 'Soft cream-coloured brick slip with Provençal warmth. Light and elegant.',
    'data.product.p22.desc': 'Soft grey with subtle variation. A modern look with a touch of character.',
    'data.product.p23.desc': 'Classic red brick slip. The timeless choice for traditional Belgian facades.',
    'data.product.p24.desc': 'Deep black brick slip with a white joint. Dramatic contrast, exclusive design.',
    'data.product.p25.desc': 'Sand-coloured elongated strip with warm yellow undertones. Light and friendly.',
    'data.product.p26.desc': 'Light terracotta brick slip. Soft pink-red tones with Tuscan character.',
    'data.product.p27.desc': 'Mixed brick slip with a red-brown colour blend and dark accents. Truly Flemish.',
    'v6.toevoegen': '＋ Add',
    'v6.sluit': 'Close',
    'v6.nieuweSteen': '＋ New brick',
    'v6.3d': '🏠 3D',
    'v6.allesWissen': '🗑 Clear all',
    'v6.allesWissenBevestig': 'Clear all chosen bricks and settings and start over?',
    'v6.allesGewist': '🗑 Selection cleared — feel free to choose again',
    'v6.reedsInOfferte': 'Already in this quote',
    'v6.staatOpen': '● open above — adjust freely',
    'v6.instellingenPrijs': 'settings & price ▸',
    'v6.offerteAanvragen': 'Request quote',
    'v6.offerteMetBerekening': 'Request a quote with this calculation',
    'v6.totaal': 'Total',
    'v6.steen': 'brick',
    'v6.inDezeOfferte': 'in this quote',
    'v6.tipHoeken': 'Outer corners of your facade — usually 4.',
    'v6.tipHoogte': 'Height of one corner in metres — e.g. 2.5 m per storey. Corners × height = corner pieces.',
    'v6.tipSnijverlies': 'Fixed percentage — automatically included in the price.',
    'v6.termijnVraag': 'When are you planning this project?',
    'v6.t3m': '≤ 3 months',
    'v6.t6m': '≤ 6 months',
    'v6.t1j': 'This year',
    'v6.tLater': 'Later',
    'v6.tInfo': 'Info only',
    'v6.termijnFout': 'Select your planned timeframe — or choose "Info only".',
    'v6.plaatsingKop': '🔨 Installation',
    'v6.plaatsingVraag': 'Would you like a professional installer, or will you install yourself?',
    'v6.plOptInstall': 'I want an installer to do this for me',
    'v6.plOptInstallSub': 'We connect you with a professional in your region',
    'v6.plOptZelf': 'I will install myself',
    'v6.plOptZelfSub': 'No installer needed — materials only',
    'v6.plOptGh': 'Via my wholesaler',
    'v6.plOptGhSub': 'Purchase and installation via your own wholesaler',
    'v6.instVulPostcode': 'Enter your postcode + town above — we will show installers in your region (±50 km).',
    'v6.instZoeken': 'Searching for installers in your region…',
    'v6.instLijstIntro': 'Installers in your region — tick who may receive a copy of your quote (no obligation, you negotiate directly):',
    'v6.instGeen': 'No installers in your region right now — contact the manufacturer: we will help you personally after your request.',
    'v6.ghIntro': 'Your wholesaler (optional) — we will send them a copy of your quote:',
    'v6.ghNaam': 'Wholesaler name',
    'v6.ghGemeente': 'Town',
    'v6.ghEmail': 'Wholesaler e-mail (for the quote copy)',
    'v6.geldstroom': '💶 If you choose an installer or wholesaler, you settle directly with them — they order the materials from us. If you install yourself, you order and pay us in advance.',
    'v6.uwGegevens': 'Your details',
    'v6.bevestigGegevens': 'Confirm your details',
    'v6.sluiten': 'Close',
    'v6.uspFabrikantKop': 'Straight from the manufacturer',
    'v6.uspFabrikant': 'No middlemen — premium Belgian brick slips up to 40% cheaper, at fixed catalogue prices.',
    'v6.uspPlaatsingKop': 'Installation arranged',
    'v6.uspPlaatsing': 'Install yourself, via your wholesaler, or pick an installer from your region (±50 km) — you negotiate directly.',
    'v6.uspLeveringKop': 'Swift delivery',
    'v6.uspLevering': 'Lead time ±8 weeks, neatly packed to your site. Your quote stays valid for 30 days — adhesive and grout included.',
    'v6.inspiratieKop': 'Inspiration & projects',
    'v6.inspiratieSub': 'See finished facades with our bricks — choose with confidence.',
    'v6.kortingCta': 'Trader, contractor or installer?',
    'v6.kortingLink': 'Request your discount →',
    'pdf.cfg.stone': 'Brick',
    'pdf.cfg.stones': 'Bricks',
    'pdf.cfg.corners': 'Corners',
    'pdf.cfg.delivery': 'Delivery time',
    'pdf.cfg.groutColor': 'Grout colour',
    'pdf.cfg.color': 'Colour / texture',
    'calc.corners': 'Corners',
    'calc.cornerCount': 'Corner count',
    'calc.cornerHeight': 'Height per corner (m)',
    'calc.cuttingLoss': 'Cutting loss',
    'calc.includeGlue': 'Include adhesive',
    'calc.includeGrout': 'Include grout',
    'calc.yourCalc': 'Your calculation',
    'calc.brickStrips': 'Brick strips',
    'calc.cornerStones': 'Corner pieces',
    'calc.glue': 'Adhesive',
    'calc.bags': 'bags',
    'calc.grout': 'Grout',
    'calc.totalExcl': 'Total excl. VAT',
    'calc.inclVat': 'Incl. 21% VAT',
    'calc.quoteWithCalc': 'Get a quote with this calculation',
    'calc.saveQuote': 'Save quote',
    'calc.print': 'Print',
    'calc.savedSuccess': 'Quote saved successfully.',
    'calc.disclaimer': 'Indicative pricing. The final price may vary based on installation conditions and current market rates.',

    // QUOTE FORM
    'quote.title': 'Request a quote',
    'quote.desc': 'Get a personalised quote within 24 hours.',
    'quote.iAm': 'I am',
    'quote.private': 'Private',
    'quote.contractor': 'Contractor',
    'quote.architect': 'Architect',
    'quote.wholesaler': 'Wholesaler',
    'quote.name': 'Name',
    'quote.email': 'Email',
    'quote.phone': 'Phone',
    'quote.company': 'Company',
    'quote.postcode': 'Postcode',
    'quote.postcodeHint': 'For routing to your local installer',
    'quote.product': 'Product',
    'quote.selectProduct': 'Select a product',
    'quote.estimatedArea': 'Estimated area (m²)',
    'quote.areaHint': 'A rough estimate is fine',
    'quote.additionalInfo': 'Additional information',
    'quote.additionalInfoHint': 'Project details, timing, special requests…',
    'quote.contactInfo': 'Contact information',
    'quote.projectDetails': 'Project details',
    'quote.submit': 'Submit quote request',
    'quote.submitNote': 'We will contact you within 24 hours.',
    'quote.successTitle': 'Quote request received!',
    'quote.successDesc': 'We have received your request and will reply within 24 hours.',
    'quote.newRequest': 'New request',

    // PDF — invoice/quote labels
    'pdf.quote': 'QUOTE',
    'pdf.ref': 'Ref',
    'pdf.date': 'Date',
    'pdf.validUntil': 'Valid until',
    'pdf.client': 'Client',
    'pdf.status': 'Status',
    'pdf.product': 'Product',
    'pdf.configuration': 'Configuration',
    'pdf.requestDetails': 'Request details',
    'pdf.priceOverview': 'Price overview',
    'pdf.unitPriceM2': 'UNIT PRICES PER M²',
    'pdf.totalsForQuote': 'Totals for this quotation',
    'pdf.installAddressLabel': 'Site address / Installation',
    'pdf.installAddressNote': 'Delivery and installation take place at this address — not at the billing address.',
    'pdf.description': 'Description',
    'pdf.amount': 'Amount',
    'pdf.subtotalExcl': 'Subtotal excl. VAT',
    'pdf.vat21': 'VAT (21%)',
    'pdf.totalIncl': 'Total incl. VAT',
    'pdf.totalPrice': 'Total price',
    'pdf.vatIncluded': 'VAT (21%) included',
    'pdf.conditions': 'General terms',
    'pdf.cond1': 'Quote valid for 30 days from the date above',
    'pdf.cond2': 'Delivery: 2–4 weeks after confirmation',
    'pdf.cond3': 'Payment: 50% deposit, 50% on delivery',
    'pdf.cond4': 'Prices exclude installation unless stated otherwise',
    'pdf.poweredBy': 'Powered by Het Gevelsteenbedrijf',
    'pdf.installerNote': 'Authorised distributor & installer in cooperation with Het Gevelsteenbedrijf',
    // PDF — Delivery & installation block + installer-status (v4)
    'pdf.deliveryHeading': 'Delivery & installation',
    'pdf.deliveryNote1': 'Pickup, delivery or installation address will be confirmed after approval of this quote.',
    'pdf.deliveryNote2': 'For self-installation we only deliver the material; for installation via our network an installer will be assigned.',
    'pdf.installerSelfShort': 'Customer installs',
    'pdf.installerWantedShort': 'Installer via our network',
    // PDF — Configuration/price-breakdown labels (v4)
    'pdf.cfg.product': 'Product',
    'pdf.cfg.articleNr': 'Article number',
    'pdf.cfg.colorTexture': 'Color / Texture',
    'pdf.cfg.dimensions': 'Dimensions',
    'pdf.cfg.area': 'Surface',
    'pdf.cfg.jointWidth': 'Joint width',
    'pdf.cfg.jointColor': 'Joint color',
    'pdf.cfg.cuttingLoss': 'Cutting loss',
    'pdf.cfg.stripCount': 'Strip count',
    'pdf.cfg.cornerPieces': 'Corner pieces',
    'pdf.cfg.glue': 'Glue',
    'pdf.cfg.grout': 'Grout',
    'pdf.cfg.bricks': 'Bricks',
    'pdf.cfg.cornerStones': 'Corner stones',
    'pdf.unit.bags': 'bags',
    'pdf.unit.pieces': 'pcs',

    // RESELLER
    'reseller.title': 'Reseller Dashboard',
    'reseller.subtitle': 'Manage your profile, customers and quotes',
    'reseller.loading': 'Loading…',
    'reseller.accessDenied': 'Access denied',
    'reseller.accessDeniedDesc': 'This page is restricted to resellers. Sign in with a reseller account.',
    'reseller.tab.profile': 'Profile',
    'reseller.tab.catalog': 'Catalog',
    'reseller.tab.customers': 'My Customers',
    'reseller.tab.quotes': 'My Quotes',
    'reseller.profile.title': 'Company profile',
    'reseller.profile.ownerControlled': 'Managed by Het Gevelsteenbedrijf',
    'reseller.profile.ownerControlledNote': 'These fields are managed centrally. Contact the owner to request changes.',
    'reseller.profile.companyName': 'Company name',
    'reseller.profile.btwNumber': 'VAT number',
    'reseller.profile.discount': 'Default discount',
    'reseller.profile.postcodes': 'Service area (postcodes)',
    'reseller.profile.contactEmail': 'Contact email',
    'reseller.profile.contactPhone': 'Contact phone',
    'reseller.profile.address': 'Address',
    'reseller.profile.city': 'City',
    'reseller.profile.postalCode': 'Postcode',
    'reseller.catalog.title': 'Products with your prices',
    'reseller.catalog.desc': 'The catalog price is what the end customer sees. Your price is your purchase price after discount.',
    'reseller.catalog.empty': 'No products available.',
    'reseller.catalog.product': 'Product',
    'reseller.catalog.color': 'Colour',
    'reseller.catalog.category': 'Category',
    'reseller.catalog.catalogPrice': 'Catalog price',
    'reseller.catalog.yourPrice': 'Your price',
    'reseller.catalog.discount': 'Discount',
    'reseller.catalog.remnant': 'End-of-stock',
    'reseller.customers.title': 'My Customers',
    'reseller.customers.empty': 'No customers in your service area yet.',
    'reseller.customers.name': 'Name',
    'reseller.customers.email': 'Email',
    'reseller.customers.type': 'Type',
    'reseller.customers.city': 'City',
    'reseller.customers.quotes': 'Quotes',
    'reseller.customers.since': 'Since',
    'reseller.quotes.title': 'My Quotes',
    'reseller.quotes.empty': 'No quotes received yet.',

    // ADMIN STATUS
    'admin.status.draft': 'Draft',
    'admin.status.pending': 'Pending',
    'admin.status.sent': 'Sent',
    'admin.status.accepted': 'Accepted',
    'admin.status.approved': 'Approved',
    'admin.status.rejected': 'Rejected',
    'admin.status.expired': 'Expired',
    'admin.status.new': 'New',
    'admin.refresh': 'Refresh',

    // SEO
    'seo.home.title': 'Premium Brick Strips · Direct from Manufacturer | Gevelsteenbedrijf.be',
    'seo.home.description': 'Premium brick strips for facade cladding direct from the manufacturer. Save up to 40% — no middlemen. Belgian quality, delivered nationwide.',
    'seo.home.keywords': 'brick strips, facade cladding, premium bricks, belgian bricks, facade renovation',
    'seo.products.title': 'Brick Strips Catalog · 27 Premium Stones | Gevelsteenbedrijf.be',
    'seo.products.description': 'Browse 27 premium brick strips in modern, traditional, industrial and classic styles. Calculate your price live with the 3D house preview.',
    'seo.products.keywords': 'brick strips catalog, facade bricks, premium bricks, modern bricks, traditional bricks',
    'seo.inspiration.title': 'Facade Inspiration · Real Projects | Gevelsteenbedrijf.be',
    'seo.inspiration.description': 'Get inspired by real facade projects in modern, traditional, industrial and classic styles. Find the perfect brick for your project.',
    'seo.inspiration.keywords': 'facade inspiration, brick projects, facade design, brick examples',

    // ════════════════════════════════════════════════════════════════════════════
    // v3.2.110 — Volledige EN vertalingen voor Custom / Architecten /
    // Restpartijen / Contact. Eerder enkel NL fallback waardoor de
    // Custom-pagina ('Custom Gevelstenen'), Restpartijen en Contact
    // er voor EN-gebruikers Nederlands uitzagen.
    // ════════════════════════════════════════════════════════════════════════════

    // ARCHITECTS
    'arch.professionals':              'Professionals',
    'arch.title':                      'Architects & Specifiers',
    'arch.desc':                       'Download technical data sheets, declarations of performance and BIM files. All documentation you need for your specification.',
    'arch.generalDocs':                'General documentation',
    'arch.perProduct':                 'Documentation per product',
    'arch.techSpecs':                  'Technical specifications',
    'arch.doc1':                       'Technical Data Sheet — Full collection',
    'arch.doc2':                       'Declaration of Performance (DoP) — CE marking',
    'arch.doc3':                       'BIM Objects — Revit compatible',
    'arch.doc4':                       'Installation manual',
    'arch.doc5':                       'Fire resistance report — Class A1',
    'arch.doc6':                       'Frost resistance test — Class F2',
    'arch.techFiche':                  'Tech sheet',
    'arch.dop':                        'Declaration of Performance',
    'arch.bim':                        'BIM Object',
    'arch.spec.dimensions':            'Dimensions',
    'arch.spec.performance':           'Performance',
    'arch.spec.installation':          'Installation',
    'arch.spec.warranty':              'Warranty',
    'arch.spec.length':                'Length',
    'arch.spec.height':                'Height',
    'arch.spec.thickness':             'Thickness',
    'arch.spec.weight':                'Weight',
    'arch.spec.fireClass':             'Fire class',
    'arch.spec.fireValue':             'A1 (non-combustible)',
    'arch.spec.frost':                 'Frost resistance',
    'arch.spec.frostValue':            'Class F2',
    'arch.spec.water':                 'Water absorption',
    'arch.spec.waterValue':            '< 6%',
    'arch.spec.ce':                    'CE marking',
    'arch.spec.ceValue':               'EN 14411',
    'arch.spec.glue':                  'Adhesive',
    'arch.spec.glueValue':             '5 kg/m² (flexible tile adhesive)',
    'arch.spec.grout':                 'Grout',
    'arch.spec.groutValue':            '1.5 kg/m² (at 10 mm joint)',
    'arch.spec.loss':                  'Cutting loss',
    'arch.spec.lossValue':             '10% standard',
    'arch.spec.dryTime':               'Drying time',
    'arch.spec.dryValue':              '24–48 hours',
    'arch.spec.productWarranty':       'Product warranty',
    'arch.spec.productWarrantyVal':    '10 years',
    'arch.spec.colorFast':             'Colour fastness',
    'arch.spec.colorFastVal':          'UV resistant',
    'arch.spec.lifespan':              'Lifespan',
    'arch.spec.lifespanVal':           '> 50 years',
    'arch.spec.maintenance':           'Maintenance',
    'arch.spec.maintenanceVal':        'Maintenance-free',
    'arch.helpTitle':                  'Need help with your specification?',
    'arch.helpDesc':                   'Our technical team is glad to help you compile the right specifications for your project.',
    'arch.contactUs':                  'Get in touch',

    // CUSTOM
    'custom.exclusive':                'Exclusive',
    'custom.title':                    'Custom Brick Strips',
    'custom.desc':                     'Make your facade unique with custom-made brick strips. Choose your own colour, texture and finish — produced by our Polish manufacturer.',
    'custom.color.title':              'Custom colour',
    'custom.color.desc':               'Choose from an unlimited colour palette. We match any RAL colour or existing stone.',
    'custom.texture.title':            'Custom texture',
    'custom.texture.desc':             'From ultra-smooth to coarse rough, hand-moulded or industrial — any texture is possible.',
    'custom.size.title':               'Custom dimensions',
    'custom.size.desc':                'Other formats, thinner or thicker strips — everything is open to discussion for your project.',
    'custom.howTitle':                 'How does it work?',
    'custom.step1.title':              'Brief',
    'custom.step1.desc':               'Share your wishes: colour, texture, format and quantity.',
    'custom.step2.title':              'Sample',
    'custom.step2.desc':               'We produce a custom sample for your approval.',
    'custom.step3.title':              'Production',
    'custom.step3.desc':               'After approval we start production. Lead time: 6–8 weeks.',
    'custom.step4.title':              'Delivery',
    'custom.step4.desc':               'Your custom stones are delivered through a local distributor.',
    'custom.interestTitle':            'Interested in custom stones?',
    'custom.interestDesc':             'Get in touch for a no-obligation conversation about the options. Minimum order quantity: 100 m².',
    'custom.contact':                  'Get in touch',

    // RESTPARTIJEN (End-of-stock)
    'restpartijen.tag':                'End-of-stock · Temporary offer',
    'restpartijen.title':              'End-of-stock — discounts on last inventory',
    'restpartijen.desc':               'Real premium brick strips, previous collections or overstock from our manufacturers. Limited quantities, sharp prices — while supplies last.',
    'restpartijen.loading':            'Loading end-of-stock…',
    'restpartijen.empty':              'No end-of-stock available at this moment',
    'restpartijen.emptyDesc':          'Our inventory rotates regularly. Come back later or browse the full catalogue.',
    'restpartijen.backToCatalog':      'Back to catalogue',
    'restpartijen.available':          'Available',
    'restpartijen.interested':         'Interested in an end-of-stock lot?',
    'restpartijen.interestedDesc':     'Quantities are limited. Call or email to reserve or view — first come, first served.',
    'restpartijen.requestQuote':       'Request quote on these stones',
    'restpartijen.hero.badge':         'While supplies last · Limited inventory',
    'restpartijen.hero.title1':        'Premium brick strips,',
    'restpartijen.hero.title2':        'up to 50% cheaper',
    'restpartijen.hero.subtitle':      'End-of-stock, end-of-collection and surplus from our Italian, Polish and Spanish manufacturers. Same premium quality — fraction of the price. First come, first served.',
    'restpartijen.hero.maxDiscount':   'Max discount',
    'restpartijen.hero.available':     'Available',
    'restpartijen.hero.lots':          'Lots',
    'restpartijen.benefit1.title':     'Up to 50% off',
    'restpartijen.benefit1.desc':      'Premium brick at a fraction of the catalogue price. The lower the stock, the sharper the price.',
    'restpartijen.benefit2.title':     'Same premium quality',
    'restpartijen.benefit2.desc':      'Frost resistant (F2 class), colour-fast, 50+ years lifespan. No seconds or factory defects.',
    'restpartijen.benefit3.title':     'Fast delivery',
    'restpartijen.benefit3.desc':      'End-of-stock is already in our depot in Schilde — delivery within 5 working days across Belgium.',
    'restpartijen.benefit4.title':     'Unique to your project',
    'restpartijen.benefit4.desc':      'Previous collections that are no longer produced — exclusive to your facade, no one else has this anymore.',
    'restpartijen.list.title':         'Available end-of-stock',
    'restpartijen.list.subtitle':      'Browse our current offers. Stock is limited — reserved m² disappear immediately from this list.',
    'restpartijen.list.opOp':          'Out of stock',
    'restpartijen.list.endOfStock':    'End of stock',
    'restpartijen.list.savings':       'You save',
    'restpartijen.list.left':          'available',
    'restpartijen.list.reserve':       'Reserve / request info',
    'restpartijen.why.title':          'Why buy end-of-stock?',
    'restpartijen.why.1':              'Identical quality to our regular catalogue — no seconds, no cracks, no colour defects.',
    'restpartijen.why.2':              'Up to 50% cheaper than the same stone at normal catalogue price.',
    'restpartijen.why.3':              'Available immediately — no 4–6 weeks of waiting for factory production.',
    'restpartijen.why.4':              'Exclusive — previous collections no longer available elsewhere.',
    'restpartijen.why.5':              'Full warranty as on new products — 10-year material warranty.',
    'restpartijen.note.title':         'Important to know',
    'restpartijen.note.heading':       'Limited stock — no reorder possible',
    'restpartijen.note.body':          'End-of-stock is exactly that: last inventory. Reordering a match for an extension is usually no longer possible. Tip: always order 5–10% extra for cutting loss and any later repairs.',
    'restpartijen.recentSold.title':   'Recently sold',
    'restpartijen.recentSold.desc':    'What you missed — these end-of-stock lots have meanwhile been completely sold. Subscribe to the newsletter to be the first to know about new offers.',
    'restpartijen.recentSold.sticker': 'Sold',

    // CONTACT
    'contact.tag':                     'Get in touch',
    'contact.title':                   'Contact us',
    'contact.subtitle':                'Questions about a project, order or partnership? Send a message or call directly. We typically reply within 24 hours.',
    'contact.formTitle':               'Send a message',
    'contact.formDesc':                'Fields with * are required. Preferably also add your postcode so we can connect you immediately with our local installer.',
    'contact.fName':                   'Your name',
    'contact.fEmail':                  'Email',
    'contact.fPhone':                  'Phone',
    'contact.fPostal':                 'Postcode',
    'contact.fPostalHint':             'Helps us connect you with our local installer in your region.',
    'contact.fSubject':                'Subject',
    'contact.fMessage':                'Your message',
    'contact.consent':                 'I agree that my data will be used to answer my question, in line with the privacy policy. They will not be shared with third parties outside our installers in my region.',
    'contact.send':                    'Send message',
    'contact.sending':                 'Sending…',
    'contact.successTitle':            'Message sent',
    'contact.successDesc':             'Thank you — we will get back to you as soon as possible.',
    'contact.errorTitle':              'Sending failed',
    'contact.errorDesc':               'Something went wrong. Please try again or call us directly.',
    'contact.responseTimeTitle':       'Response time',
    'contact.responseTimeDesc':        'Messages are answered within 24 business hours. For urgent projects please call directly.',
    'contact.emailRequired':           'Email address is required.',
    'contact.emailFormat':             'Please enter a valid email (e.g. name@example.com).',
    'contact.emailDomain':             'This address looks like a test or disposable address. Please use a real email so we can reach you.',

    // ADMIN — Mijn Profiel sectie (v3.2.111 — EN nakijken op Filip's verzoek)
    'admin.dashboard':               'Dashboard',
    'admin.ownerDashboard':          'Owner Dashboard',
    'admin.loggedInAs':               'Signed in as',
    'admin.refresh':                  'Refresh',
    'admin.profile':                  'My Profile',
    'admin.profile.edit':             'Edit',
    'admin.profile.save':             'Save',
    'admin.profile.saving':           'Saving...',
    'admin.profile.cancel':           'Cancel',
    'admin.profile.success':          'Profile updated successfully',
    'admin.profile.name':             'Name',
    'admin.profile.email':            'Email',
    'admin.profile.phone':            'Phone',
    'admin.profile.company':          'Company name',
    'admin.profile.companyShort':     'Company',
    'admin.profile.btw':              'VAT number',
    'admin.profile.btwShort':         'VAT',
    'admin.profile.address':          'Address',
    'admin.profile.city':             'City',
    'admin.profile.postalCode':       'Postcode',

    // PRODUCT DISCOUNT MODAL (EN)
    'admin.products.discountFor':         'Discount for',
    'admin.products.discountHelp':        'Set up a temporary promo or liquidation campaign for this product. Visible on the product card on the website.',
    'admin.products.discountShow':        'Show standard discount',
    'admin.products.discountShowHelp':    'Shows a red discount pill on the product card. E.g. "Early booking" or "Promo".',
    'admin.products.discountPct':         'Discount %',
    'admin.products.discountMax':         'max 50%',
    'admin.products.discountMax50':       'Discount can be at most 50%.',
    'admin.products.discountLabel':       'Label',
    'admin.products.discountLabelPh':     'e.g. Promo, Early booking',
    'admin.products.discountSave':        'Save',
    'admin.products.discountDateInvalid': 'End date must be after start date.',
    'admin.products.liquidationToggle':   'Liquidation / end of season',
    'admin.products.liquidationHelp':     'Temporary amber badge with start and end date. Wins over the standard discount during the window.',
    'admin.products.liquidationPct':      'Liquidation %',
    'admin.products.liquidationLabel':    'Label',
    'admin.products.liquidationLabelPh':  'e.g. Liquidation, End of season',
    'admin.products.liquidationStart':    'Start date',
    'admin.products.liquidationEnd':      'End date',
    'admin.products.preset1month':        'Next month',
    'admin.products.preset1week':         'Next week',
    'admin.products.preview':             'Preview',
    'admin.products.catalogPrice':        'Catalog price',
    'admin.products.afterDiscount':       'After discount',

    'admin.stat.producten':           'Products · Prices',
    'admin.stat.leveranciers':        'Suppliers',
    'admin.stat.resellers':           'Resellers',
    'admin.stat.resellersSub':        'Wholesale partners',
    'admin.stat.klantenOffertes':     'Clients & Quotes',
    'admin.stat.klanten':             'clients',
    'admin.stat.offertes':            'quotes',
    'admin.stat.prijzen':             'Reseller Discounts',
    'admin.stat.prijzenSub':          'Discounts + pause per reseller',
    'admin.status.draft':             'Draft',
    'admin.status.pending':           'Pending',
    'admin.status.sent':              'Sent',
    'admin.status.accepted':          'Accepted',
    'admin.status.rejected':          'Rejected',
    'admin.status.expired':           'Expired',
    'admin.status.new':               'New',
  },

  // ─── POLISH (v3.2.60) — voor Bicek om te reviewen ──────────────────────────
  // Niet-vertaalde keys vallen automatisch terug op NL via translations['nl']
  // (zie de fallback-keten in t() onderaan). Deze block dekt nav, public pages,
  // klant-dashboard, reseller-dashboard, admin-dashboard, PDF en cookie banner.
  pl: {
    'seo.retour.description': 'Jak zwrócić płytki elewacyjne i kiedy otrzymasz zwrot pieniędzy: terminy, warunki i kroki w każdym przypadku.',
    'seo.retour.keywords': 'zwrot płytek elewacyjnych, refundacja, prawo odstąpienia, polityka zwrotów',
    'seo.retour.title': 'Zwroty i refundacje | Gevelsteenbedrijf.be',
    // NAV
    'nav.home': 'Strona główna',
    'nav.products': 'Produkty',
    'nav.calculator': 'Kalkulator',
    'nav.inspiration': 'Inspiracje i realizacje',
    'nav.architects': 'Architekci',
    'nav.custom': 'Na zamówienie',
    'nav.quote': 'Oferta',
    'nav.login': 'Zaloguj się',
    'nav.logout': 'Wyloguj się',
    'nav.dashboard': 'Panel',
    'nav.restpartijen': 'Końcówki serii',
    'nav.contact': 'Kontakt',
    'nav.requestQuote': 'Poproś o ofertę',

    // FOOTER
    'footer.desc': 'Specjalista w premium płytkach klinkierowych. Bezpośrednio od producenta do Twojego projektu, w najlepszym stosunku ceny do jakości.',
    'footer.belgian': 'Firma belgijska',
    'footer.nav': 'Nawigacja',
    'footer.professionals': 'Profesjonaliści',
    'footer.techSheets': 'Karty techniczne',
    'footer.customStones': 'Kamienie na zamówienie',
    'footer.wholesaleLogin': 'Logowanie hurtowe',
    'footer.b2bPricing': 'Zapytanie B2B',
    'footer.contact': 'Kontakt',
    'footer.rights': '© 2026 Gevelsteenbedrijf.be — Wszelkie prawa zastrzeżone',
    'footer.terms': 'Regulamin',
    'pdp.title.suffix': 'kup płytki ceglane',
    'pdp.metaDesc': 'płytki ceglane premium, prosto od producenta. Cena za m²:',
    'pdp.exVat': 'bez VAT',
    'pdp.spec.dimensions': 'Wymiary',
    'pdp.spec.color': 'Kolor',
    'pdp.spec.texture': 'Faktura',
    'pdp.spec.category': 'Styl',
    'pdp.spec.sku': 'Nr artykułu',
    'pdp.spec.delivery': 'Czas dostawy',
    'pdp.spec.weeks': 'tyg.',
    'pdp.usp1': 'Jakość identyczna z pełną cegłą elewacyjną — 10 lat gwarancji na materiał',
    'pdp.usp2': 'Prosto od producenta, bez pośredników',
    'pdp.usp3': 'Bezpłatna oferta z dokładnym obliczeniem materiału',
    'pdp.ctaCalc': 'Oblicz swój projekt z tą cegłą',
    'pdp.ctaFiche': 'Karta techniczna',
    'pdp.more': 'Odkryj nasze pozostałe cegły',
    'retour.title': 'Zwroty i refundacje',
    'retour.intro': 'Poniżej opisujemy zasady zwrotów i refundacji. Pytania? Skontaktuj się z nami — chętnie pomożemy.',
    'retour.s1.title': 'Prawo odstąpienia — 14 dni',
    'retour.s1.body': 'Jako konsument masz ustawowe prawo odstąpić od zakupu online w ciągu 14 dni kalendarzowych od dostawy, bez podania przyczyny. Zgłoś to e-mailem; od razu otrzymasz instrukcje zwrotu.',
    'retour.s2.title': 'Odesłanie towaru',
    'retour.s2.body': 'Odeślij produkty w ciągu 14 dni od zgłoszenia — nieuszkodzone, nieużywane i w oryginalnym opakowaniu na paletach. Koszty i ryzyko zwrotu ponosi kupujący, chyba że dostawa była błędna lub uszkodzona — wtedy my organizujemy i opłacamy odbiór.',
    'retour.s3.title': 'Refundacja — w ciągu 14 dni',
    'retour.s3.body': 'Zwracamy pełną kwotę zakupu (wraz z pierwotnymi kosztami dostawy) w ciągu 14 dni od otrzymania zwrotu lub dowodu nadania — w zależności od tego, co nastąpi wcześniej. Zwrot następuje tą samą metodą płatności.',
    'retour.s4.title': 'Wyjątki',
    'retour.s4.body': 'Końcówki serii sprzedawane są w stanie, w jakim się znajdują, i są wyłączone ze zwrotu (wyraźnie zaznaczone na stronie Końcówki serii). Cegły na zamówienie lub produkowane specjalnie również nie podlegają zwrotowi. Otwartych worków kleju lub fugi nie przyjmujemy.',
    'retour.s5.title': 'Uszkodzona lub błędna dostawa',
    'retour.s5.body': 'Sprawdź dostawę przy odbiorze i zgłoś widoczne uszkodzenia w ciągu 48 godzin wraz ze zdjęciami. Uszkodzone lub błędnie dostarczone produkty bezpłatnie wymienimy lub zwrócimy pieniądze — według Twojego wyboru.',
    'retour.btw': 'NIP/VAT',
    'footer.retour': 'Zwroty i refundacje',
    'calc.fixedLoss': '(stały — doliczany automatycznie)',
    'calc.stoneStrip': 'Nasze rodzaje cegieł — dotknij, aby zmienić',
    'data.insp.i5.title': 'Kamienica Antwerpia-Południe',
    'data.insp.i5.desc': 'Odnowiona kamienica w odcieniach szarości z wąskimi fugami — miejska i ponadczasowa.',
    'data.insp.i6.title': 'Dom energooszczędny Mechelen',
    'data.insp.i6.desc': 'Niemal zeroenergetyczny nowy dom z akcentami taupe i szaroniebieskimi na szczycie.',
    'data.insp.i7.title': 'Fermette w Pajottenland',
    'data.insp.i7.desc': 'Ciepła fermette z płytkami w kolorze kości słoniowej i terakoty, wtopiona w pagórkowaty krajobraz.',
    'data.insp.i8.title': 'Zagroda w Westhoek',
    'data.insp.i8.desc': 'Odbudowany dom wiejski z rustykalnymi czerwonymi płytkami i szeroką piaskową fugą.',
    'data.insp.i9.title': 'Magazyn Hasselt Kanaalkom',
    'data.insp.i9.desc': 'Przebudowany magazyn nad wodą, czarno-szare pasy w dzikim wiązaniu.',
    'data.insp.i10.title': 'Hala biurowa Kortrijk',
    'data.insp.i10.desc': 'Hala przekształcona w biura; ciemne płytki nadają bryle industrialny charakter.',
    'data.insp.i11.title': 'Kamienica mieszczańska Leuven',
    'data.insp.i11.desc': 'Kamienica w chronionym centrum, odrestaurowana z niuansowaną cegłą i białą fugą.',
    'data.insp.i12.title': 'Dom plebanijny Waasland',
    'data.insp.i12.desc': 'Klasyczny dom w stylu plebanii w ciepłej czerwieni i piaskowej żółci — flamandzka tradycja ceglana.',
    'data.insp.i1.title': 'Willa Kalmthout',
    'data.insp.i1.desc': 'Nowoczesna willa z białymi i czarnymi płytkami ceglanymi w leśnym otoczeniu.',
    'data.insp.i2.title': 'Renowacja zagrody Limburgia',
    'data.insp.i2.desc': 'Autentyczna renowacja limburskiej zagrody z czerwonymi i wielobarwnymi płytkami.',
    'data.insp.i3.title': 'Loft w centrum Gandawy',
    'data.insp.i3.desc': 'Industrialny loft w sercu Gandawy z szarymi i czarnymi akcentami.',
    'data.insp.i4.title': 'Willa Brugia',
    'data.insp.i4.desc': 'Klasyczna willa pod Brugią z ciepłymi brązowymi i piaskowymi płytkami.',
    'footer.privacy': 'Prywatność',
    'footer.cookies': 'Pliki cookie',
    'footer.location': 'Bredabaan 123, 2970 Schilde, Belgia',
    'footer.btw': 'NIP BE0123.456.789',

    // HOME
    'home.badge': 'Premium jakość belgijska',
    'home.heroTitle1': 'Nadaj swojej fasadzie',
    'home.heroTitle2': 'charakter',
    'home.heroDesc': 'Premium płytki klinkierowe bezpośrednio od producenta. Oszczędź do 40% dzięki naszemu modelowi sprzedaży bezpośredniej — bez pośredników.',
    'home.discoverCollection': 'Odkryj kolekcję',
    'home.calculator': 'Kalkulator',
    'home.stat.products': 'Produkty',
    'home.stat.projects': 'Projekty',
    'home.stat.savings': 'Oszczędność',
    'home.usp.factory': 'Bezpośrednio od producenta',
    'home.usp.delivery': 'Dostawa przez lokalnego dystrybutora',
    'home.usp.calc': 'Bezpłatne wyliczenie',
    'home.usp.custom': 'Możliwość personalizacji',
    'home.usp.warranty': 'Gwarancja w cenie',
    'home.model': 'Nasz model',
    'home.whyTitle': 'Dlaczego Gevelsteenbedrijf?',
    'home.whyDesc': 'Łączymy międzynarodowych producentów bezpośrednio z Tobą, poprzez sieć lokalnych dystrybutorów i instalatorów — najlepsza cena i osobista obsługa.',
    'home.model1.title': 'Międzynarodowi producenci',
    'home.model1.desc': 'Bezpośrednia współpraca z czołowymi producentami z Polski, Hiszpanii i Włoch dla najlepszej jakości i ceny.',
    'home.model2.title': 'Lokalni dystrybutorzy',
    'home.model2.desc': 'Twoje zamówienie zostanie dostarczone przez autoryzowanego dystrybutora w Twoim regionie, który może również polecić instalatora.',
    'home.model3.title': 'Gwarancja najlepszej ceny',
    'home.model3.desc': 'Dzięki bezpośredniej linii oszczędzasz do 40% w porównaniu z tradycyjnymi kanałami dystrybucji.',
    'home.collection': 'Kolekcja',
    'home.popularStrips': 'Popularne płytki klinkierowe',
    'home.viewAll': 'Zobacz wszystkie produkty →',
    'home.onlineTool': 'Narzędzie online',
    'home.calcTitle': 'Wylicz swój projekt w 2 minuty',
    'home.calcDesc': 'Nasz zaawansowany kalkulator dokładnie wyliczy ile płytek, kleju, fugi i narożników potrzebujesz — z uwzględnieniem strat na cięciu.',
    'home.openCalc': 'Otwórz kalkulator',
    'home.realisations': 'Realizacje',
    'home.inspiration': 'Inspiracje',
    'home.allProjects': 'Wszystkie projekty →',
    'home.private.title': 'Klienci indywidualni',
    'home.private.desc': 'Buduj lub remontuj wymarzony dom z płytkami klinkierowymi w cenach fabrycznych. Skorzystaj z kalkulatora i zamów ofertę — łączymy Cię z lokalnym dystrybutorem.',
    'home.private.cta': 'Wylicz swój projekt',
    'home.pro.title': 'Architekci i profesjonaliści',
    'home.pro.desc': 'Pobierz karty techniczne, deklaracje właściwości użytkowych i pliki BIM. Skorzystaj z naszego programu na zamówienie dla unikalnych projektów.',
    'home.pro.cta': 'Informacje techniczne',
    'home.ctaTitle': 'Gotowy by zacząć?',
    'home.ctaDesc': 'Poproś dziś o niezobowiązującą ofertę i odkryj ile możesz zaoszczędzić dzięki naszemu modelowi sprzedaży bezpośredniej.',
    'home.requestQuote': 'Poproś o ofertę',
    'home.viewCollection': 'Zobacz kolekcję',

    // PRODUCTS
    'products.collection': 'Kolekcja',
    'products.title': 'Nasze płytki klinkierowe',
    'products.desc': 'Odkryj nasz asortyment premium płytek klinkierowych. Filtruj według koloru i tekstury, aby znaleźć idealny kamień do Twojego projektu.',
    'products.search': 'Szukaj płytek klinkierowych...',
    'products.allColors': 'Wszystkie kolory',
    'products.allTextures': 'Wszystkie tekstury',
    'products.clearFilters': 'Wyczyść filtry',
    'products.found': 'znaleziono',
    'products.product': 'produkt',
    'products.products': 'produkty',
    'products.tapToPreview': 'Kliknij numer wybranego kamienia',
    'products.noResults': 'Nie znaleziono produktów',
    'products.noResultsDesc': 'Dostosuj filtry, aby znaleźć produkty.',
    'products.quote': 'Oferta',
    'products.calculate': 'Wylicz',
    'products.3d.title': 'Podgląd fasady 3D',
    'products.3d.rotate': 'Obróć i pochyl, aby zobaczyć dom z każdej strony',
    'products.3d.download': 'Pobierz PDF',
    'products.3d.materialsLabel': 'Wyliczenie materiałów',
    'products.3d.materialsWall': 'Powierzchnia fasady',
    'products.3d.materialsWindows': 'Otwory',
    'products.3d.materialsNet': 'Netto cegieł',
    'products.3d.gardenClick': 'Kliknij elementy ogrodu, aby uzyskać informacje',
    'products.3d.nightMode': 'Noc',
    'products.3d.dayMode': 'Dzień',
    'products.3d.dragHint': 'przeciągnij',

    // HERO + CALCULATOR INTRO (v3.2.102)
    'home.heroBadge':       'Premium belgijska jakość',
    'home.heroTitle':       'Nadaj swojej elewacji',
    'home.heroTitleAccent': 'charakter',
    'home.heroSubtitle':    'Płytki klinkierowe premium bezpośrednio od producenta. Oszczędź do 40% — bez pośredników.',
    'home.calcBadge':       'Kalkulator',
    'home.calcTitle':       'Oblicz swój projekt',
    'home.calcDesc':        'Wybierz produkt poniżej, wprowadź powierzchnię i otrzymaj natychmiast dokładne wyliczenie.',
    'home.savings':         'oszczędź',

    // CALCULATOR
    'calc.onlineTool': 'Narzędzie online',
    'calc.title': 'Kalkulator materiałów',
    'calc.desc': 'Wylicz dokładnie ile płytek klinkierowych, kleju, fugi i narożników potrzebujesz do swojego projektu.',
    'calc.productChoice': 'Wybór produktu',
    'calc.area': 'Powierzchnia',
    'calc.areaLabel': 'Powierzchnia netto (m²) — bez okien i drzwi',
    'calc.jointWidth': '↔️ Szerokość fugi',
    'calc.noJoint': 'Bez fugi',

    'v6.kiesSteen': 'Wybierz ten kamień — oblicz cenę',
    'cookie.functional.desc': 'Zapamiętują Twoje preferencje, np. język i wybrane produkty.',
    'cookie.functional.title': 'Funkcjonalne',
    'cookie.gpcDetected': 'Twoja przeglądarka wysyła sygnał Global Privacy Control. Automatycznie zaznaczyliśmy tylko niezbędne pliki cookie.',
    'cookie.savePreferences': 'Zapisz preferencje',
    'home.tagline': 'BE PREMIUM BELGIAN QUALITY',
    'admin.accepted': 'zaakceptowana',
    'admin.action': 'Akcja',
    'admin.backlog.activityDesc': 'Pełny przegląd wszystkich działań administracyjnych: logowania, oferty, zmiany statusu, przejęcia konta. Dowód zgodności.',
    'admin.backlog.activityEmpty': 'Brak zarejestrowanej aktywności.',
    'admin.backlog.activityTitle': 'Dziennik aktywności',
    'admin.backlog.cloudflareDesc': 'Oprócz tych eksportów CSV Cloudflare D1 tworzy również automatyczne kopie zapasowe point-in-time — w razie awarii można przywrócić stan z wcześniejszego momentu. Zarządzanie odbywa się w Cloudflare Dashboard → D1 → gevelstenen-db → Backups.',
    'admin.backlog.cloudflareTitle': 'Automatyczne kopie zapasowe w chmurze',
    'admin.backlog.deletedAt': 'Usunięto dnia',
    'admin.backlog.deletedDesc': 'Oferty w wersji roboczej, które zostały wyrzucone. Nic nie ginie bezpowrotnie — proszę kliknąć "Przywróć", aby je odzyskać.',
    'admin.backlog.deletedEmpty': 'Brak usuniętych ofert.',
    'admin.backlog.deletedTitle': 'Usunięte oferty (możliwe przywrócenie)',
    'admin.backlog.details': 'Szczegóły',
    'admin.backlog.exportCustomers': 'Klienci (CSV)',
    'admin.backlog.exportDesc': 'Proszę pobrać wszystkie swoje dane w formacie CSV. Warto przechowywać je offline lub w chmurze — to dodatkowe zabezpieczenie oprócz automatycznych kopii Cloudflare D1.',
    'admin.backlog.exportQuotes': 'Oferty (CSV)',
    'admin.backlog.exportTitle': 'Eksport / Kopia zapasowa',
    'admin.backlog.restore': 'Przywróć',
    'admin.backlog.restoreConfirm': 'Przywrócić tę ofertę do statusu wersji roboczej?',
    'admin.backlog.what': 'Akcja',
    'admin.backlog.when': 'Kiedy',
    'admin.backlog.who': 'Kto',
    'admin.bestseller': 'Bestseller',
    'admin.coOwner': 'Współwłaściciel',
    'admin.coOwner.sameData': 'Obaj właściciele widzą te same dane',
    'admin.company': 'Firma',
    'admin.customers': 'Klienci',
    'admin.customers.auditNote': 'Oferty są przechowywane na stałe i nie można ich usunąć. Wszystkie oferty są trwale archiwizowane i po wysłaniu nie mogą już zostać usunięte.',
    'admin.customers.clients': 'klienci',
    'admin.customers.excl': 'netto',
    'admin.customers.loadingQuotes': 'Wczytywanie ofert...',
    'admin.customers.noCustomers': 'Nie znaleziono klientów.',
    'admin.customers.noQuotesForCustomer': 'Brak ofert dla tego klienta.',
    'admin.customers.off': 'of.',
    'admin.customers.quotes': 'oferty',
    'admin.customers.quotesTitle': 'Oferty',
    'admin.customers.since': 'Od',
    'admin.customers.title': 'Klienci i oferty',
    'admin.customers.total': 'razem',
    'admin.date': 'Data',
    'admin.demoMode': 'Tryb demo — widoczne są tylko produkty',
    'admin.draft': 'wersja robocza',
    'admin.email': 'E-mail',
    'admin.exclVat': 'Bez VAT',
    'admin.filter': 'Filtr',
    'admin.inclVat': 'Z VAT',
    'admin.items': 'Pozycje',
    'admin.markup': 'Marża',
    'admin.name': 'Nazwa',
    'admin.noQuotes': 'Brak ofert',
    'admin.pdf.amount': 'Kwota',
    'admin.pdf.articleNumber': 'Numer artykułu',
    'admin.pdf.btwNumber': 'Numer VAT',
    'admin.pdf.client': 'Klient',
    'admin.pdf.clientType': 'Typ klienta',
    'admin.pdf.colorTexture': 'Kolor / Tekstura',
    'admin.pdf.company': 'Firma',
    'admin.pdf.cond1': 'Oferta ważna 30 dni od daty wystawienia',
    'admin.pdf.cond2': 'Termin dostawy: 2-4 tygodnie od potwierdzenia',
    'admin.pdf.cond3': 'Płatność: 50% zaliczki, 50% przy dostawie',
    'admin.pdf.cond4': 'Ceny nie obejmują montażu, o ile nie wskazano inaczej',
    'admin.pdf.conditions': 'Ogólne warunki',
    'admin.pdf.configuration': 'Konfiguracja',
    'admin.pdf.corners': 'Elementy narożne',
    'admin.pdf.cuttingLoss': 'Straty na docinaniu',
    'admin.pdf.date': 'Data',
    'admin.pdf.description': 'Opis',
    'admin.pdf.glue': 'Klej',
    'admin.pdf.grout': 'Zaprawa do fugowania',
    'admin.pdf.jointColor': 'Kolor fugi',
    'admin.pdf.jointWidth': 'Szerokość fugi',
    'admin.pdf.message': 'Wiadomość',
    'admin.pdf.postalCode': 'Kod pocztowy',
    'admin.pdf.priceOverview': 'Zestawienie cen',
    'admin.pdf.private': 'Osoba prywatna (B2C)',
    'admin.pdf.product': 'Produkt',
    'admin.pdf.quote': 'OFERTA',
    'admin.pdf.ref': 'Nr ref.',
    'admin.pdf.requestDetails': 'Dane zapytania',
    'admin.pdf.status': 'Status',
    'admin.pdf.stripCount': 'Liczba płytek',
    'admin.pdf.subtotalExclVat': 'Suma częściowa bez VAT',
    'admin.pdf.surface': 'Powierzchnia',
    'admin.pdf.totalInclVat': 'Razem z VAT',
    'admin.pdf.validUntil': 'Ważna do',
    'admin.pdf.vat': 'VAT (21%)',
    'admin.phone': 'Telefon',
    'admin.price': 'Cena',
    'admin.pricing': 'Zarządzanie cenami',
    'admin.pricing.cancel': 'Anuluj',
    'admin.pricing.edit': 'Edytuj',
    'admin.pricing.save': 'Zapisz',
    'admin.products': 'Produkty',
    'admin.products.weight': 'Waga',
    'admin.quotes': 'Oferty',
    'admin.rejected': 'odrzucona',
    'admin.resellers.deactivate': 'Dezaktywuj (dane zostają zachowane)',
    'admin.resellers.notes': 'Notatki wewnętrzne',
    'admin.role': 'Rola',
    'admin.save': 'Zapisz',
    'admin.sellPrice': 'Cena sprzedaży',
    'admin.sent': 'wysłana',
    'admin.sort': 'Sortuj',
    'admin.stat.internTesten': 'Testy wewnętrzne',
    'admin.stat.offertesCount': 'Oferty',
    'admin.stat.prijsbeheer': 'Zarządzanie cenami',
    'admin.stat.testOffertes': 'Oferty testowe',
    'admin.status': 'Status',
    'admin.status.approved': 'Zatwierdzona',
    'admin.status.new': 'Nowa',
    'admin.status.pending': 'W trakcie realizacji',
    'admin.supplier': 'Dostawca',
    'admin.suppliers': 'Dostawcy',
    'admin.suppliers.cancel': 'Anuluj',
    'admin.suppliers.save': 'Zapisz',
    'admin.suppliers.saving': 'Zapisywanie...',
    'admin.tab.backlog': 'Backlog',
    'admin.tab.resellers': 'Resellerzy',
    'admin.tab.testOffertes': 'Oferty testowe',
    'admin.testQuotes.action': 'Akcja',
    'admin.testQuotes.archiveNote': 'Oferty w wersji roboczej można usuwać. Oferty wysłane, zaakceptowane lub odrzucone są trwale archiwizowane na potrzeby audytu.',
    'admin.testQuotes.autoAppear': 'Oferty złożone w tym miejscu pojawią się tutaj automatycznie.',
    'admin.testQuotes.changeStatus': 'Zmień status',
    'admin.testQuotes.client': 'Klient',
    'admin.testQuotes.date': 'Data',
    'admin.testQuotes.delete': 'Usuń',
    'admin.testQuotes.deleteConfirm': 'Czy na pewno chce Pan/Pani usunąć tę ofertę? Tej operacji nie można cofnąć.',
    'admin.testQuotes.deleteOnlyDraft': 'Usuwać można wyłącznie oferty w wersji roboczej. Wysłane oferty pozostają w archiwum.',
    'admin.testQuotes.deleteTitle': 'Usuń ofertę',
    'admin.testQuotes.deleted': 'Oferta usunięta.',
    'admin.testQuotes.desc': 'Tutaj jako właściciel może Pan/Pani przeglądać testowe oferty i nimi zarządzać. Proszę zasymulować to, co zobaczyłby klient.',
    'admin.testQuotes.email': 'E-mail',
    'admin.testQuotes.exclVat': 'Bez VAT',
    'admin.testQuotes.goToPrefix': 'Proszę przejść na ',
    'admin.testQuotes.goToQuotePage': 'stronę ofert',
    'admin.testQuotes.goToSuffix': ', aby utworzyć testową ofertę jako klient.',
    'admin.testQuotes.inclVat': 'Z VAT',
    'admin.testQuotes.items': 'Pozycje',
    'admin.testQuotes.noQuotes': 'Nie znaleziono ofert.',
    'admin.testQuotes.status': 'Status',
    'admin.testQuotes.title': 'Oferty testowe (wewnętrzne)',
    'calc.area': 'Powierzchnia',
    'calc.areaLabel': 'Powierzchnia netto (m²) — bez okien i drzwi',
    'calc.desc': 'Proszę dokładnie obliczyć, ile płytek ceglanych, kleju, zaprawy do fugowania i elementów narożnych potrzeba do Państwa projektu.',
    'calc.fixedLoss': '(stałe — wliczone automatycznie)',
    'calc.jointWidth': '↔️ Szerokość fugi',
    'calc.noJoint': 'Bez fugi',
    'calc.onlineTool': 'Narzędzie online',
    'calc.productChoice': 'Wybór produktu',
    'calc.stoneStrip': 'Nasze rodzaje cegieł — proszę dotknąć, aby zmienić',
    'calc.swipeHint': 'Proszę przesunąć palcem, aby zobaczyć więcej cegieł',
    'calc.title': 'Kalkulator materiałów',
    'customer.btwAccount': 'Konto z obowiązkiem VAT',
    'customer.business': 'Firmowy',
    'customer.data.address': 'Adres',
    'customer.data.btw': 'Numer VAT',
    'customer.data.cancel': 'Anuluj',
    'customer.data.city': 'Miasto',
    'customer.data.company': 'Nazwa firmy',
    'customer.data.companyLabel': 'Firma',
    'customer.data.edit': 'Edytuj',
    'customer.data.email': 'E-mail',
    'customer.data.name': 'Imię i nazwisko',
    'customer.data.notes': 'Uwagi',
    'customer.data.phone': 'Telefon',
    'customer.data.postalCode': 'Kod pocztowy',
    'customer.data.save': 'Zapisz',
    'customer.data.saveFailed': 'Zapisywanie nie powiodło się.',
    'customer.data.saved': 'Dane zapisane',
    'customer.data.saving': 'Zapisywanie...',
    'customer.data.title': 'Moje dane',
    'customer.favorites.browse': 'Zobacz katalog',
    'customer.favorites.desc': 'Produkty oznaczone przez Państwa. Filip oraz dystrybutor w Państwa regionie widzą te preferencje przy sporządzaniu oferty.',
    'customer.favorites.empty': 'Brak ulubionych',
    'customer.favorites.emptyDesc': 'Proszę oznaczyć produkty serduszkiem w katalogu, aby znaleźć je tutaj.',
    'customer.favorites.loading': 'Wczytywanie ulubionych…',
    'customer.favorites.remove': 'Usuń z ulubionych',
    'customer.photos.ruleMax': 'Maksymalnie {n} zdjęć na konto',
    'customer.photos.ruleResolution': 'Zalecane: co najmniej 1024×768 pikseli; zdjęcia są automatycznie pomniejszane w celu optymalnego wyświetlania',
    'customer.photos.ruleType': 'Dozwolone: JPG, PNG, WebP',
    'customer.photos.rulesTitle': 'Zasady przesyłania',
    'customer.private': 'Klient indywidualny',
    'customer.quotes.goToCalc': 'Do kalkulatora',
    'customer.quotes.newCalc': 'Nowe obliczenie',
    'customer.quotes.noQuotes': 'Brak ofert',
    'customer.quotes.noQuotesDesc': 'Prosimy skorzystać z naszego kalkulatora, aby zamówić pierwszą ofertę.',
    'customer.quotes.pdfGenerating': 'Trwa generowanie pliku PDF',
    'customer.quotes.priceInclVat': 'Cena z VAT',
    'customer.quotes.subtotal': 'Suma częściowa',
    'customer.quotes.title': 'Moje oferty',
    'customer.quotes.totalInclVat': 'Razem z VAT',
    'customer.quotes.vat': 'VAT',
    'customer.status.accepted': 'Zaakceptowana',
    'customer.status.draft': 'Wersja robocza',
    'customer.status.expired': 'Wygasła',
    'customer.status.rejected': 'Odrzucona',
    'customer.status.sent': 'Wysłana',
    'dash.active': 'Aktywny',
    'dash.activeWholesalers': 'Aktywne hurtownie',
    'dash.addProduct': 'Dodaj produkt',
    'dash.address': 'Adres',
    'dash.admin': '👑 Administrator',
    'dash.analytics': '📈 Analityka',
    'dash.calculations': 'Obliczenia',
    'dash.cancel': 'Anuluj',
    'dash.category': 'Kategoria',
    'dash.chooseFile': 'Wybierz plik',
    'dash.client': 'Klient',
    'dash.color': 'Kolor',
    'dash.comingSoon': 'Wkrótce dostępne',
    'dash.comingSoonDesc': 'Ten moduł jest obecnie rozwijany i będzie dostępny wkrótce.',
    'dash.conversionRate': 'Współczynnik konwersji',
    'dash.corners': 'elementy narożne',
    'dash.customer': '👤 Klient',
    'dash.customers': '👥 Klienci',
    'dash.delete': 'Usuń',
    'dash.description': 'Opis',
    'dash.descriptionPlaceholder': 'Prosimy opisać produkt...',
    'dash.dimensions': 'Wymiary',
    'dash.downloadPdf': 'Pobierz PDF',
    'dash.dragDrop': 'Prosimy przeciągnąć plik tutaj lub kliknąć, aby przesłać',
    'dash.edit': 'Edytuj',
    'dash.editProduct': 'Edytuj produkt',
    'dash.exclusiveContract': 'Umowa na wyłączność',
    'dash.favorites': 'Ulubione',
    'dash.future': 'Przyszły',
    'dash.glueBags': 'worki kleju',
    'dash.goToCalc': 'Przejdź do kalkulatora',
    'dash.groutBags': 'worki zaprawy do fugowania',
    'dash.importProducts': 'Importuj',
    'dash.inProgress': 'W trakcie realizacji',
    'dash.inTalks': 'W trakcie rozmów',
    'dash.myDiscount': 'Mój rabat',
    'dash.myOrders': 'Moje zamówienia',
    'dash.myPrices': '💰 Moje ceny',
    'dash.myQuotes': '📄 Moje oferty',
    'dash.new': 'Nowy',
    'dash.newProduct': '+ Nowy produkt',
    'dash.newQuote': 'Nowe obliczenie',
    'dash.noQuotes': 'Brak zapisanych ofert',
    'dash.noQuotesDesc': 'Prosimy wykonać obliczenie w kalkulatorze i zapisać je jako ofertę.',
    'dash.openQuotes': 'Otwarte oferty',
    'dash.orders': '📋 Zamówienia',
    'dash.overview': '📊 Przegląd',
    'dash.phone': 'Telefon',
    'dash.planned': 'Zaplanowany',
    'dash.potential': 'Potencjalny',
    'dash.pricing': 'Ceny',
    'dash.product': 'Produkt',
    'dash.productMgmt': '📦 Zarządzanie produktami',
    'dash.productName': 'Nazwa produktu',
    'dash.quoteSent': 'Oferta wysłana',
    'dash.recentQuotes': 'Ostatnie zapytania ofertowe',
    'dash.retailPrice': 'Cena sprzedaży/m²',
    'dash.revenueMonth': 'Obrót w tym miesiącu',
    'dash.saveChanges': 'Zapisz zmiany',
    'dash.searchCustomer': 'Szukaj klienta...',
    'dash.serialNumber': 'Numer seryjny',
    'dash.status': 'Status',
    'dash.stock': 'Stan magazynowy',
    'dash.supplier': 'Dostawca',
    'dash.supplierCode': 'Kod dostawcy',
    'dash.supplierMgmt': 'Zarządzanie dostawcami',
    'dash.suppliers': '🚚 Dostawcy',
    'dash.supportedFormats': 'Obsługiwane formaty: Excel (.xlsx, .xls, .csv) lub PDF',
    'dash.templateDesc': 'Prosimy użyć kolumn: Nazwa, Numer seryjny, Kolor, Faktura, Cena/m², B2B Tier 1-3, Wymiary, Waga',
    'dash.templateInfo': 'Szablon',
    'dash.texture': 'Faktura',
    'dash.totalOrdered': 'Łącznie zamówiono',
    'dash.totalQuotes': 'Łączna liczba ofert',
    'dash.type': 'Typ',
    'dash.weight': 'Waga',
    'dash.wholesaler': '🏭 Hurtownia',
    'dash.yourPrices': 'Państwa ceny hurtowe',
    'data.alt.customStones': 'Cegły na wymiar',
    'data.color.Bont': 'Melanż',
    'data.color.Bruin': 'Brązowy',
    'data.color.Creme': 'Kremowy',
    'data.color.Geel': 'Żółty',
    'data.color.Grijs': 'Szary',
    'data.color.Rood': 'Czerwony',
    'data.color.Taupe': 'Taupe',
    'data.color.Wit': 'Biały',
    'data.color.Zwart': 'Czarny',
    'data.insp.i1.desc': 'Nowoczesna willa o surowej bryle z białymi i czarnymi płytkami elewacyjnymi, w otoczeniu lasu.',
    'data.insp.i1.title': 'Willa Kalmthout',
    'data.insp.i10.desc': 'Hala przekształcona w biura; ciemne płytki nadają bryle industrialny charakter.',
    'data.insp.i10.title': 'Hala biurowa Kortrijk',
    'data.insp.i11.desc': 'Kamienica mieszczańska w strefie ochrony konserwatorskiej, odrestaurowana w zniuansowanym wątku z białą fugą.',
    'data.insp.i11.title': 'Kamienica mieszczańska Leuven',
    'data.insp.i12.desc': 'Klasyczna plebania w ciepłej czerwieni i piaskowej żółci — flamandzka tradycja w płytkach elewacyjnych.',
    'data.insp.i12.title': 'Plebania w regionie Waasland',
    'data.insp.i2.desc': 'Autentyczna renowacja limburskiej zagrody z czerwonymi i brązowymi płytkami ceglanymi.',
    'data.insp.i2.title': 'Renowacja zagrody w Limburgii',
    'data.insp.i3.desc': 'Industrialny projekt loftu w samym sercu Gandawy z szarymi i czarnymi akcentami.',
    'data.insp.i3.title': 'Loft w centrum Gandawy',
    'data.insp.i4.desc': 'Klasyczna willa w pobliżu Brugii z ciepłymi brązowymi i złocistożółtymi płytkami elewacyjnymi.',
    'data.insp.i4.title': 'Willa Brugia',
    'data.insp.i5.desc': 'Wyremontowany dom szeregowy w odcieniach szarości z wąskimi fugami — miejski i ponadczasowy.',
    'data.insp.i5.title': 'Dom szeregowy Antwerpia-Południe',
    'data.insp.i6.desc': 'Nowy budynek niemal zeroenergetyczny z akcentami taupe i szaroniebieskimi na szczycie.',
    'data.insp.i6.title': 'Dom niemal zeroenergetyczny w Mechelen',
    'data.insp.i7.desc': 'Przytulny domek wiejski z płytkami w kolorze kości słoniowej i terakoty, w harmonii z pagórkowatym krajobrazem.',
    'data.insp.i7.title': 'Domek wiejski w Pajottenland',
    'data.insp.i8.desc': 'Odbudowana zagroda z rustykalnymi czerwonymi płytkami i szeroką piaskową fugą.',
    'data.insp.i8.title': 'Zagroda w regionie Westhoek',
    'data.insp.i9.desc': 'Magazyn nad wodą po konwersji, czarne pasy i zniuansowana szarość w dzikim wątku.',
    'data.insp.i9.title': 'Magazyn Hasselt Kanaalkom',
    'data.supplier.italie': 'Producent Włochy',
    'data.supplier.polen': 'Producent Polska',
    'data.supplier.spanje': 'Producent Hiszpania',
    'data.texture.Gesinterd': 'Spiekana',
    'data.texture.Gewassen': 'Płukana',
    'data.texture.Glad': 'Gładka',
    'data.texture.Ruw': 'Szorstka',
    'data.tier': 'Poziom',
    'data.type.aannemer': 'Firma budowlana',
    'data.type.architect': 'Architekt',
    'data.type.groothandel': 'Hurtownia',
    'data.type.particulier': 'Klient indywidualny',
    'footer.b2bPricing': 'Ceny B2B',
    'footer.belgian': 'Firma belgijska',
    'footer.btw': 'VAT BE0123.456.789',
    'footer.contact': 'Kontakt',
    'footer.cookies': 'Pliki cookie',
    'footer.customStones': 'Cegły na wymiar',
    'footer.desc': 'Państwa specjalista od płytek elewacyjnych klasy premium. Bezpośrednio z fabryki do Państwa projektu, w najlepszym stosunku jakości do ceny.',
    'footer.location': 'Bredabaan 123, 2970 Schilde, Belgia',
    'footer.nav': 'Nawigacja',
    'footer.privacy': 'Prywatność',
    'footer.professionals': 'Profesjonaliści',
    'footer.retour': 'Zwroty i zwrot pieniędzy',
    'footer.rights': '© 2026 Gevelsteenbedrijf.be — Wszelkie prawa zastrzeżone',
    'footer.techSheets': 'Karty techniczne',
    'footer.terms': 'Ogólne warunki handlowe',
    'footer.wholesaleLogin': 'Logowanie dla hurtowni',
    'home.allProjects': 'Wszystkie projekty →',
    'home.badge': 'Belgijska jakość premium',
    'home.calcBadge': 'Kalkulator',
    'home.calcDesc': 'Prosimy wybrać produkt poniżej, podać powierzchnię i natychmiast otrzymać dokładne wyliczenie.',
    'home.calcTitle': 'Prosimy wybrać cegłę — obliczyć cenę',
    'home.calculator': 'Kalkulator',
    'home.collection': 'Kolekcja',
    'home.ctaDesc': 'Prosimy poprosić dziś o bezpłatną ofertę i przekonać się, ile mogą Państwo zaoszczędzić dzięki naszemu modelowi dostawy bezpośredniej.',
    'home.ctaTitle': 'Gotowi, aby zacząć?',
    'home.discoverCollection': 'Poznaj kolekcję',
    'home.heroBadge': 'Belgijska jakość premium',
    'home.heroDesc': 'Płytki elewacyjne klasy premium prosto z fabryki. Oszczędność do 40% dzięki naszemu modelowi dostawy bezpośredniej — bez pośredników.',
    'home.heroSubtitle': 'Płytki elewacyjne klasy premium prosto od producenta. Oszczędność do 40% — bez pośredników.',
    'home.heroTitle': 'Nadaj',
    'home.heroTitle1': 'Nadaj swojej elewacji',
    'home.heroTitle2': 'charakteru',
    'home.heroTitleAccent': 'charakteru swojej elewacji',
    'home.inspiration': 'Inspiracje',
    'home.model': 'Nasz model',
    'home.model1.desc': 'Bezpośrednia współpraca z najlepszymi producentami z Polski, Hiszpanii i Włoch — najwyższa jakość w najlepszej cenie.',
    'home.model1.title': 'Producenci międzynarodowi',
    'home.model2.desc': 'Zamówienie dostarcza autoryzowany dystrybutor w Pana/Pani regionie, który poleci również montażystę.',
    'home.model2.title': 'Lokalni dystrybutorzy',
    'home.model3.desc': 'Dzięki bezpośredniej linii dostaw oszczędza Pan/Pani do 40% w porównaniu z tradycyjnymi kanałami sprzedaży.',
    'home.model3.title': 'Gwarancja najlepszej ceny',
    'home.onlineTool': 'Narzędzie online',
    'home.openCalc': 'Otwórz kalkulator',
    'home.popularStrips': 'Popularne płytki elewacyjne',
    'home.private.cta': 'Oblicz swój projekt',
    'home.private.desc': 'Buduj lub remontuj dom marzeń z płytkami premium w cenach fabrycznych. Skorzystaj z kalkulatora i poproś o ofertę — połączymy Pana/Panią z lokalnym dystrybutorem.',
    'home.private.title': 'Klienci indywidualni',
    'home.pro.cta': 'Informacje techniczne',
    'home.pro.desc': 'Pobierz karty techniczne, deklaracje właściwości użytkowych i pliki BIM. Skorzystaj z produkcji na wymiar dla wyjątkowych projektów.',
    'home.pro.title': 'Architekci i profesjonaliści',
    'home.realisations': 'Realizacje',
    'home.requestQuote': 'Poproś o ofertę',
    'home.savings': 'oszczędność',
    'home.stat.products': 'Produkty',
    'home.stat.projects': 'Projekty',
    'home.stat.savings': 'Oszczędność',
    'home.usp.calc': 'Bezpłatna kalkulacja',
    'home.usp.custom': 'Produkcja na wymiar',
    'home.usp.delivery': 'Dostawa przez lokalnego dystrybutora',
    'home.usp.factory': 'Bezpośrednio z fabryki',
    'home.usp.warranty': 'Gwarancja w cenie',
    'home.viewAll': 'Zobacz wszystkie produkty →',
    'home.viewCollection': 'Zobacz kolekcję',
    'home.whyDesc': 'Łączymy Pana/Panią bezpośrednio z międzynarodowymi producentami poprzez sieć lokalnych dystrybutorów i montażystów — najlepsza cena i osobista obsługa.',
    'home.whyTitle': 'Dlaczego Gevelsteenbedrijf?',
    'insp.noResults': 'Nie znaleziono projektów',
    'insp.noResultsDesc': 'Proszę wybrać inny styl, aby zobaczyć projekty.',
    'login.demoAdmin': 'Administrator',
    'login.demoCustomer': 'Klient',
    'login.demoTitle': 'Konta demo:',
    'login.demoWholesaler': 'Hurtownia',
    'login.emailExists': 'Ten adres e-mail jest już zarejestrowany.',
    'login.invalidInput': 'Proszę poprawnie wypełnić wszystkie pola (hasło min. 4 znaki).',
    'login.registerSubtitle': 'Załóż bezpłatne konto klienta',
    'login.registerTitle': 'Utwórz konto',
    'nav.architects': 'Architekci',
    'nav.calculator': 'Kalkulator',
    'nav.contact': 'Kontakt',
    'nav.custom': 'Informacje techniczne',
    'nav.dashboard': 'Panel',
    'nav.home': 'Strona główna',
    'nav.inspiration': 'Inspiracje i realizacje',
    'nav.login': 'Zaloguj się',
    'nav.logout': 'Wyloguj się',
    'nav.products': 'Produkty',
    'nav.quote': 'Oferta',
    'nav.requestQuote': 'Poproś o ofertę',
    'nav.restpartijen': 'Końcówki serii',
    'pdp.ctaCalc': 'Oblicz swój projekt z tą cegłą',
    'pdp.ctaFiche': 'Karta techniczna',
    'pdp.exVat': 'bez VAT',
    'pdp.metaDesc': 'płytki ceglane premium, bezpośrednio od producenta. Cena za m²:',
    'pdp.more': 'Poznaj nasze pozostałe cegły',
    'pdp.spec.category': 'Styl',
    'pdp.spec.color': 'Kolor',
    'pdp.spec.delivery': 'Termin dostawy',
    'pdp.spec.dimensions': 'Wymiary',
    'pdp.spec.sku': 'Nr katalogowy',
    'pdp.spec.texture': 'Faktura',
    'pdp.spec.weeks': 'tygodni',
    'pdp.title.suffix': 'kup płytki elewacyjne',
    'pdp.usp1': 'Jakość identyczna z cegłą pełną — 10 lat gwarancji na materiał',
    'pdp.usp2': 'Bezpośrednio od producenta, bez pośredników',
    'pdp.usp3': 'Bezpłatna oferta z dokładnym wyliczeniem materiałów',
    'products.3d.comingSoon': 'Wkrótce',
    'products.3d.dayMode': 'Dzień',
    'products.3d.download': 'Pobierz PDF',
    'products.3d.dragHint': 'przeciągnij',
    'products.3d.gardenClick': 'Kliknij elementy ogrodu, aby zobaczyć informacje',
    'products.3d.materialsLabel': 'Wyliczenie materiałów',
    'products.3d.materialsNet': 'Cegły netto',
    'products.3d.materialsWall': 'Łączna powierzchnia elewacji',
    'products.3d.materialsWindows': 'Otwory',
    'products.3d.nightMode': 'Noc',
    'products.3d.roofFlat': 'Płaski',
    'products.3d.roofLabel': 'Dach',
    'products.3d.roofSlate': 'Łupek',
    'products.3d.roofTiles': 'Dachówka',
    'products.3d.roofToggle': 'Dach otwarty',
    'products.3d.roofToggleClose': 'Dach zamknięty',
    'products.3d.rotate': 'Obracaj i pochylaj, aby zobaczyć dom ze wszystkich stron',
    'products.3d.title': 'Podgląd elewacji 3D',
    'products.3d.uploadCta': 'Wkrótce: prześlij maks. 5 zdjęć i zobacz swoją elewację z wybranymi płytkami w 3D.',
    'products.3d.uploadTeaser': 'Prześlij zdjęcia swojego domu',
    'products.allColors': 'Wszystkie kolory',
    'products.allTextures': 'Wszystkie faktury',
    'products.calculate': 'Oblicz',
    'products.clearFilters': 'Wyczyść filtry',
    'products.collection': 'Kolekcja',
    'products.desc': 'Poznaj naszą ofertę płytek elewacyjnych premium. Filtruj według koloru i faktury, aby znaleźć idealną cegłę do swojego projektu.',
    'products.found': 'znaleziono',
    'products.noResults': 'Nie znaleziono produktów',
    'products.noResultsDesc': 'Proszę zmienić filtry, aby znaleźć produkty.',
    'products.product': 'produkt',
    'products.products': 'produkty',
    'products.quote': 'Oferta',
    'products.search': 'Szukaj płytek...',
    'products.tapToPreview': 'Kliknij numer wybranej cegły',
    'products.title': 'Nasze płytki elewacyjne',
    'quote.additionalInfoHint': 'Proszę opisać projekt, szczególne życzenia, oczekiwany termin dostawy...',
    'quote.areaHint': 'np. 80',
    'quote.postcodeHint': 'np. 2000',
    'quote.submitNote': 'Zgłoszenie rozpatrzymy w ciągu 24 godzin. Pana/Pani dane traktujemy poufnie.',
    'retour.btw': 'VAT',
    'retour.intro': 'Tak działają zwroty i refundacje. Ma Pan/Pani pytania? Prosimy o kontakt — chętnie znajdziemy rozwiązanie.',
    'retour.s1.body': 'Jako konsument ma Pan/Pani ustawowe prawo odstąpienia od umowy w ciągu 14 dni kalendarzowych od dostawy, bez podania przyczyny. Wystarczy zgłoszenie e-mailem; instrukcję zwrotu prześlemy od razu.',
    'retour.s1.title': 'Prawo odstąpienia — 14 dni',
    'retour.s2.body': 'Prosimy odesłać produkty w ciągu 14 dni od zgłoszenia — nieuszkodzone, nieużywane i w oryginalnym opakowaniu na paletach. Koszty i ryzyko zwrotu ponosi kupujący, chyba że dostawa była błędna lub uszkodzona — wtedy odbiór organizujemy i opłacamy my.',
    'retour.s2.title': 'Odesłanie',
    'retour.s3.body': 'Pełną kwotę (wraz z pierwotnymi kosztami dostawy) zwracamy w ciągu 14 dni od otrzymania zwrotu lub od przedstawienia dowodu nadania — zależnie od tego, co nastąpi wcześniej. Zwrot następuje tą samą metodą płatności.',
    'retour.s3.title': 'Zwrot pieniędzy — w ciągu 14 dni',
    'retour.s4.body': 'Końcówki serii sprzedawane są w stanie takim, jaki jest, i nie podlegają zwrotowi (jest to wyraźnie zaznaczone na stronie Końcówki serii). Cegły na wymiar lub produkowane specjalnie nie podlegają zwrotowi. Napoczętych worków kleju ani zaprawy do fugowania nie przyjmujemy z powrotem.',
    'retour.s4.title': 'Wyjątki',
    'retour.s5.body': 'Prosimy sprawdzić dostawę przy odbiorze i zgłosić widoczne uszkodzenia w ciągu 48 godzin wraz ze zdjęciami. Produkty uszkodzone lub błędne wymieniamy bezpłatnie albo zwracamy za nie pieniądze — wedle Pana/Pani wyboru.',
    'retour.s5.title': 'Dostawa uszkodzona lub błędna',
    'retour.title': 'Zwroty i refundacje',
    'seo.architects.description': 'Karty techniczne, deklaracje właściwości użytkowych (DoP), pliki BIM i instrukcje montażu płytek elewacyjnych. Komplet dokumentacji do specyfikacji przetargowej.',
    'seo.architects.keywords': 'karty techniczne płytek elewacyjnych, pliki BIM, deklaracja właściwości użytkowych, oznakowanie CE, dokumentacja dla architektów',
    'seo.architects.title': 'Architekci i projektanci — karty techniczne | Gevelsteenbedrijf.be',
    'seo.calculator.description': 'Oblicz bezpłatnie, ile płytek ceglanych, kleju, zaprawy do fugowania i elementów narożnych potrzebujesz. Z uwzględnieniem odpadu z docinania. Wycena orientacyjna od razu.',
    'seo.calculator.keywords': 'kalkulator płytek elewacyjnych, obliczanie materiału, ile płytek ceglanych, wyliczenie kleju, odpad z docinania',
    'seo.calculator.title': 'Kalkulator materiałów — oblicz swój projekt | Gevelsteenbedrijf.be',
    'seo.contact.description': 'Pytania o płytki elewacyjne, ceny, dostawę lub montaż? Napisz lub zadzwoń bezpośrednio. Schilde w Belgii — obsługujemy całą Flandrię, Brukselę i Walonię.',
    'seo.contact.keywords': 'kontakt gevelsteenbedrijf, kontakt płytki elewacyjne, wykonawca elewacji Schilde, oferta na renowację elewacji, płytki elewacyjne Antwerpia kontakt',
    'seo.contact.title': 'Kontakt · Gevelsteenbedrijf.be — płytki elewacyjne premium, Belgia',
    'seo.cookiePolicy.description': 'Nasza polityka plików cookie i informacja o prywatności. Cookies, przetwarzanie danych i Twoje prawa zgodnie z RODO. Gevelsteenbedrijf.be.',
    'seo.cookiePolicy.keywords': 'polityka cookies, prywatność, RODO, GDPR, ochrona danych, pliki cookie',
    'seo.cookiePolicy.title': 'Polityka cookies i prywatność | Gevelsteenbedrijf.be',
    'seo.custom.description': 'Płytki ceglane produkowane na wymiar: własny kolor, faktura i wymiary. Minimalne zamówienie 100 m². Termin realizacji 6-8 tygodni.',
    'seo.custom.keywords': 'płytki ceglane na wymiar, płytki elewacyjne na zamówienie, kolor na wymiar, faktura na wymiar, elewacja na zamówienie',
    'seo.custom.title': 'Informacje techniczne i płytki na wymiar | Gevelsteenbedrijf.be',
    'seo.dashboard.description': 'Zarządzaj zamówieniami, ofertami i danymi klientów w panelu Gevelsteenbedrijf.be.',
    'seo.dashboard.keywords': 'panel, zamówienia, oferty, obsługa klientów',
    'seo.dashboard.title': 'Panel — Gevelsteenbedrijf.be',
    'seo.home.description': 'Cegły i płytki elewacyjne premium prosto od producenta. Oszczędź do 40% na płytkach ceglanych i elementach narożnych. Bezpłatna kalkulacja i oferta. Belgijska firma z siecią lokalnych dystrybutorów.',
    'seo.home.keywords': 'cegła elewacyjna, kup cegłę elewacyjną, płytki ceglane, płytki elewacyjne, cegły, okładzina elewacyjna, płytki elewacyjne premium, Belgia, prosto od producenta, budowa, przebudowa, renowacja, nowy dom, murarstwo, fugowanie, dobudówka, stan surowy',
    'seo.home.title': 'Cegły i płytki elewacyjne — prosto od producenta | Gevelsteenbedrijf.be',
    'seo.inspiration.description': 'Zobacz zrealizowane projekty z naszymi płytkami elewacyjnymi. Elewacje nowoczesne, rustykalne, industrialne i klasyczne. Zainspiruj się przed własnym projektem.',
    'seo.inspiration.keywords': 'inspiracje płytki elewacyjne, renowacja elewacji, nowoczesna elewacja, elewacja rustykalna, industrialna, klasyczna',
    'seo.inspiration.title': 'Galeria inspiracji — zrealizowane projekty | Gevelsteenbedrijf.be',
    'seo.login.description': 'Zaloguj się do portalu B2B Gevelsteenbedrijf.be. Dostęp do cen hurtowych, zamówień i ofert.',
    'seo.login.keywords': 'logowanie B2B, portal hurtowy, logowanie gevelsteenbedrijf',
    'seo.login.title': 'Logowanie — portal B2B | Gevelsteenbedrijf.be',
    'seo.products.description': 'Poznaj nasz asortyment płytek elewacyjnych premium. Filtruj według koloru i faktury: czerwone, białe, brązowe, czarne, żółte i szare. Prosto od producenta w najlepszej cenie.',
    'seo.products.keywords': 'kolekcja płytek elewacyjnych, czerwone płytki ceglane, białe płytki ceglane, brązowe płytki elewacyjne, czarne płytki ceglane, murowanie, murarstwo, kup płytki elewacyjne',
    'seo.products.title': 'Kolekcja płytek elewacyjnych — wszystkie kolory i faktury | Gevelsteenbedrijf.be',
    'seo.quote.description': 'Poproś o niezobowiązującą ofertę na płytki elewacyjne. Odpowiedź w ciągu 24 godzin. Osobista obsługa przez lokalnego dystrybutora w Twoim regionie.',
    'seo.quote.keywords': 'oferta na płytki elewacyjne, zapytanie o cenę płytek, niezobowiązująca oferta, zamów płytki ceglane',
    'seo.quote.title': 'Zapytanie ofertowe — szybko i niezobowiązująco | Gevelsteenbedrijf.be',
    'seo.restpartijen.description': 'Płytki elewacyjne premium z nadwyżek magazynowych i poprzednich kolekcji — do 50% taniej. Ograniczone ilości, mrozoodporne, gotowe do wysyłki.',
    'seo.restpartijen.keywords': 'końcówki serii płytki ceglane, nadwyżki płytek elewacyjnych, cegła końcówka zapasu, promocja na cegłę, tanie płytki ceglane, outlet okładziny elewacyjnej',
    'seo.restpartijen.title': 'Końcówki serii płytek elewacyjnych · rabaty do 50% | Gevelsteenbedrijf.be',
    'seo.terms.description': 'Zapoznaj się z naszym regulaminem: ceny, dostawa, prawo odstąpienia, gwarancja i więcej. Gevelsteenbedrijf.be — firma belgijska.',
    'seo.terms.keywords': 'regulamin, ogólne warunki sprzedaży, gwarancja na płytki elewacyjne, prawo odstąpienia',
    'seo.terms.title': 'Regulamin i warunki sprzedaży | Gevelsteenbedrijf.be',
    'switcher.empty': 'Nie znaleziono kont testowych.',
    'switcher.error': 'Przełączenie nie powiodło się',
    'switcher.hint': '⚠️ Wszystkie działania są zapisywane w activity_log. Wyloguj się, aby wrócić do konta superadmin.',
    'switcher.loading': 'Wczytywanie kont…',
    'switcher.subtitle': 'Przełącz się na dowolne konto, aby zobaczyć, co widzi jego użytkownik',
    'switcher.title': 'Przełącznik kont testowych',
    'switcher.toggle': 'Testuj jako…',
    'data.product.p1.desc': 'Smukła wydłużona płytka w ciepłym kolorze taupe. Idealna do nowoczesnych elewacji o miękkim, neutralnym wyglądzie.',
    'data.product.p2.desc': 'Ciemnoszara wydłużona płytka z subtelną zmiennością faktury. Nadaje odważny, współczesny charakter.',
    'data.product.p3.desc': 'Biała wydłużona płytka o lekko strukturalnej powierzchni. Ponadczasowa i elegancka.',
    'data.product.p4.desc': 'Jasnoszara wydłużona płytka. Subtelna i wyrafinowana do nowoczesnych projektów architektonicznych.',
    'data.product.p5.desc': 'Niebiesko-szara wydłużona płytka o chłodnym odcieniu. Wyjątkowy akcent kolorystyczny nowoczesnych elewacji.',
    'data.product.p6.desc': 'Głęboko czarna wydłużona płytka dla mocnego, ekskluzywnego wyglądu elewacji. Maksymalny kontrast.',
    'data.product.p7.desc': 'Rustykalna płytka w kolorze kości słoniowej z szarymi niuansami i surową powierzchnią. Autentyczna i ciepła.',
    'data.product.p8.desc': 'Jasnobiała płytka ceglana o surowym charakterze. Skandynawska inspiracja, ponadczasowa prostota.',
    'data.product.p9.desc': 'Ciepła terakotowa wydłużona płytka o rustykalnej powierzchni. Śródziemnomorski charakter.',
    'data.product.p10.desc': 'Wielobarwna płytka ceglana z autentycznymi stemplami. Charakter cegły z odzysku — każda sztuka jest unikatowa.',
    'data.product.p11.desc': 'Miękka różowo-czerwona płytka ceglana o postarzonym wyglądzie. Klasyczne belgijskie murarstwo.',
    'data.product.p12.desc': 'Ciemna antracytowo-czarna wydłużona płytka. Industrialna i mocna.',
    'data.product.p13.desc': 'Ciepła terakotowa płytka ceglana o łagodnej zmienności koloru. Śródziemnomorski urok.',
    'data.product.p14.desc': 'Biała wydłużona płytka o surowej fakturze. Styl skandynawski, nowoczesny i jasny.',
    'data.product.p15.desc': 'Wielobarwna płytka ceglana z Kempen w czerwono-brązowych odcieniach. Autentycznie flamandzka.',
    'data.product.p16.desc': 'Miękka biała płytka ceglana o wiejskim charakterze. Idealna do domów w stylu cottage.',
    'data.product.p17.desc': 'Szaro-brązowa wydłużona płytka o zniuansowanych odcieniach. Miejska i wyrazista.',
    'data.product.p18.desc': 'Ciepła rdzawobrązowa wydłużona płytka. Bogaty kolor z naturalną zmiennością.',
    'data.product.p19.desc': 'Ciemnoszara płytka ceglana o głębokim charakterze. Mocna i ponadczasowa.',
    'data.product.p20.desc': 'Ciepła brązowa wydłużona płytka z szarymi niuansami. Elegancja Brugii.',
    'data.product.p21.desc': 'Miękka kremowa płytka ceglana z prowansalskim ciepłem. Lekka i elegancka.',
    'data.product.p22.desc': 'Miękka szarość z subtelną zmiennością. Nowoczesny wygląd z nutą charakteru.',
    'data.product.p23.desc': 'Klasyczna czerwona płytka ceglana. Ponadczasowy wybór do tradycyjnych belgijskich elewacji.',
    'data.product.p24.desc': 'Głęboko czarna płytka ceglana z białą fugą. Dramatyczny kontrast, ekskluzywny design.',
    'data.product.p25.desc': 'Wydłużona płytka w kolorze piasku o ciepłych żółtych podtonach. Jasna i przyjazna.',
    'data.product.p26.desc': 'Jasna terakotowa płytka ceglana. Delikatne różowo-czerwone odcienie o toskańskim charakterze.',
    'data.product.p27.desc': 'Wielobarwna płytka ceglana z czerwono-brązową mieszanką i ciemnymi akcentami. Prawdziwie flamandzka.',
    'v6.toevoegen': '＋ Dodaj',
    'v6.sluit': 'Zamknij',
    'v6.nieuweSteen': '＋ Nowy kamień',
    'v6.3d': '🏠 3D',
    'v6.allesWissen': '🗑 Wyczyść wszystko',
    'v6.allesWissenBevestig': 'Usunąć wszystkie wybrane kamienie i ustawienia i zacząć od nowa?',
    'v6.allesGewist': '🗑 Wybór wyczyszczony — wybierz ponownie',
    'v6.reedsInOfferte': 'Już w tej ofercie',
    'v6.staatOpen': '● otwarte powyżej — dostosuj swobodnie',
    'v6.instellingenPrijs': 'ustawienia i cena ▸',
    'v6.offerteAanvragen': 'Poproś o ofertę',
    'v6.offerteMetBerekening': 'Poproś o ofertę z tym obliczeniem',
    'v6.totaal': 'Razem',
    'v6.steen': 'kamień',
    'v6.inDezeOfferte': 'w tej ofercie',
    'v6.tipHoeken': 'Narożniki zewnętrzne elewacji — zwykle 4.',
    'v6.tipHoogte': 'Wysokość jednego narożnika w metrach — np. 2,5 m na kondygnację. Narożniki × wysokość = kształtki.',
    'v6.tipSnijverlies': 'Stały procent — automatycznie wliczony w cenę.',
    'v6.termijnVraag': 'Kiedy planujesz ten projekt?',
    'v6.t3m': '≤ 3 miesiące',
    'v6.t6m': '≤ 6 miesięcy',
    'v6.t1j': 'W tym roku',
    'v6.tLater': 'Później',
    'v6.tInfo': 'Tylko informacyjnie',
    'v6.termijnFout': 'Wskaż planowany termin — lub wybierz „Tylko informacyjnie”.',
    'v6.plaatsingKop': '🔨 Montaż',
    'v6.plaatsingVraag': 'Chcesz profesjonalnego montażystę, czy montujesz samodzielnie?',
    'v6.plOptInstall': 'Chcę, aby montażysta wykonał to za mnie',
    'v6.plOptInstallSub': 'Skontaktujemy Cię z fachowcem z Twojego regionu',
    'v6.plOptZelf': 'Montuję samodzielnie',
    'v6.plOptZelfSub': 'Bez montażysty — tylko materiał',
    'v6.plOptGh': 'Przez mojego hurtownika',
    'v6.plOptGhSub': 'Zakup i montaż przez własną hurtownię',
    'v6.instVulPostcode': 'Wpisz powyżej kod pocztowy + miejscowość — pokażemy montażystów z Twojego regionu (±50 km).',
    'v6.instZoeken': 'Szukanie montażystów w Twoim regionie…',
    'v6.instLijstIntro': 'Montażyści w Twoim regionie — zaznacz, kto może otrzymać kopię oferty (bez zobowiązań, negocjujesz sam):',
    'v6.instGeen': 'Obecnie brak montażystów w Twoim regionie — skontaktuj się z producentem: pomożemy osobiście po Twoim zapytaniu.',
    'v6.ghIntro': 'Twoja hurtownia (opcjonalnie) — wyślemy jej kopię oferty:',
    'v6.ghNaam': 'Nazwa hurtowni',
    'v6.ghGemeente': 'Miejscowość',
    'v6.ghEmail': 'E-mail hurtowni (dla kopii oferty)',
    'v6.geldstroom': '💶 Przy montażyście lub hurtowniku rozliczasz się bezpośrednio z nimi — oni zamawiają materiały u nas. Przy samodzielnym montażu zamawiasz i płacisz u nas z góry.',
    'v6.uwGegevens': 'Twoje dane',
    'v6.bevestigGegevens': 'Potwierdź swoje dane',
    'v6.sluiten': 'Zamknij',
    'v6.uspFabrikantKop': 'Prosto od producenta',
    'v6.uspFabrikant': 'Bez pośredników — belgijskie premium płytki ceglane do 40% taniej, w stałych cenach katalogowych.',
    'v6.uspPlaatsingKop': 'Montaż zorganizowany',
    'v6.uspPlaatsing': 'Montuj sam, przez hurtownię, lub wybierz montażystę z regionu (±50 km) — negocjujesz bezpośrednio.',
    'v6.uspLeveringKop': 'Sprawna dostawa',
    'v6.uspLevering': 'Termin ±8 tygodni, starannie zapakowane na budowę. Oferta ważna 30 dni — klej i fuga w zestawie.',
    'v6.inspiratieKop': 'Inspiracje i realizacje',
    'v6.inspiratieSub': 'Zobacz gotowe elewacje z naszymi kamieniami — wybieraj pewnie.',
    'v6.kortingCta': 'Handlowiec, wykonawca czy montażysta?',
    'v6.kortingLink': 'Poproś o rabat →',
    'pdf.cfg.stone': 'Kamień',
    'pdf.cfg.stones': 'Kamienie',
    'pdf.cfg.corners': 'Narożniki',
    'pdf.cfg.delivery': 'Czas dostawy',
    'pdf.cfg.groutColor': 'Kolor fugi',
    'pdf.cfg.color': 'Kolor / faktura',
    'calc.corners': 'Narożniki',
    'calc.cornerCount': 'Liczba narożników',
    'calc.cornerHeight': 'Wysokość narożnika (m)',
    'calc.accessories': 'Akcesoria',
    'calc.includeGlue': 'Uwzględnij klej',
    'calc.glueHint': '(~5 kg/m²)',
    'calc.includeGrout': 'Uwzględnij fugę',
    'calc.groutHint': '(zależnie od szerokości fugi)',
    'calc.groutColor': 'Kolor fugi',
    'calc.grey': 'szary',
    'calc.white': 'biały',
    'calc.anthracite': 'antracyt',
    'calc.sand': 'piaskowy',
    'calc.cuttingLoss': 'Straty na cięciu',
    'calc.cuttingLossHint': 'Standardowo 10%. Zwiększ przy złożonych fasadach.',
    'calc.yourCalc': 'Twoje wyliczenie',
    'calc.brickStrips': 'Płytki klinkierowe',
    'calc.pieces': 'szt.',
    'calc.cornerStones': 'Narożniki',
    'calc.glue': 'Klej',
    'calc.bags': 'worki po 25kg',
    'calc.grout': 'Fuga',
    'calc.totalExcl': 'Razem (bez VAT)',
    'calc.inclVat': 'Z 21% VAT',
    'calc.quoteWithCalc': 'Poproś o ofertę z tym wyliczeniem',
    'calc.print': 'Drukuj wyliczenie',
    'calc.saveQuote': 'Zapisz ofertę',
    'calc.savedSuccess': 'Oferta zapisana! Zobacz w panelu.',
    'calc.disclaimer': '* Ceny orientacyjne. Dokładna cena w Twojej osobistej ofercie.',

    // QUOTE
    'quote.title': 'Poproś o ofertę',
    'quote.desc': 'Poproś o niezobowiązującą ofertę. Łączymy Cię z lokalnym dystrybutorem w Twoim regionie.',
    'quote.iAm': 'Jestem...',
    'quote.private': 'Klient indywidualny',
    'quote.contractor': 'Wykonawca',
    'quote.architect': 'Architekt',
    'quote.wholesaler': 'Hurtownia',
    'quote.contactInfo': 'Dane kontaktowe',
    'quote.name': 'Imię i nazwisko',
    'quote.email': 'E-mail',
    'quote.phone': 'Telefon',
    'quote.postcode': 'Kod pocztowy',
    'quote.company': 'Nazwa firmy',
    'quote.projectDetails': 'Szczegóły projektu',
    'quote.product': 'Produkt',
    'quote.selectProduct': 'Wybierz produkt...',
    'quote.estimatedArea': 'Szacowana powierzchnia (m²)',
    'quote.additionalInfo': 'Dodatkowe informacje',
    'quote.submit': 'Poproś o ofertę →',
    'quote.successTitle': 'Oferta zamówiona!',
    'quote.successDesc': 'Dziękujemy za zapytanie. Skontaktujemy się w ciągu 24 godzin z osobistą ofertą.',
    'quote.newRequest': 'Nowe zapytanie',

    // INSPIRATION
    'insp.realisations': 'Realizacje',
    'insp.title': 'Galeria inspiracji',
    'insp.desc': 'Zobacz zrealizowane projekty z naszymi płytkami klinkierowymi.',
    'insp.allStyles': 'Wszystkie style',
    'insp.style.modern': 'Nowoczesny',
    'insp.style.landelijk': 'Wiejski',
    'insp.style.industrieel': 'Industrialny',
    'insp.style.klassiek': 'Klasyczny',
    'insp.architect': 'Architekt',

    // LOGIN
    'login.title': 'Logowanie',
    'login.subtitle': 'Dostęp do panelu B2B',
    'login.email': 'Adres e-mail',
    'login.emailPlaceholder': 'twoj@email.pl',
    'login.password': 'Hasło',
    'login.submit': 'Zaloguj się',
    'login.error': 'Nieprawidłowe dane logowania.',
    'login.name': 'Imię i nazwisko',
    'login.namePlaceholder': 'Twoje pełne imię i nazwisko',
    'login.passwordHint': 'Min. 4 znaki',
    'login.registerSubmit': 'Zarejestruj się',
    'login.hasAccount': 'Masz już konto? Zaloguj się',
    'login.noAccount': 'Nie masz konta? Zarejestruj się za darmo',

    // CUSTOMER DASHBOARD
    'customer.tab.myQuotes': 'Moje oferty',
    'customer.tab.myPhotos': 'Moje zdjęcia',
    'customer.tab.myData': 'Moje dane',
    'customer.tab.myFavorites': 'Ulubione',
    'customer.myQuotes': 'Moje oferty',
    'customer.myPhotos': 'Moje zdjęcia',
    'customer.myData': 'Moje dane',
    'customer.noQuotes': 'Brak ofert',
    'customer.uploadPhoto': 'Prześlij zdjęcie',
    'customer.favorites.title': 'Moje ulubione',
    'customer.photos.title': 'Moje zdjęcia projektowe',
    'customer.photos.count': 'zdjęć',
    'customer.photos.upload': 'Prześlij zdjęcia',
    'customer.photos.addPhoto': 'Dodaj zdjęcie',
    'customer.photos.noPhotos': 'Brak zdjęć',
    'customer.photos.noPhotosDesc': 'Prześlij zdjęcia swojego projektu (maks. 5).',
    'customer.photos.maxReached': 'Maksymalnie 5 zdjęć osiągnięte.',
    'customer.photos.errType': 'Nieobsługiwany format pliku',
    'customer.photos.errSize': 'Plik zbyt duży',
    'customer.photos.errTrimmed': 'Tylko {n} zdjęć dodano (limit osiągnięty)',
    'customer.photos.errGeneric': 'Wysyłanie nie powiodło się.',
    'customer.photos.projectPhoto': 'Zdjęcie projektowe',
    'customer.photos.ruleSize': 'Maks. {mb} MB na zdjęcie',

    // RESELLER DASHBOARD
    'reseller.title': 'Panel sprzedawcy',
    'reseller.subtitle': 'Twoja przestrzeń sprzedawcy',
    'reseller.loading': 'Ładowanie danych...',
    'reseller.accessDenied': 'Dostęp zabroniony',
    'reseller.accessDeniedDesc': 'Zaloguj się jako sprzedawca, aby zobaczyć panel.',
    'reseller.tab.profile': 'Mój profil',
    'reseller.tab.catalog': 'Katalog',
    'reseller.tab.customers': 'Klienci',
    'reseller.tab.quotes': 'Oferty',

    // ADMIN DASHBOARD
    'admin.accessDenied': 'Dostęp zabroniony',
    'admin.accessDeniedDesc': 'Zaloguj się jako właściciel lub superadmin.',
    'admin.dashboard': 'Panel',
    'admin.ownerDashboard': 'Panel właściciela',
    'admin.loggedInAs': 'Zalogowany jako',
    'admin.refresh': 'Odśwież',
    'admin.loading': 'Ładowanie danych...',
    'admin.apiFooter': 'Wszystkie dane przez bezpieczne endpointy admin.',

    // Admin tabs
    'admin.tab.producten': 'Produkty',
    'admin.tab.leveranciers': 'Dostawcy',
    'admin.tab.klanten': 'Klienci i oferty',
    'admin.tab.klantenOffertes': 'Klienci i oferty',
    'admin.tab.prijzen': 'Rabaty sprzedawców',
    'admin.tab.berichten': 'Wiadomości',

    // Admin stat
    'admin.stat.producten': 'Produkty · Ceny',
    'admin.stat.leveranciers': 'Dostawcy',
    'admin.stat.resellers': 'Sprzedawcy',
    'admin.stat.resellersSub': 'Dystrybutorzy',
    'admin.stat.klantenOffertes': 'Klienci i oferty',
    'admin.stat.klanten': 'klientów',
    'admin.stat.offertes': 'ofert',
    'admin.stat.prijzen': 'Rabaty sprzedawców',
    'admin.stat.prijzenSub': 'Zarządzanie cenami',

    // Admin products — common
    'admin.products.title': 'Produkty · Dostosowanie cen',
    'admin.products.name': 'Nazwa',
    'admin.products.supplier': 'Dostawca',
    'admin.products.purchase': 'Zakup',
    'admin.products.exclVat': 'Bez VAT',
    'admin.products.incl21': 'Z 21%',
    'admin.products.incl6': 'Z 6%',
    'admin.products.actions': 'Akcje',
    'admin.products.bestsellers': 'Bestsellery',
    'admin.products.optional': 'Opcjonalne',
    'admin.products.category': 'Kategoria',
    'admin.products.dimensions': 'Wymiary',
    'admin.products.color': 'Kolor',
    'admin.products.texture': 'Tekstura',
    'admin.products.serialNumber': 'Numer seryjny',
    'admin.products.ean': 'Kod EAN',
    'admin.products.fixedPrice': 'Cena stała',
    'admin.products.techSpecs': 'Specyfikacja techniczna',
    'admin.products.specs': 'Specyf.',
    'admin.products.editProduct': 'Edytuj produkt',
    'admin.products.save': 'Zapisz',
    'admin.products.saving': 'Zapisywanie...',
    'admin.products.cancel': 'Anuluj',
    'admin.products.edit': 'Edytuj',
    'admin.products.discountManage': 'Zarządzaj rabatem',
    'admin.products.filterSupplier': 'Filtruj według dostawcy',
    'admin.products.showAll': 'Pokaż wszystkie',

    // v3.2.49+ — AKP semantics
    'admin.products.priceM2Badge': 'Ceny / m²',
    'admin.products.priceM2Title': 'Wszystkie ceny w tej tabeli są za metr kwadratowy (m²)',
    'admin.products.akpLev': 'CZS Sprz.',
    'admin.products.akpLevTooltip': 'Cena zakupu sprzedawcy — co sprzedawca płaci Filipowi (bez VAT)',
    'admin.products.akpLevHint': 'Cena zakupu sprzedawcy — podstawa rabatu w zakładce Rabaty',
    'admin.products.akpDiscount': 'Rabat Sprz.→Klient',
    'admin.products.akpDiscountTooltip': 'Rabat, który sprzedawca przekazuje klientowi końcowemu (widoczny online)',
    'admin.products.netAkp': 'Netto klient bez VAT',
    'admin.products.netAkpTooltip': 'Cena netto klienta końcowego bez VAT — co klient płaci na stronie przed VAT',
    'admin.products.purchaseTooltip': 'Netto CZS — co jest płacone dostawcy (kliknij ✎ aby wprowadzić CZS Dost. + rabat)',
    'admin.products.margeTooltip': 'Marża na cenie sprzedaży — (sprzedaż − zakup) / sprzedaż × 100',
    'admin.products.exclVatTooltip': 'Cena sprzedaży bez VAT — co klient płaci przed VAT',
    'admin.products.dealConsistent': 'Formuła sprzedaży poprawna — kliknij Zapisz aby potwierdzić',
    'admin.products.dealInconsistent': 'Netto różni się od CZS Sprz × (1 − rabat). Wprowadź ponownie.',
    'admin.products.akpBlockTitle': '📦 Cena sprzedaży / m²',

    // PRODUCT DISCOUNT MODAL (PL)
    'admin.products.discountFor':         'Rabat dla',
    'admin.products.discountHelp':        'Skonfiguruj tymczasową promocję lub akcję likwidacyjną dla tego produktu. Widoczne na karcie produktu na stronie.',
    'admin.products.discountShow':        'Pokaż standardowy rabat',
    'admin.products.discountShowHelp':    'Pokazuje czerwoną pigułkę rabatu na karcie produktu. Np. "Wczesna rezerwacja" lub "Promo".',
    'admin.products.discountPct':         'Rabat %',
    'admin.products.discountMax':         'maks. 50%',
    'admin.products.discountMax50':       'Rabat może wynosić maksymalnie 50%.',
    'admin.products.discountLabel':       'Etykieta',
    'admin.products.discountLabelPh':     'np. Promo, Wczesna rezerwacja',
    'admin.products.discountSave':        'Zapisz',
    'admin.products.discountDateInvalid': 'Data zakończenia musi być późniejsza niż data rozpoczęcia.',
    'admin.products.liquidationToggle':   'Likwidacja / koniec sezonu',
    'admin.products.liquidationHelp':     'Tymczasowa bursztynowa pigułka z datą początkową i końcową. Wygrywa ze standardowym rabatem w czasie okna.',
    'admin.products.liquidationPct':      'Likwidacja %',
    'admin.products.liquidationLabel':    'Etykieta',
    'admin.products.liquidationLabelPh':  'np. Likwidacja, Koniec sezonu',
    'admin.products.liquidationStart':    'Data rozpoczęcia',
    'admin.products.liquidationEnd':      'Data zakończenia',
    'admin.products.preset1month':        'Następny miesiąc',
    'admin.products.preset1week':         'Następny tydzień',
    'admin.products.preview':             'Podgląd',
    'admin.products.catalogPrice':        'Cena katalogowa',
    'admin.products.afterDiscount':       'Po rabacie',
    'admin.products.akpBlockHint': '(opcjonalne — można też później przez tabelę Produkty)',
    'admin.products.purchaseSubtitle': 'CZS {akp} · −{disc}%',
    'admin.products.bestsellersOn': 'Kliknij, aby wyłączyć filtr bestsellerów',
    'admin.products.bestsellersOff': 'Pokaż tylko bestsellery',
    'admin.products.resetFilters': 'Resetuj filtry · pokaż wszystkie produkty',
    'admin.products.resetFiltersFull': 'Resetuj filtry · pokaż wszystkie {count} produktów',
    'admin.products.resetFiltersTitle': 'Wyłącz wszystkie filtry, aby zobaczyć wszystkie produkty',
    'admin.products.emptyFilterTitle': 'Brak widocznych produktów — filtr aktywny',
    'admin.products.emptyBoth': 'Filtr bestsellerów + dostawca {supplier} ukrywają wszystkie {count} produktów.',
    'admin.products.emptyBestsellers': 'Filtr bestsellerów aktywny, ale żaden produkt nie jest oznaczony.',
    'admin.products.emptySupplier': 'Dostawca {supplier} nie ma produktów na tej liście.',

    // Admin suppliers
    'admin.suppliers.title': 'Dostawcy',
    'admin.suppliers.add': 'Dodaj dostawcę',
    'admin.suppliers.new': 'Nowy',
    'admin.suppliers.editSupplier': 'Edytuj dostawcę',
    'admin.suppliers.newSupplier': 'Nowy dostawca',
    'admin.suppliers.company': 'Firma',
    'admin.suppliers.name': 'Nazwa',
    'admin.suppliers.nameShort': 'Nazwa (krótka)',
    'admin.suppliers.contactPerson': 'Osoba kontaktowa',
    'admin.suppliers.email': 'E-mail',
    'admin.suppliers.phone': 'Telefon',
    'admin.suppliers.city': 'Miasto',
    'admin.suppliers.address': 'Adres',
    'admin.suppliers.postalCode': 'Kod pocztowy',
    'admin.suppliers.btw': 'NIP',
    'admin.suppliers.products': 'Produkty',
    'admin.suppliers.actions': 'Akcje',
    'admin.suppliers.edit': 'Edytuj',
    'admin.suppliers.delete': 'Usuń',
    'admin.suppliers.confirmDelete': 'Na pewno usunąć tego dostawcę?',
    'admin.suppliers.nameRequired': 'Nazwa jest wymagana',
    'admin.suppliers.noSuppliers': 'Brak dostawców',
    'admin.suppliers.website': 'Strona',
    'admin.suppliers.prod': 'prod.',

    // Admin resellers
    'admin.resellers.title': 'Sprzedawcy',
    'admin.resellers.add': 'Dodaj sprzedawcę',
    'admin.resellers.new': 'Nowy sprzedawca',
    'admin.resellers.edit': 'Edytuj sprzedawcę',
    'admin.resellers.companyName': 'Nazwa firmy',
    'admin.resellers.slug': 'URL slug',
    'admin.resellers.contactPerson': 'Osoba kontaktowa',
    'admin.resellers.loginEmail': 'E-mail logowania',
    'admin.resellers.btwNumber': 'NIP',
    'admin.resellers.discount': 'Rabat',
    'admin.resellers.contactEmail': 'E-mail kontaktowy',
    'admin.resellers.contactPhone': 'Telefon kontaktowy',
    'admin.resellers.address': 'Adres',
    'admin.resellers.postalCode': 'Kod pocztowy',
    'admin.resellers.city': 'Miasto',
    'admin.resellers.servicePostcodes': 'Obszar działania (kody pocztowe oddzielone przecinkami)',
    'admin.resellers.postcodesHint': 'Np. 2000,2018,2020 — klienci końcowi z tymi kodami zostaną automatycznie przypisani.',
    'admin.resellers.logoUrl': 'URL logo',
    'admin.resellers.logoHint': 'Ścieżka do pliku logo (np. /reseller-logos/nazwa.svg)',
    'admin.resellers.website': 'Strona',
    'admin.resellers.websiteHint': 'Publiczna strona tego sprzedawcy (opcjonalne)',
    'admin.resellers.contactsTitle': 'Dodatkowe osoby kontaktowe',
    'admin.resellers.contactsHint': 'Inne osoby tej samej firmy sprzedawcy',
    'admin.resellers.contactsEmpty': 'Brak dodatkowych kontaktów',
    'admin.resellers.contactAdd': 'Dodaj',
    'admin.resellers.installerTitle': 'Instalatorzy tego sprzedawcy',
    'admin.resellers.installersHint': 'Firmy, które sprzedawca może polecić do instalacji',
    'admin.resellers.installersEmpty': 'Brak instalatorów',
    'admin.resellers.installerAdd': 'Dodaj',
    'admin.resellers.installerNotes': 'Notatki (specjalność, region, godziny kontaktu...)',
    'admin.resellers.contactRole': 'Rola (np. Sprzedaż, Logistyka)',
    'admin.resellers.agreementsTitle': 'Umowy i warunki z tym sprzedawcą',
    'admin.resellers.agreementsHint': 'Wolny tekst — warunki dostawy, terminy płatności, ekskluzywność, kontakt, protokół klienta końcowego itp. Tylko Ty i współwłaściciel widzą to; sprzedawca nie.',
    'admin.resellers.agreementsPlaceholder': 'Demo dystrybutor dla...',
    'admin.resellers.confirmDeactivate': 'Czy na pewno dezaktywować tego sprzedawcę?',
    'admin.resellers.empty': 'Brak sprzedawców',
    'admin.resellers.company': 'Firma',
    'admin.resellers.city': 'Miasto',
    'admin.resellers.discount2': 'Rabat',
    'admin.resellers.postcodes': 'Obszar',
    'admin.resellers.customers': 'Klienci',
    'admin.resellers.quotes': 'Oferty',
    'admin.resellers.status': 'Status',
    'admin.resellers.active': 'Aktywny',
    'admin.resellers.inactive': 'Nieaktywny',

    // PDF labels
    'pdf.quote': 'OFERTA',
    'pdf.ref': 'Nr',
    'pdf.date': 'Data',
    'pdf.validUntil': 'Ważne do',
    'pdf.client': 'Klient',
    'pdf.status': 'Status',
    'pdf.product': 'PRODUKT',
    'pdf.priceOverview': 'Podsumowanie cen',
    'pdf.unitPriceM2': 'CENY JEDNOSTKOWE ZA M²',
    'pdf.totalsForQuote': 'Suma dla tej oferty',
    'pdf.description': 'Opis',
    'pdf.amount': 'Kwota',
    'pdf.subtotalExcl': 'Suma częściowa bez VAT',
    'pdf.vat21': 'VAT 21%',
    'pdf.totalIncl': 'Razem z VAT',
    'pdf.totalPrice': 'Cena całkowita',
    'pdf.vatIncluded': 'VAT w cenie',
    'pdf.conditions': 'Warunki',
    'pdf.cond1': 'Oferta ważna 30 dni',
    'pdf.cond2': 'Płatność w 30 dni od faktury',
    'pdf.cond3': 'Dostawa w 2-3 tygodnie po potwierdzeniu',
    'pdf.cond4': 'Ceny mogą się zmienić w przypadku wahań kursów walut',
    'pdf.installerNote': 'Płatność u sprzedawcy. Het Gevelsteenbedrijf jako pośrednik logistyczny.',
    'pdf.poweredBy': 'WSPIERANE PRZEZ HET GEVELSTEENBEDRIJF',
    'pdf.configuration': 'Konfiguracja',
    'pdf.requestDetails': 'Szczegóły zapytania',
    'pdf.installAddressLabel': 'Adres montażu / Instalacja',
    'pdf.installAddressNote': 'Dostawa i montaż odbywają się pod tym adresem — nie pod adresem do faktury.',
    'pdf.print': 'Zapisz jako PDF',
    'pdf.downloadHint': 'Kliknij "Zapisz jako PDF" lub naciśnij Ctrl/Cmd+P',
    // PDF — Dostawa & montaż + status montażysty (v4)
    'pdf.deliveryHeading': 'Dostawa & montaż',
    'pdf.deliveryNote1': 'Adres odbioru, dostawy lub montażu zostanie potwierdzony po akceptacji oferty.',
    'pdf.deliveryNote2': 'Przy montażu własnym dostarczamy tylko materiał; przy montażu przez naszą sieć przydzielony zostanie monter.',
    'pdf.installerSelfShort': 'Klient montuje sam',
    'pdf.installerWantedShort': 'Monter przez naszą sieć',
    // PDF — Etykiety konfiguracji i podziału cen (v4)
    'pdf.cfg.product': 'Produkt',
    'pdf.cfg.articleNr': 'Numer artykułu',
    'pdf.cfg.colorTexture': 'Kolor / Tekstura',
    'pdf.cfg.dimensions': 'Wymiary',
    'pdf.cfg.area': 'Powierzchnia',
    'pdf.cfg.jointWidth': 'Szerokość fugi',
    'pdf.cfg.jointColor': 'Kolor fugi',
    'pdf.cfg.cuttingLoss': 'Strata cięcia',
    'pdf.cfg.stripCount': 'Liczba pasków',
    'pdf.cfg.cornerPieces': 'Narożniki',
    'pdf.cfg.glue': 'Klej',
    'pdf.cfg.grout': 'Fuga',
    'pdf.cfg.bricks': 'Cegły',
    'pdf.cfg.cornerStones': 'Cegły narożne',
    'pdf.unit.bags': 'worki',
    'pdf.unit.pieces': 'szt',

    // STATUS
    'admin.status.draft': 'wersja robocza',
    'admin.status.sent': 'wysłana',
    'admin.status.accepted': 'zaakceptowana',
    'admin.status.rejected': 'odrzucona',
    'admin.status.expired': 'wygasła',

    // COOKIE BANNER
    'cookie.title': 'Szanujemy Twoją prywatność',
    'cookie.description': 'Używamy plików cookie, aby ulepszyć Twoje doświadczenie. Pliki cookie niezbędne są wymagane do działania strony. Pliki cookie analityczne i marketingowe są umieszczane tylko za Twoją zgodą, zgodnie z RODO.',
    'cookie.acceptAll': 'Zaakceptuj wszystkie',
    'cookie.essentialOnly': 'Tylko niezbędne',
    'cookie.showDetails': 'Więcej szczegółów',
    'cookie.hideDetails': 'Ukryj szczegóły',
    'cookie.essential.title': 'Pliki niezbędne',
    'cookie.essential.desc': 'Wymagane do działania strony: język, wybór cookie, sesja logowania.',
    'cookie.analytics.title': 'Pliki analityczne',
    'cookie.analytics.desc': 'Pomagają nam zrozumieć, jak odwiedzający korzystają ze strony. Dane zanonimizowane.',
    'cookie.marketing.title': 'Pliki marketingowe',
    'cookie.marketing.desc': 'Używane do wyświetlania reklam dopasowanych do Twoich zainteresowań.',
    'cookie.legalNote': 'Więcej informacji znajdziesz w naszym',
    'cookie.policyLink': 'polityce cookie',

    // PROFILE / common
    'admin.profile': 'Mój profil',
    'admin.profile.edit': 'Edytuj',
    'admin.profile.save': 'Zapisz',
    'admin.profile.saving': 'Zapisywanie...',
    'admin.profile.cancel': 'Anuluj',
    'admin.profile.success': 'Profil pomyślnie zaktualizowany',
    'admin.profile.name': 'Imię i nazwisko',
    'admin.profile.email': 'E-mail',
    'admin.profile.phone': 'Telefon',
    'admin.profile.company': 'Nazwa firmy',
    'admin.profile.companyShort': 'Firma',
    'admin.profile.btw': 'NIP',
    'admin.profile.btwShort': 'NIP',
    'admin.profile.address': 'Adres',
    'admin.profile.city': 'Miasto',
    'admin.profile.postalCode': 'Kod pocztowy',

    // PRICING / Kortingen Resellers tab (v3.2.61)
    'admin.pricing.title': 'Zarządzanie cenami',
    'admin.pricing.desc': 'Cena brutto = cena dostawcy. Marża % = Twoja marża (domyślnie 25%). Cena sprzedaży = brutto × (1 + marża/100).',
    'admin.pricing.noProducts': 'Brak produktów.',
    'admin.pricing.grossPrice': 'Cena brutto',
    'admin.pricing.grossPriceSupplier': 'Cena brutto (dostawca)',
    'admin.pricing.markup': 'Marża',
    'admin.pricing.markupPct': 'Marża %',
    'admin.pricing.sellPrice': 'Cena sprzedaży',
    'admin.pricing.fixedPrice': 'Cena stała',
    'admin.pricing.product': 'Produkt',
    'admin.pricing.category': 'Kategoria',
    'admin.pricing.actions': 'Akcje',
    'admin.pricing.resellerSection': 'Umowy cenowe per sprzedawca',
    'admin.pricing.resellerSectionDesc': 'Ustaw ogólny rabat dla wszystkich produktów + opcjonalne nadpisania na produkt. Stosowane po stronie serwera dla każdej oferty od/do tego sprzedawcy.',
    'admin.pricing.selectReseller': 'Wybierz sprzedawcę',
    'admin.pricing.defaultDiscount': 'Rabat standardowy',
    'admin.pricing.lastSaved': 'Ostatnia aktualizacja',
    'admin.pricing.saved': '✓ Umowy zapisane',
    'admin.pricing.saving': 'Zapisywanie…',
    'admin.pricing.saveAgreement': 'Zapisz umowy',
    'admin.pricing.overridesTitle': 'Nadpisania na produkt (opcjonalne)',
    'admin.pricing.overridesDesc': 'Wypełnij tylko gdy umowa różni się od rabatu standardowego. Cena stała ma pierwszeństwo przed rabatem %.',
    'admin.pricing.catalog': 'Katalog',
    'admin.pricing.overrideDiscount': 'Inny %',
    'admin.pricing.overridePrice': 'Cena stała/m²',
    'admin.pricing.effective': 'Efektywna',
    'admin.pricing.saveChanges': 'Zapisz zmiany',
    'admin.pricing.saveBarDesc': 'rabat standardowy, rabaty na produkt i status pauzy dla',
    'admin.pricing.saveAllAbove': 'Zapisuje wszystkie zmiany powyżej',
    'admin.pricing.savingDots': 'Zapisywanie…',
    'admin.pricing.saveFor': 'Zapisz dla',
    'admin.pricing.thisReseller': 'tego sprzedawcy',
    'admin.pricing.tableDesc': 'Jedna tabela ze wszystkim co potrzebne: cena brutto dostawcy, Twoja marża i cena sprzedaży z Zarządzania cenami, plus rabat per sprzedawca (i opcjonalna pauza, jeśli nie chcesz oferować produktu przez tego sprzedawcę).',
    'admin.pricing.colProduct': 'Produkt',
    'admin.pricing.colInkoop': 'Zakup',
    'admin.pricing.colInkoopTitle': 'Cena zakupu od dostawcy',
    'admin.pricing.colMarge': 'Marża',
    'admin.pricing.colMargeTitle': 'Marża = (sprzedaż − zakup) / sprzedaż × 100.',
    'admin.pricing.colSell': 'Sprzedaż',
    'admin.pricing.colSellTitle': 'Cena sprzedaży (bez VAT). Edytowana w zakładce Produkty · Ceny.',
    'admin.pricing.colStandard': 'Standard %',
    'admin.pricing.colStandardTitle': 'Rabat standardowy tego sprzedawcy (taki sam dla wszystkich produktów)',
    'admin.pricing.colDiscount': 'Rabat na produkt %',
    'admin.pricing.colDiscountTitle': 'Rabat specyficzny dla tego produktu — nadpisuje rabat standardowy',
    'admin.pricing.colEffective': 'Efektywna',
    'admin.pricing.colStatus': 'Status',
    'admin.pricing.statusActive': 'Aktywny',
    'admin.pricing.statusPause': 'Pauza',
    'admin.pricing.pausedItalic': 'wstrzymane',
    'admin.pricing.pauseClick': 'Kliknij, aby wstrzymać ten produkt dla tego sprzedawcy',
    'admin.pricing.pauseUnpause': 'Kliknij, aby ponownie udostępnić',

    // CALCULATOR / catalog (reseller side)
    'reseller.profile.title': 'Mój profil',
    'reseller.profile.companyName': 'Nazwa firmy',
    'reseller.profile.btwNumber': 'NIP',
    'reseller.profile.discount': 'Rabat',
    'reseller.profile.postcodes': 'Obszar',
    'reseller.profile.contactEmail': 'E-mail kontaktowy',
    'reseller.profile.contactPhone': 'Telefon kontaktowy',
    'reseller.profile.address': 'Adres',
    'reseller.profile.city': 'Miasto',
    'reseller.profile.postalCode': 'Kod pocztowy',
    'reseller.profile.ownerControlled': 'Dane zarządzane przez właściciela',
    'reseller.profile.ownerControlledNote': 'Te pola może edytować tylko właściciel (Filip).',
    'reseller.catalog.title': 'Katalog',
    'reseller.catalog.desc': 'Twoje ceny zakupu (z rabatem). Sprzedawcy końcowi widzą cenę katalogową.',
    'reseller.catalog.empty': 'Brak produktów w katalogu',
    'reseller.catalog.product': 'Produkt',
    'reseller.catalog.color': 'Kolor',
    'reseller.catalog.category': 'Kategoria',
    'reseller.catalog.catalogPrice': 'Cena katalog.',
    'reseller.catalog.yourPrice': 'Twoja cena',
    'reseller.catalog.discount': 'Rabat',
    'reseller.catalog.remnant': 'Resztka',
    'reseller.customers.title': 'Klienci',
    'reseller.customers.empty': 'Brak klientów',
    'reseller.customers.name': 'Nazwa',
    'reseller.customers.email': 'E-mail',
    'reseller.customers.type': 'Typ',
    'reseller.customers.city': 'Miasto',
    'reseller.customers.quotes': 'Oferty',
    'reseller.customers.since': 'Od',
    'reseller.quotes.title': 'Oferty',
    'reseller.quotes.empty': 'Brak ofert',
    'reseller.quotes.create': 'Nowa oferta',
    'reseller.quotes.createTitle': 'Nowa oferta',
    'reseller.quotes.fName': 'Nazwa klienta',
    'reseller.quotes.fEmail': 'E-mail klienta',
    'reseller.quotes.fPhone': 'Telefon',
    'reseller.quotes.fPostal': 'Kod pocztowy',
    'reseller.quotes.fIsB2B': 'Klient B2B (z NIP)',
    'reseller.quotes.fNotes': 'Notatki',
    'reseller.quotes.items': 'Pozycje',
    'reseller.quotes.addItem': 'Dodaj pozycję',
    'reseller.quotes.selectProduct': 'Wybierz produkt...',

    // ════════════════════════════════════════════════════════════════════════════
    // v3.2.104 — Volledige PL vertalingen voor Custom / Architecten /
    // Restpartijen / Contact pagina's. Eerder enkel NL fallback waardoor de
    // hele publieke site er voor PL-gebruikers Nederlands uitzag.
    // ════════════════════════════════════════════════════════════════════════════

    // ARCHITECTS / CUSTOM
    'arch.professionals':              'Profesjonaliści',
    'arch.title':                      'Architekci i specyfikatorzy',
    'arch.desc':                       'Pobierz karty techniczne, deklaracje właściwości i pliki BIM. Cała dokumentacja potrzebna do specyfikacji.',
    'arch.generalDocs':                'Dokumentacja ogólna',
    'arch.perProduct':                 'Dokumentacja per produkt',
    'arch.techSpecs':                  'Specyfikacja techniczna',
    'arch.doc1':                       'Karta techniczna — pełna kolekcja',
    'arch.doc2':                       'Deklaracja właściwości (DoP) — oznakowanie CE',
    'arch.doc3':                       'Obiekty BIM — zgodne z Revit',
    'arch.doc4':                       'Instrukcja montażu',
    'arch.doc5':                       'Raport odporności ogniowej — klasa A1',
    'arch.doc6':                       'Test mrozoodporności — klasa F2',
    'arch.techFiche':                  'Karta techniczna',
    'arch.dop':                        'Deklaracja właściwości',
    'arch.bim':                        'Obiekt BIM',
    'arch.spec.dimensions':            'Wymiary',
    'arch.spec.performance':           'Właściwości',
    'arch.spec.installation':          'Montaż',
    'arch.spec.warranty':              'Gwarancja',
    'arch.spec.length':                'Długość',
    'arch.spec.height':                'Wysokość',
    'arch.spec.thickness':             'Grubość',
    'arch.spec.weight':                'Waga',
    'arch.spec.fireClass':             'Klasa ogniowa',
    'arch.spec.fireValue':             'A1 (niepalna)',
    'arch.spec.frost':                 'Mrozoodporność',
    'arch.spec.frostValue':            'Klasa F2',
    'arch.spec.water':                 'Nasiąkliwość',
    'arch.spec.waterValue':            '< 6%',
    'arch.spec.ce':                    'Oznakowanie CE',
    'arch.spec.ceValue':               'EN 14411',
    'arch.spec.glue':                  'Klej',
    'arch.spec.glueValue':             '5 kg/m² (elastyczny klej do płytek)',
    'arch.spec.grout':                 'Fuga',
    'arch.spec.groutValue':            '1,5 kg/m² (przy 10 mm fudze)',
    'arch.spec.loss':                  'Straty cięcia',
    'arch.spec.lossValue':             '10% standardowo',
    'arch.spec.dryTime':               'Czas schnięcia',
    'arch.spec.dryValue':              '24–48 godz.',
    'arch.spec.productWarranty':       'Gwarancja produktu',
    'arch.spec.productWarrantyVal':    '10 lat',
    'arch.spec.colorFast':             'Trwałość koloru',
    'arch.spec.colorFastVal':          'Odporność UV',
    'arch.spec.lifespan':              'Żywotność',
    'arch.spec.lifespanVal':           '> 50 lat',
    'arch.spec.maintenance':           'Konserwacja',
    'arch.spec.maintenanceVal':        'Bezobsługowy',
    'arch.helpTitle':                  'Potrzebujesz pomocy ze specyfikacją?',
    'arch.helpDesc':                   'Nasz zespół techniczny chętnie pomoże w doborze właściwej specyfikacji do Twojego projektu.',
    'arch.contactUs':                  'Skontaktuj się',

    // CUSTOM (Pierres sur mesure)
    'custom.exclusive':                'Ekskluzywne',
    'custom.title':                    'Płytki klinkierowe na zamówienie',
    'custom.desc':                     'Spraw, by Twoja elewacja była wyjątkowa dzięki płytkom klinkierowym wykonanym na zamówienie. Wybierz własny kolor, fakturę i wykończenie — produkowane przez naszego polskiego producenta.',
    'custom.color.title':              'Kolor na zamówienie',
    'custom.color.desc':               'Wybierz z nieograniczonej palety kolorów. Dopasujemy każdy kolor RAL lub istniejący kamień.',
    'custom.texture.title':            'Faktura według życzenia',
    'custom.texture.desc':             'Od ultragładkiej po grubą szorstką, ręcznie formowaną czy industrialną — każda faktura jest możliwa.',
    'custom.size.title':               'Niestandardowe wymiary',
    'custom.size.desc':                'Inne formaty, cieńsze lub grubsze płytki — wszystko podlega rozmowie dla Twojego projektu.',
    'custom.howTitle':                 'Jak to działa?',
    'custom.step1.title':              'Brief',
    'custom.step1.desc':               'Podziel się swoimi wymaganiami: kolor, faktura, format i ilość.',
    'custom.step2.title':              'Próbka',
    'custom.step2.desc':               'Wyprodukujemy próbkę na zamówienie do Twojej akceptacji.',
    'custom.step3.title':              'Produkcja',
    'custom.step3.desc':               'Po akceptacji rozpoczynamy produkcję. Termin realizacji: 6–8 tygodni.',
    'custom.step4.title':              'Dostawa',
    'custom.step4.desc':               'Twoje płytki na zamówienie zostaną dostarczone przez lokalnego dystrybutora.',
    'custom.interestTitle':            'Zainteresowany płytkami na zamówienie?',
    'custom.interestDesc':             'Skontaktuj się, aby porozmawiać niezobowiązująco o możliwościach. Minimalna ilość zamówienia: 100 m².',
    'custom.contact':                  'Skontaktuj się',

    // RESTPARTIJEN (Końcówki serii)
    'restpartijen.tag':                'Końcówki serii · Oferta tymczasowa',
    'restpartijen.title':              'Końcówki serii — zniżki na ostatnie zapasy',
    'restpartijen.desc':               'Prawdziwe premium płytki klinkierowe, poprzednie kolekcje lub nadwyżki naszych producentów. Ograniczone ilości, atrakcyjne ceny — do wyczerpania zapasów.',
    'restpartijen.loading':            'Ładowanie końcówek serii…',
    'restpartijen.empty':              'Obecnie brak dostępnych końcówek serii',
    'restpartijen.emptyDesc':          'Nasz stan magazynowy rotuje regularnie. Wróć później lub przejrzyj pełny katalog.',
    'restpartijen.backToCatalog':      'Powrót do katalogu',
    'restpartijen.available':          'Dostępne',
    'restpartijen.interested':         'Zainteresowany końcówką serii?',
    'restpartijen.interestedDesc':     'Ilości są ograniczone. Zadzwoń lub napisz, aby zarezerwować lub obejrzeć — kto pierwszy, ten lepszy.',
    'restpartijen.requestQuote':       'Oferta na te płytki',
    'restpartijen.hero.badge':         'Do wyczerpania · Ograniczony zapas',
    'restpartijen.hero.title1':        'Premium płytki klinkierowe,',
    'restpartijen.hero.title2':        'do 50% taniej',
    'restpartijen.hero.subtitle':      'Końcówki serii, zakończone kolekcje i nadwyżki naszych włoskich, polskich i hiszpańskich producentów. Ta sama premium jakość — ułamek ceny. Kto pierwszy, ten lepszy.',
    'restpartijen.hero.maxDiscount':   'Maks. zniżka',
    'restpartijen.hero.available':     'Dostępne',
    'restpartijen.hero.lots':          'Partie',
    'restpartijen.benefit1.title':     'Do 50% zniżki',
    'restpartijen.benefit1.desc':      'Premium cegła za ułamek ceny katalogowej. Im niższy stan, tym lepsza cena.',
    'restpartijen.benefit2.title':     'Ta sama premium jakość',
    'restpartijen.benefit2.desc':      'Mrozoodporna (klasa F2), trwała kolorystycznie, żywotność 50+ lat. Bez wad fabrycznych ani drugiego gatunku.',
    'restpartijen.benefit3.title':     'Szybka dostawa',
    'restpartijen.benefit3.desc':      'Końcówki serii są już w naszym magazynie w Schilde — dostawa w 5 dni roboczych na terenie całej Belgii.',
    'restpartijen.benefit4.title':     'Unikalne dla Twojego projektu',
    'restpartijen.benefit4.desc':      'Poprzednie kolekcje, które nie są już produkowane — ekskluzywne dla Twojej elewacji, nikt inny tego już nie ma.',
    'restpartijen.list.title':         'Dostępne końcówki serii',
    'restpartijen.list.subtitle':      'Przejrzyj nasze aktualne oferty. Zapas jest ograniczony — zarezerwowane m² znikają od razu z tej listy.',
    'restpartijen.list.opOp':          'Do wyczerpania',
    'restpartijen.list.endOfStock':    'Koniec stocku',
    'restpartijen.list.savings':       'Oszczędzasz',
    'restpartijen.list.left':          'dostępne',
    'restpartijen.list.reserve':       'Zarezerwuj / zapytaj',
    'restpartijen.why.title':          'Dlaczego kupować końcówki serii?',
    'restpartijen.why.1':              'Identyczna jakość jak nasz regularny katalog — bez drugiego gatunku, pęknięć ani błędów kolorystycznych.',
    'restpartijen.why.2':              'Do 50% taniej niż ten sam kamień w cenie katalogowej.',
    'restpartijen.why.3':              'Dostępne od ręki — bez 4–6 tygodni oczekiwania na produkcję fabryczną.',
    'restpartijen.why.4':              'Ekskluzywne — poprzednie kolekcje niedostępne już nigdzie indziej.',
    'restpartijen.why.5':              'Pełna gwarancja jak na nowe produkty — 10-letnia gwarancja materiałowa.',
    'restpartijen.note.title':         'Ważne informacje',
    'restpartijen.note.heading':       'Do wyczerpania — bez możliwości dozamówienia',
    'restpartijen.note.body':          'Końcówki serii to dokładnie to: ostatni zapas. Dozamówienie pasującego materiału na rozszerzenie projektu zwykle nie jest już możliwe. Wskazówka: zamawiaj zawsze 5–10% więcej na straty cięcia i ewentualne późniejsze naprawy.',
    'restpartijen.recentSold.title':   'Niedawno sprzedane',
    'restpartijen.recentSold.desc':    'Co przegapiłeś — te końcówki serii zostały w międzyczasie wyprzedane. Zapisz się do newslettera, aby pierwszy dowiedzieć się o nowych ofertach.',
    'restpartijen.recentSold.sticker': 'Sprzedane',

    // CONTACT
    'contact.tag':                     'Skontaktuj się z nami',
    'contact.title':                   'Skontaktuj się',
    'contact.subtitle':                'Pytania o projekt, zamówienie lub współpracę? Wyślij wiadomość lub zadzwoń bezpośrednio. Zwykle odpowiadamy w ciągu 24 godzin.',
    'contact.formTitle':               'Wyślij wiadomość',
    'contact.formDesc':                'Pola z * są obowiązkowe. Najlepiej podaj też kod pocztowy, abyśmy mogli od razu połączyć Cię z naszym lokalnym dystrybutorem.',
    'contact.fName':                   'Twoje imię',
    'contact.fEmail':                  'E-mail',
    'contact.fPhone':                  'Telefon',
    'contact.fPostal':                 'Kod pocztowy',
    'contact.fPostalHint':             'Pomaga nam połączyć Cię z naszym lokalnym dystrybutorem w Twoim regionie.',
    'contact.fSubject':                'Temat',
    'contact.fMessage':                'Twoja wiadomość',
    'contact.consent':                 'Zgadzam się, aby moje dane zostały wykorzystane do odpowiedzi na moje pytanie, zgodnie z polityką prywatności. Nie będą udostępniane stronom trzecim poza naszymi instalatorami w moim regionie.',
    'contact.send':                    'Wyślij wiadomość',
    'contact.sending':                 'Wysyłanie…',
    'contact.successTitle':            'Wiadomość wysłana',
    'contact.successDesc':             'Dziękujemy — skontaktujemy się z Tobą najszybciej, jak to możliwe.',
    'contact.errorTitle':              'Wysyłanie nie powiodło się',
    'contact.errorDesc':               'Coś poszło nie tak. Spróbuj ponownie lub zadzwoń bezpośrednio.',
    'contact.responseTimeTitle':       'Czas odpowiedzi',
    'contact.responseTimeDesc':        'Wiadomości są odpowiadane w ciągu 24 godzin roboczych. W pilnych projektach najlepiej zadzwoń bezpośrednio.',
    'contact.emailRequired':           'Adres e-mail jest wymagany.',
    'contact.emailFormat':             'Podaj prawidłowy adres e-mail (np. imie@przyklad.pl).',
    'contact.emailDomain':             'Ten adres wygląda na testowy lub jednorazowy. Podaj prawdziwy adres e-mail, abyśmy mogli się z Tobą skontaktować.',
  },
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    const saved = localStorage.getItem('gvs-lang') as Lang;
    return saved && (saved === 'nl' || saved === 'fr' || saved === 'en' || saved === 'pl') ? saved : 'nl';
  });

  const handleSetLang = (newLang: Lang) => {
    setLang(newLang);
    localStorage.setItem('gvs-lang', newLang);
  };

  const t = (key: string): string => {
    return translations[lang][key] || translations['nl'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang: handleSetLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}
